/**
 * Product setup — chunked (Upload → Categorize → Costs → Review). Each chunk is a
 * conversation on TOP with the live sheet BELOW.
 *
 * Categorize: the assistant does a first pass (Product → Variant → SKU), then ASKS
 * the judgment calls — keep scents as variants or split into products? these are
 * bundles? these are set aside? — instead of guessing. You answer with a tap or
 * free text; the sheet updates live; drag is a manual override. Real mode persists
 * via /catalog; sandbox stays client-side.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { invalidateCatalogQueries, relockFinancialQueries } from "../lib/query-invalidation.js";
import { productSetupPath } from "../lib/onboarding-step-access.js";
import { inferDirectSkuCommand } from "../lib/catalog-direct-command.js";
import { newlyUnclassifiedSellingSkuIds } from "../lib/catalog-suggestion-needed.js";
import { bundleReferenceIndex, skuNeedsProductSetup } from "../lib/catalog-cost-bearing.js";
import { untypedRegularProductIds } from "../lib/catalog-product-types.js";
import {
  isOnboardingConversationConflict,
  onboardingPromptGuard,
  onboardingRequestWasSaved,
  retryOnboardingImportOnce,
  withSavedCatalogInterviewProgress,
  type CatalogInterviewQueryData,
} from "../lib/onboarding-chat-reliability.js";

/* ----------------------------- types ----------------------------- */
type GroupKind = "product" | "bundle" | "bonus" | "misc" | "wholesale";
interface RawClassification { productGroupId: string | null; variantLabel: string | null; manualPrice: number | null; packSize: number | null; boxesPerSale: number | null; offerLabel: string | null; status: "needs_review" | "confirmed" | "excluded" | "retired"; inferenceNote?: string | null; }
interface RawSku { skuId: string; skuCode: string; productName: string; variantName: string; lastPrice: number | null; units: number; revenue: number; orders: number; components?: SkuComponent[]; classification: RawClassification | null; }
interface RawGroup { id: string; name: string; active: boolean; unit: string; kind?: GroupKind; type?: ProductType | null; }
interface ClassificationView { settings: { unitLabel: string; includeWholesale?: boolean } | null; groups: RawGroup[]; skus: RawSku[]; }

interface SkuComponent { id: string; componentSkuId: string | null; componentProductGroupId: string | null; label: string; quantity: number; }
interface SheetSku { skuId: string; label: string; skuCode: string; orderPrice: number | null; manualPrice: number | null; units: number; revenue: number; orders: number; packSize: number | null; boxesPerSale: number | null; offerLabel: string | null; variant: string | null; components: SkuComponent[]; needsReview: boolean; inferenceNote: string | null; removedFromGroupId: string | null; }
interface Product { id: string; name: string; unit: string; kind: GroupKind; type?: ProductType | null; skus: SheetSku[]; }
interface Model { products: Product[]; aside: SheetSku[]; removed: SheetSku[]; }
interface ProductImportHealth {
  status: "pending" | "ready" | "error";
  lastSyncedAt: string | null;
  errorMessage: string | null;
  skuCount: number;
  activeJobs: Array<{ id: string; status: string; progressCurrent: number | null; progressTotal: number | null }>;
}

// Mirrors the server's command parser (apps/api/.../command-llm.ts).
interface CatalogAction { op: "move" | "exclude" | "include" | "retire" | "restore" | "rename" | "merge" | "split" | "setKind" | "setVariant" | "setField"; selector?: { product?: string; variant?: string; contains?: string; codes?: string[] }; targetProduct?: string; toAside?: boolean; name?: string; kind?: GroupKind; variant?: string; intoProduct?: string; field?: "qty" | "unitsPerSale" | "price" | "all"; value?: number; onlyBlank?: boolean; }
interface ParsedCommand { intent: "answer" | "command" | "question" | "navigate"; reply: string; answer?: string; action?: CatalogAction; followUp?: string; navigate?: "skip" | "back" | "done"; }

type Stage = "upload" | "categorize" | "costs" | "review";
const ASIDE = "__aside__";

// Currency comes from the ACCOUNT (a CAD/EUR client must never see "$") —
// callers thread the fetched base currency; USD is only the pre-fetch fallback.
const money = (n: number | null, currency = "USD"): string => {
  if (n === null) return "—";
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n); }
  catch { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n); }
};
const whole = (n: number): string => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
const labelOf = (s: RawSku): string => (s.variantName && s.variantName.toLowerCase() !== "default" ? s.variantName : s.productName);
const labelForGroup = (s: RawSku, group: Product | undefined): string => (
  group?.kind === "bundle" && s.variantName && s.variantName.toLowerCase() !== "default"
    ? `${s.productName} — ${s.variantName}`
    : labelOf(s)
);
let SYNTH = 0;
const synthId = (): string => `local-${++SYNTH}`;

/* distinct non-empty variant labels within a product */
function variantsOf(p: Product): string[] {
  const set = new Set<string>();
  for (const s of p.skus) if (s.variant && s.variant.trim()) set.add(s.variant.trim());
  return [...set];
}

const SANDBOX_KEY = "ccd.onboarding.sandbox";
// Sandbox Categorize edits never persist to the server — this session cache is
// what lets a mid-walkthrough refresh keep the founder's organizing work.
const LEGACY_SANDBOX_MODEL_KEY = "ccd.onboarding.sandboxModel.v1";
const LEGACY_SANDBOX_CATEGORY_REVIEW_KEY = "ccd.onboarding.catReviewed.sandbox";
const LEGACY_SANDBOX_COST_REVIEW_KEY = "ccd.onboarding.reviewed.sandbox";
const sandboxModelKey = (accountId: string): string => `ccd.onboarding.sandboxModel.v2.${accountId}`;
const sandboxCategoryReviewKey = (accountId: string): string => `ccd.onboarding.catReviewed.sandbox.${accountId}`;
const sandboxCostReviewKey = (accountId: string): string => `ccd.onboarding.reviewed.sandbox.${accountId}`;

// Module-scoped (survives React remounts): last time the catalog/preview load
// ran, per account + mode. Circuit-breaker for the 2026-07-06 remount storm — a redirect/
// remount loop on a reset account re-fired this LOAD effect (incl. the LLM
// /catalog/suggest) dozens of times a second, blowing the per-account AI limit.
const catalogBootAt = new Map<string, number>();
// DURABLE latch (no expiry) for the ONE expensive LLM call — the AI grouping
// (/catalog/suggest) fires only when a real account has no product groups, and
// a reset account can stay group-less, so it would re-POST on EVERY remount
// forever. Fire it at most once per account + mode per session; the cheap classification
// GET still runs every mount (so normal navigation still refreshes the sheet).
const suggestedModes = new Set<string>();

export default function OnboardingChatPage(): React.ReactElement {
  const api = useApiClient();
  const [params, setParams] = useSearchParams();
  // STICKY sandbox: once the owner enters ?mode=sandbox, stay there across refresh
  // + navigation (the query param kept getting dropped, dumping him on the real
  // page). sessionStorage, not localStorage — sticky through refreshes in THIS
  // tab, but a later real onboarding run in a fresh tab is never silently forced
  // into sandbox. "Exit sandbox" (footer) clears it.
  const urlSandbox = params.get("mode") === "sandbox";
  const stored = typeof window !== "undefined" && window.sessionStorage.getItem(SANDBOX_KEY) === "1";
  const mode = urlSandbox || stored ? "sandbox" : "real";
  const real = mode === "real";
  useEffect(() => {
    if (urlSandbox) window.sessionStorage.setItem(SANDBOX_KEY, "1");
    else if (stored) { const next = new URLSearchParams(params); next.set("mode", "sandbox"); setParams(next, { replace: true }); }
  }, [urlSandbox, stored]);
  const toast = useToast();
  const queryClient = useQueryClient();

  // After a catalog edit that can clear a review flag, refetch the setup gate +
  // cost-readiness so the "N of 6" counter and the dashboard's red/amber box go
  // away LIVE (founder 2026-07-12: "as soon as they finish that it should go
  // away — no manual reload"). These keys are deliberately spared by
  // invalidateCatalogQueries (refetching them on a filter toggle blanks the
  // shell), so a real confirm/approve action invalidates them EXPLICITLY here.
  const refreshGate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
    void queryClient.invalidateQueries({ queryKey: ["cost-readiness"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard", "status"] });
  };
  const navigate = useNavigate();

  // UPDATE MODE vs FIRST-TIME (founder 2026-07-12: "delineate whether it's their
  // first time going through it or a product update"). An account that already
  // FINISHED setup (gate finalized) and comes back to cost a newly-added product
  // gets the SHORT scoped flow: the server scopes the costs conversation to just
  // that product (off the same gate signal), and the finish returns to the setup
  // hub — NOT the full Review → "Finish setup" journey a first-timer walks. The
  // frontend only needs `finalized` to route the finish; it shares the
  // ["onboarding","state"] cache with the hub, so this adds no extra request.
  const gateStateQ = useQuery({ queryKey: ["onboarding", "state"], queryFn: () => api.get<{ finalized: boolean }>("/onboarding/state") });
  const updateMode = Boolean(gateStateQ.data?.finalized);

  // Jump straight to a stage via ?stage=costs — a fast way to test Costs without
  // re-walking categorize each time (the grouping is already saved). setStage
  // writes the param back, so a refresh RESUMES the current step instead of
  // dumping the founder at Upload (founder 2026-07-01).
  const STAGES: Stage[] = ["upload", "categorize", "costs", "review"];
  const startStage: Stage = ((): Stage => { const s = params.get("stage"); return s && (STAGES as string[]).includes(s) ? (s as Stage) : "upload"; })();
  // ?review=1 — deep-link from the setup hub / dashboard box straight to the
  // Categorize REVIEW SHEET (the flagged SKU visible), not the top of the flow.
  // Consumed once at mount so a set-up account never replays Upload → interview.
  const startAtReview = params.get("review") === "1";
  // ?session=fresh — founder-requested test control: wipe the saved costs
  // conversation (and the sandbox categorize cache) and start clean. Consumed
  // ONCE via a ref: the param used to survive in the URL through the whole
  // Upload/Categorize walk (only the costs chat stripped it), so every effect
  // re-run re-wiped the tab cache and refetched the preview (QA round 8). Now
  // the intent is captured at mount and the URL is cleaned right away.
  const freshOnce = useRef(params.get("session") === "fresh");
  const freshRequested = freshOnce.current;
  const resumeAfterOverhead = params.get("overheadReviewed") === "1";
  const fromGuided = params.get("from") === "guided";
  const resetReview = params.get("resetReview") === "1";

  const [stage, rawSetStage] = useState<Stage>(startStage);
  const setStage = (s: Stage): void => {
    rawSetStage(s);
    // Skip the write when the URL already says this stage — every param write is
    // a navigation, and redundant ones caused a remount loop (2026-07-02).
    if (params.get("stage") !== s) setParams((prev) => { const next = new URLSearchParams(prev); next.set("stage", s); return next; }, { replace: true });
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };
  const [loaded, setLoaded] = useState(false);
  const [noCatalog, setNoCatalog] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [model, setModel] = useState<Model>({ products: [], aside: [], removed: [] });
  // Every optimistic catalog edit is written in order. The client cannot move
  // into Costs until the whole queue is safely stored.
  const catalogSaveTail = useRef<Promise<void>>(Promise.resolve());
  const pendingCatalogSaveTasks = useRef(new Set<Promise<boolean>>());
  const queuedCatalogSaveCount = useRef(0);
  const catalogSaveFailureVersion = useRef(0);
  const observedCatalogSaveFailureVersion = useRef(0);
  const catalogNeedsReconcile = useRef(false);
  const [catalogSavesPending, setCatalogSavesPending] = useState(0);
  const [catalogSaveError, setCatalogSaveError] = useState<string | null>(null);
  const catalogSaveErrorRef = useRef<string | null>(null);
  const [catalogRetry, setCatalogRetry] = useState(0);
  // Files stay on the Upload screen until every real-account file has reached
  // private storage. Sandbox files remain in memory because sandbox must never
  // write into the account.
  const [pendingUploads, setPendingUploads] = useState<File[]>([]);
  const [productUploadBusy, setProductUploadBusy] = useState(false);
  const [productUploadError, setProductUploadError] = useState<string | null>(null);
  const [stageChildBusy, setStageChildBusy] = useState(false);

  const accountQ = useQuery({
    queryKey: ["account"],
    queryFn: () => api.get<{ account: { id: string; baseCurrency: string } }>("/account"),
  });
  const accountId = accountQ.data?.account.id ?? "";
  const accountCurrency = accountQ.data?.account.baseCurrency || "USD";
  const scopedSandboxModelKey = accountId ? sandboxModelKey(accountId) : "";
  const exitSandbox = (): void => {
    window.sessionStorage.removeItem(SANDBOX_KEY);
    if (scopedSandboxModelKey) window.sessionStorage.removeItem(scopedSandboxModelKey);
    if (accountId) window.sessionStorage.removeItem(sandboxCategoryReviewKey(accountId));
    if (accountId) window.sessionStorage.removeItem(sandboxCostReviewKey(accountId));
    window.sessionStorage.removeItem(LEGACY_SANDBOX_MODEL_KEY);
    window.sessionStorage.removeItem(LEGACY_SANDBOX_CATEGORY_REVIEW_KEY);
    window.sessionStorage.removeItem(LEGACY_SANDBOX_COST_REVIEW_KEY);
    const next = new URLSearchParams(params);
    next.delete("mode");
    setParams(next, { replace: true });
  };
  const runKey = accountId ? `${accountId}:${mode}` : "";
  useEffect(() => {
    if (!accountId) return;
    // Older builds stored one sandbox catalog for every client in this browser.
    // Never read that global cache, and remove it once an account is known.
    window.sessionStorage.removeItem(LEGACY_SANDBOX_MODEL_KEY);
    window.sessionStorage.removeItem(LEGACY_SANDBOX_CATEGORY_REVIEW_KEY);
    window.sessionStorage.removeItem(LEGACY_SANDBOX_COST_REVIEW_KEY);
  }, [accountId]);
  const importHealthQ = useQuery({
    queryKey: ["onboarding", "import-health", "product-setup"],
    enabled: real && fromGuided && noCatalog,
    queryFn: () => api.get<ProductImportHealth>("/onboarding/import-health"),
    refetchInterval: (query) => query.state.data?.status === "pending" || (query.state.data?.activeJobs.length ?? 0) > 0 ? 3_000 : false,
  });
  const retriedReadyImport = useRef<string | null>(null);

  useEffect(() => {
    if (!noCatalog || !fromGuided || importHealthQ.data?.status !== "ready" || importHealthQ.data.activeJobs.length > 0) return;
    const readyKey = importHealthQ.data.lastSyncedAt ?? "ready-without-date";
    if (retriedReadyImport.current === readyKey) return;
    retriedReadyImport.current = readyKey;
    setCatalogRetry((value) => value + 1);
  }, [fromGuided, importHealthQ.data, noCatalog]);

  useEffect(() => {
    if (!runKey) return;
    // CIRCUIT BREAKER (2026-07-06 incident): if a redirect/remount loop keeps
    // re-mounting this page, cap how often the catalog load (incl. the LLM
    // /catalog/suggest) actually fires so it can't flood the AI rate limit. A
    // fast remount WAITS for the safe window; it never pretends an empty model
    // finished loading.
    const delayMs = Math.max(0, 2500 - (Date.now() - (catalogBootAt.get(runKey) ?? 0)));
    setLoaded(false);
    setNoCatalog(false);
    setLoadError(null);
    // Never leave the previous account's catalog visible during the safety wait.
    setModel({ products: [], aside: [], removed: [] });
    // rawSetStage, NOT setStage: startStage CAME from the URL. Writing it back
    // on mount is a redundant navigation (was one leg of the remount loop).
    rawSetStage(startStage);
    // ?session=fresh also clears the sandbox categorize cache — a fresh run
    // means the whole walkthrough, not just the costs chat. Wipe once, then
    // strip the param so later effect re-runs (stage hops, refreshes) resume
    // the walk instead of restarting it.
    if (freshRequested) {
      if (scopedSandboxModelKey) window.sessionStorage.removeItem(scopedSandboxModelKey);
      window.sessionStorage.removeItem(LEGACY_SANDBOX_MODEL_KEY);
      window.sessionStorage.removeItem(`ccd.onboarding.catReviewed.${mode}`);
      window.sessionStorage.removeItem(`ccd.onboarding.reviewed.${mode}`);
      if (accountId) {
        window.sessionStorage.removeItem(sandboxCategoryReviewKey(accountId));
        window.sessionStorage.removeItem(sandboxCostReviewKey(accountId));
      }
      if (params.get("session") === "fresh") setParams((prev) => { const next = new URLSearchParams(prev); next.delete("session"); return next; }, { replace: true });
    }
    // The AI grouping can run for minutes on a big cold catalog — long enough
    // for the edge to drop the response while the server keeps working (QA
    // round 8: the spinner stranded forever, yet a reload came back instantly
    // from the server's now-warm cache). So each attempt gets 75s, then we
    // quietly retry: the server dedups concurrent runs per account, so a retry
    // just re-subscribes to the SAME in-flight work and the one after it
    // finishes returns in milliseconds. `alive` stops the loop on unmount/mode
    // change — an abandoned sandbox chain must never write a preview catalog
    // over the real one (adversarial review 2026-07-05).
    let alive = true;
    let bootTimer: ReturnType<typeof setTimeout> | undefined;
    const withStallRetry = async <T,>(go: () => Promise<T>, attempts = 6, timeoutMs = 75_000): Promise<T> => {
      let lastErr: unknown;
      for (let a = 0; a < attempts && alive; a++) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
          return await Promise.race([
            go(),
            new Promise<never>((_, rej) => { timer = setTimeout(() => rej(new Error("stall")), timeoutMs); }),
          ]);
        } catch (e) { lastErr = e; } finally { if (timer) clearTimeout(timer); }
      }
      throw lastErr ?? new Error("cancelled");
    };
    const bootCatalog = async (): Promise<void> => {
      catalogBootAt.set(runKey, Date.now());
      // Sandbox categorize work lives only in the browser; restore this tab's
      // cached copy so a refresh doesn't wipe (or re-shuffle) the organizing.
      if (mode === "sandbox" && !freshRequested) {
        try {
          const cached = scopedSandboxModelKey ? window.sessionStorage.getItem(scopedSandboxModelKey) : null;
          if (cached) {
            const m = JSON.parse(cached) as Model;
            if (Array.isArray(m.products) && m.products.length > 0) {
              setModel({ ...m, removed: Array.isArray(m.removed) ? m.removed : [] });
              setLoaded(true);
              return;
            }
          }
        } catch { /* fall through to a fresh preview */ }
      }
      try {
        // A large first import can leave PostgreSQL warming its new order data.
        // The first summary occasionally reaches the statement timeout, while
        // the immediate retry succeeds. Retry this inexpensive read just like
        // the slower AI grouping below instead of dumping the client into a
        // dead-end refresh message.
        let v = await withStallRetry(() => api.get<ClassificationView>("/catalog/classification"), 2, 30_000);
        if (!alive) return;
        if (!v.skus || v.skus.length === 0) { setNoCatalog(true); setLoaded(true); return; }
        if (mode === "sandbox") {
          v = await withStallRetry(() => api.get<ClassificationView>("/catalog/preview-classification"));
        } else {
          const unclassifiedSelling = newlyUnclassifiedSellingSkuIds(v.skus);
          const suggestionKey = `${runKey}:${unclassifiedSelling.join(",") || "initial"}`;
          const needsSuggestions = v.groups.length === 0 || unclassifiedSelling.length > 0;
          if (needsSuggestions && !suggestedModes.has(suggestionKey)) {
          // Fire the AI grouping at most ONCE per session (durable latch above):
          // a reset account can classify to no product groups, which would keep
          // re-triggering this on every mount forever (2026-07-06 incident).
          // Existing groups do not mean the work is done: a later import can
          // add unclassified selling SKUs, and those must be suggested now.
            try {
              v = await withStallRetry(() => api.post<ClassificationView>("/catalog/suggest", {
                flagForReview: v.groups.length > 0,
              }));
              relockFinancialQueries(queryClient);
              if (v.groups.length === 0) throw new Error("no product groups");
              suggestedModes.add(suggestionKey);
            } catch (error) {
              suggestedModes.delete(suggestionKey);
              throw error;
            }
          }
        }
        if (mode === "real" && v.groups.length === 0) throw new Error("no product groups");
        if (!alive) return;
        const globalUnit = v.settings?.unitLabel || "units";
        const byId = new Map<string, Product>();
        for (const g of v.groups) byId.set(g.id, { id: g.id, name: g.name, unit: g.unit || globalUnit, kind: g.kind ?? "product", type: g.type ?? null, skus: [] });
        const aside: SheetSku[] = [];
        const removed: SheetSku[] = [];
        for (const s of v.skus) {
          // Never-sold SKUs never demand review (same rule as the costs chat):
          // stores carry dead platform-sample/test SKUs (Cristina 2026-07-11 had
          // 25 — watches & hoodies in a supplement shop) that would otherwise
          // flood the review list. They surface on their first real sale.
          const everSold = (s.units ?? 0) > 0 || (s.revenue ?? 0) > 0;
          const excluded = s.classification?.status === "excluded";
          const gid = s.classification?.productGroupId;
          const sk: SheetSku = { skuId: s.skuId, label: labelForGroup(s, gid ? byId.get(gid) : undefined), skuCode: s.skuCode, orderPrice: s.lastPrice, manualPrice: s.classification?.manualPrice ?? null, units: s.units, revenue: s.revenue, orders: s.orders, packSize: s.classification?.packSize ?? null, boxesPerSale: s.classification?.boxesPerSale ?? null, offerLabel: s.classification?.offerLabel ?? null, variant: s.classification?.variantLabel ?? null, components: s.components ?? [], needsReview: everSold && s.classification?.status === "needs_review", inferenceNote: s.classification?.inferenceNote ?? null, removedFromGroupId: gid ?? null };
          // A bundle with no products inside it can't be costed — force it to
          // "needs review" so it blocks Continue (founder 2026-07-06: an empty
          // bundle slipped straight to the costs step). Overrides a stale
          // "confirmed" so ✓ Looks-right can't bypass it either.
          if (everSold && gid && byId.get(gid)?.kind === "bundle" && (sk.components?.length ?? 0) === 0) sk.needsReview = true;
          if (s.classification?.status === "retired") removed.push(sk);
          else if (excluded || !gid || !byId.has(gid)) aside.push(sk);
          else byId.get(gid)!.skus.push(sk);
        }
        setModel({ products: [...byId.values()].filter((p) => p.skus.length > 0), aside, removed });
        setLoaded(true);
      } catch {
        if (!alive) return;
        setLoadError("I couldn't pull up your products just now. Nothing was changed.");
        setLoaded(true);
      }
    };
    if (delayMs > 0) bootTimer = setTimeout(() => { void bootCatalog(); }, delayMs);
    else void bootCatalog();
    return () => { alive = false; if (bootTimer) clearTimeout(bootTimer); };
  }, [api, catalogRetry, mode, runKey]);

  // Keep the sandbox categorize cache current — every local edit survives a
  // refresh for the rest of this tab's session.
  useEffect(() => {
    if (mode !== "sandbox" || !loaded || model.products.length === 0) return;
    if (!scopedSandboxModelKey) return;
    try { window.sessionStorage.setItem(scopedSandboxModelKey, JSON.stringify(model)); } catch { /* quota — non-fatal */ }
  }, [loaded, mode, model, scopedSandboxModelKey]);

  const reloadSavedCatalog = async (): Promise<void> => {
    const view = await api.get<ClassificationView>("/catalog/classification");
    rebuildFromView(view);
  };

  /**
   * Catalog edits are optimistic so the sheet feels instant, but their network
   * writes run one at a time. If any write fails, finish the already-queued
   * writes and then reload the one authoritative saved catalog. That keeps a
   * later successful edit from being painted over by an earlier failure.
   */
  const queueCatalogSave = (go: () => Promise<unknown>, failureMessage: string, onSuccess?: () => void): Promise<boolean> => {
    if (!real) return Promise.resolve(true);
    queuedCatalogSaveCount.current += 1;
    setCatalogSavesPending(queuedCatalogSaveCount.current);
    const task = catalogSaveTail.current.then(async () => {
      let saved = true;
      try {
        await go();
        relockFinancialQueries(queryClient);
        onSuccess?.();
      } catch {
        saved = false;
        catalogSaveFailureVersion.current += 1;
        catalogNeedsReconcile.current = true;
        toast.error(failureMessage);
      }

      queuedCatalogSaveCount.current -= 1;
      if (queuedCatalogSaveCount.current === 0 && catalogNeedsReconcile.current) {
        try {
          await reloadSavedCatalog();
          catalogNeedsReconcile.current = false;
          catalogSaveErrorRef.current = null;
          setCatalogSaveError(null);
        } catch {
          const message = "I couldn't reload your last saved catalog. Try again before moving to Costs.";
          catalogSaveErrorRef.current = message;
          setCatalogSaveError(message);
        }
      }
      setCatalogSavesPending(queuedCatalogSaveCount.current);
      return saved;
    });
    catalogSaveTail.current = task.then(() => undefined, () => undefined);
    pendingCatalogSaveTasks.current.add(task);
    void task.finally(() => pendingCatalogSaveTasks.current.delete(task));
    return task;
  };

  const waitForCatalogSaves = async (): Promise<boolean> => {
    while (pendingCatalogSaveTasks.current.size > 0) {
      await Promise.all([...pendingCatalogSaveTasks.current]);
    }
    const saved = catalogSaveFailureVersion.current === observedCatalogSaveFailureVersion.current && catalogSaveErrorRef.current === null;
    observedCatalogSaveFailureVersion.current = catalogSaveFailureVersion.current;
    return saved;
  };

  const retryCatalogReload = async (): Promise<void> => {
    setCatalogSavesPending((count) => Math.max(1, count));
    try {
      await reloadSavedCatalog();
      catalogNeedsReconcile.current = false;
      catalogSaveErrorRef.current = null;
      setCatalogSaveError(null);
    } catch {
      const message = "I still couldn't reload your saved catalog. Try again in a moment.";
      catalogSaveErrorRef.current = message;
      setCatalogSaveError(message);
    } finally {
      setCatalogSavesPending(queuedCatalogSaveCount.current);
    }
  };

  const uploadProductSetupFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0 || productUploadBusy) return;
    if (!real) {
      setPendingUploads(files);
      toast.success(`Got ${files.length} file${files.length === 1 ? "" : "s"}. I'll read ${files.length === 1 ? "it" : "them"} after your catalog is organized.`);
      setStage("categorize");
      return;
    }

    setProductUploadBusy(true);
    setProductUploadError(null);
    const failed: File[] = [];
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append("displayName", file.name);
      form.append("description", "Cost, shipping, fulfillment, or supplier file uploaded during product setup.");
      form.append("purpose", "costs");
      try {
        await api.postForm("/onboarding/data-files", form);
      } catch {
        failed.push(file);
      }
    }
    setProductUploadBusy(false);
    void queryClient.invalidateQueries({ queryKey: ["onboarding", "journey"] });
    if (failed.length > 0) {
      setPendingUploads(failed);
      setProductUploadError(`${failed.length} file${failed.length === 1 ? "" : "s"} did not upload. Nothing else was lost.`);
      return;
    }
    setPendingUploads([]);
    toast.success(`${files.length} file${files.length === 1 ? " is" : "s are"} saved. I'll use ${files.length === 1 ? "it" : "them"} when we set your costs.`);
    setStage("categorize");
  };

  /* ---- mutations (persist in real mode; local-only in sandbox) ---- */
  const moveSku = (skuId: string, toGroupId: string): void => {
    setModel((m) => {
      let found: SheetSku | undefined;
      const products = m.products.map((p) => ({ ...p, skus: p.skus.filter((s) => { if (s.skuId === skuId) { found = s; return false; } return true; }) }));
      let aside = m.aside.filter((s) => { if (s.skuId === skuId) { found = found ?? s; return false; } return true; });
      if (!found) return m;
      // Moving to a new product clears the old variant (it belongs to the old product).
      const moved: SheetSku = { ...found, variant: null };
      if (toGroupId === ASIDE) aside = [...aside, moved];
      else { const i = products.findIndex((p) => p.id === toGroupId); const cur = products[i]; if (cur) products[i] = { ...cur, skus: [...cur.skus, moved] }; }
      // Keep empty bundle/bonus lanes as drop targets; drop empty product lanes.
      return { ...m, products: products.filter((p) => p.skus.length > 0 || p.kind !== "product"), aside };
    });
    if (real) {
      // Moving/excluding a flagged SKU resolves it → refresh the gate box + counter.
      void queueCatalogSave(
        () => toGroupId === ASIDE
          ? api.post("/catalog/exclude", { skuIds: [skuId], excluded: true })
          : api.patch("/catalog/classifications", { edits: [{ skuId, productGroupId: toGroupId, variantLabel: null, status: "confirmed" }] }),
        "Couldn't save that move. I put the sheet back to its saved version.",
        refreshGate,
      );
    }
  };

  const renameProduct = (id: string, name: string): void => {
    setModel((m) => ({ ...m, products: m.products.map((p) => (p.id === id ? { ...p, name } : p)) }));
    if (real) void queueCatalogSave(
      () => api.patch(`/catalog/groups/${id}`, { name }),
      "Couldn't save that name. I put the sheet back to its saved version.",
    );
  };

  // "No longer sold" is not the same as set aside. A removed SKU disappears
  // from current setup but keeps its product mapping and any costs already on
  // file. Uncosted history stays visibly incomplete instead of being called CM.
  const retireSkus = (skuIds: string[], retired: boolean): void => {
    const ids = new Set(skuIds);
    setModel((m) => {
      if (retired) {
        const pulled: SheetSku[] = [];
        const products = m.products.map((p) => ({ ...p, skus: p.skus.filter((s) => {
          if (!ids.has(s.skuId)) return true;
          pulled.push({ ...s, removedFromGroupId: p.id });
          return false;
        }) }));
        const aside = m.aside.filter((s) => {
          if (!ids.has(s.skuId)) return true;
          pulled.push({ ...s, removedFromGroupId: null });
          return false;
        });
        return { ...m, products: products.filter((p) => p.skus.length > 0 || p.kind !== "product"), aside, removed: [...m.removed, ...pulled] };
      }

      const restoring = m.removed.filter((s) => ids.has(s.skuId));
      const products = m.products.map((p) => ({ ...p, skus: [...p.skus, ...restoring.filter((s) => s.removedFromGroupId === p.id)] }));
      const aside = [...m.aside, ...restoring.filter((s) => !s.removedFromGroupId || !products.some((p) => p.id === s.removedFromGroupId))];
      return { ...m, products, aside, removed: m.removed.filter((s) => !ids.has(s.skuId)) };
    });
    if (real) {
      void queueCatalogSave(
        async () => {
          await api.post<ClassificationView>("/catalog/retire", { skuIds, retired });
        },
        retired ? "Couldn't remove those SKUs. I put the sheet back to its saved version." : "Couldn't restore those SKUs. I put the sheet back to its saved version.",
        () => { catalogNeedsReconcile.current = true; refreshGate(); },
      );
    }
  };

  // Update the in-memory model only. Controlled inputs call this on every keystroke
  // so a field always shows exactly what's in the model — no stale/garbled values
  // when the list re-renders (the "typed 1, it flipped to 0" bug).
  const editLocal = (skuId: string, patch: { packSize?: number | null; boxesPerSale?: number | null; offerLabel?: string | null; manualPrice?: number | null }): void => {
    setModel((m) => {
      const apply = (s: SheetSku): SheetSku => (s.skuId === skuId ? { ...s, ...patch } : s);
      return { ...m, products: m.products.map((p) => ({ ...p, skus: p.skus.map(apply) })), aside: m.aside.map(apply) };
    });
  };
  const editSku = (skuId: string, patch: { packSize?: number | null; boxesPerSale?: number | null; offerLabel?: string | null; manualPrice?: number | null }): void => {
    if (patch.boxesPerSale !== undefined && patch.boxesPerSale !== null && (!Number.isFinite(patch.boxesPerSale) || patch.boxesPerSale <= 0)) {
      editLocal(skuId, { boxesPerSale: null });
      toast.error("Units per sale must be more than zero.");
      return;
    }
    editLocal(skuId, patch);
    if (real) void queueCatalogSave(
      () => api.patch("/catalog/classifications", { edits: [{ skuId, ...patch }] }),
      "Couldn't save that edit. I put the sheet back to its saved version.",
    );
  };

  // Apply a per-SKU patch to many SKUs in ONE model update + ONE request (for bulk
  // field-fills like "set all the blanks to 0" — never a PATCH-per-SKU storm).
  type FieldPatch = { packSize?: number | null; boxesPerSale?: number | null; manualPrice?: number | null };
  const bulkEdit = (patchById: Map<string, FieldPatch>): void => {
    if (patchById.size === 0) return;
    setModel((m) => {
      const apply = (s: SheetSku): SheetSku => (patchById.has(s.skuId) ? { ...s, ...patchById.get(s.skuId)! } : s);
      return { ...m, products: m.products.map((p) => ({ ...p, skus: p.skus.map(apply) })), aside: m.aside.map(apply) };
    });
    if (real) void queueCatalogSave(
      () => api.patch("/catalog/classifications", { edits: [...patchById.entries()].map(([skuId, patch]) => ({ skuId, ...patch })) }),
      "Couldn't save those edits. I put the sheet back to its saved version.",
    );
  };

  // Every complete bundle snapshot is queued in order. A newer edit can never
  // race an older replace-all request or vanish before the older one finishes.
  const setSkuComponents = (bundleSkuId: string, components: SkuComponent[]): void => {
    setModel((m) => {
      const apply = (s: SheetSku): SheetSku => (s.skuId === bundleSkuId ? { ...s, components } : s);
      return { ...m, products: m.products.map((p) => ({ ...p, skus: p.skus.map(apply) })), aside: m.aside.map(apply) };
    });
    const completeDraft = components.every((component) => component.label.trim() && Number.isFinite(component.quantity) && component.quantity > 0);
    if (real && completeDraft) {
      void queueCatalogSave(
        () => api.put("/catalog/bundle-components", {
          bundleSkuId,
          components: components.map((c) => ({ componentSkuId: c.componentSkuId, componentProductGroupId: c.componentProductGroupId, componentLabel: c.label, quantity: c.quantity })),
        }),
        "Couldn't save what's inside that bundle. I put the sheet back to its saved version.",
      );
    }
  };

  const setProductKind = (id: string, kind: GroupKind): void => {
    setModel((m) => ({ ...m, products: m.products.map((p) => (p.id === id ? { ...p, kind } : p)) }));
    if (real) void queueCatalogSave(
      () => api.patch(`/catalog/groups/${id}`, { kind }),
      "Couldn't save that product type. I put the sheet back to its saved version.",
    );
  };

  // Tag a group as physical / software / info / service. Set here in Categorize so
  // the Costs step asks the right cost questions per type (and batches by type)
  // instead of re-asking. Persists on the product group (real mode); local in sandbox.
  const setProductType = (id: string, type: ProductType): void => {
    setModel((m) => ({ ...m, products: m.products.map((p) => (p.id === id ? { ...p, type } : p)) }));
    if (real) void queueCatalogSave(
      () => api.patch(`/catalog/groups/${id}`, { type }),
      "Couldn't save that choice. I put the sheet back to its saved version.",
    );
  };

  // Merchant confirmed a flagged (AI-unsure) row is right → clear the flag + mark
  // it confirmed so it stops nagging and the gate clears.
  const confirmSku = (skuId: string): void => {
    // An empty bundle can NEVER be confirmed away — it has no products to cost,
    // so confirming it would let it bypass the Continue gate. Refuse + nudge.
    const target = [...model.products, { kind: "", skus: model.aside } as unknown as Product].flatMap((p) => p.skus.map((s) => ({ s, kind: p.kind }))).find((x) => x.s.skuId === skuId);
    if (target && target.kind === "bundle" && !bundlePartsComplete(target.s)) {
      toast.error("Add the products inside this bundle first — a bundle with nothing in it can't be costed.");
      return;
    }
    setModel((m) => {
      const clear = (s: SheetSku): SheetSku => (s.skuId === skuId ? { ...s, needsReview: false } : s);
      return { ...m, products: m.products.map((p) => ({ ...p, skus: p.skus.map(clear) })), aside: m.aside.map(clear) };
    });
    if (real) void queueCatalogSave(
      () => api.patch("/catalog/classifications", { edits: [{ skuId, status: "confirmed" }] }),
      "Couldn't save that confirmation. I put the sheet back to its saved version.",
      refreshGate,
    );
  };

  // One uncertainty = one confirmation: when the AI's doubt was about a whole
  // product (its flags all share the same judgment call), the merchant clears
  // them in ONE tap instead of row by row (founder 2026-07-11: 18 amber rows
  // were really a handful of questions). Empty bundles keep their flag — they
  // still need their parts before they can be costed.
  const confirmGroup = (groupId: string): void => {
    setModel((m) => ({
      ...m,
      products: m.products.map((p) => p.id !== groupId ? p : {
        ...p,
        skus: p.skus.map((s) => (p.kind === "bundle" && !bundlePartsComplete(s) ? s : { ...s, needsReview: false })),
      }),
    }));
    if (real) void queueCatalogSave(
      () => api.post("/catalog/approve-all", { groupId }),
      "Couldn't save those confirmations. I put the sheet back to its saved version.",
      refreshGate,
    );
  };

  // Split a product into one product per variant (each variant becomes its own product).
  const splitProductByVariant = async (id: string): Promise<void> => {
    const product = model.products.find((p) => p.id === id);
    if (!product) return;
    const variants = variantsOf(product);
    if (variants.length < 2) return;
    if (real) {
      await queueCatalogSave(
        () => api.post("/catalog/groups/split-by-variant", { groupId: id }),
        "Couldn't split that product. I put the sheet back to its saved version.",
        () => { catalogNeedsReconcile.current = true; },
      );
      return;
    }
    // sandbox: local only
    setModel((m) => {
      const others = m.products.filter((p) => p.id !== id);
      const newProducts: Product[] = variants.map((variant) => ({ id: synthId(), name: `${product.name} — ${variant}`, unit: product.unit, kind: "product", skus: product.skus.filter((s) => (s.variant ?? "") === variant).map((s) => ({ ...s, variant: null })) }));
      const noVariant = product.skus.filter((s) => !s.variant || !s.variant.trim());
      if (noVariant.length) newProducts.push({ ...product, skus: noVariant });
      return { ...m, products: [...others, ...newProducts] };
    });
  };

  function rebuildFromView(v: ClassificationView): void {
    const globalUnit = v.settings?.unitLabel || "units";
    const byId = new Map<string, Product>();
    for (const g of v.groups) byId.set(g.id, { id: g.id, name: g.name, unit: g.unit || globalUnit, kind: g.kind ?? "product", type: g.type ?? null, skus: [] });
    const aside: SheetSku[] = [];
    const removed: SheetSku[] = [];
    for (const s of v.skus) {
      // Never-sold SKUs never demand review (see note in the loader above).
      const everSold = (s.units ?? 0) > 0 || (s.revenue ?? 0) > 0;
      const gid = s.classification?.productGroupId;
      const sk: SheetSku = { skuId: s.skuId, label: labelForGroup(s, gid ? byId.get(gid) : undefined), skuCode: s.skuCode, orderPrice: s.lastPrice, manualPrice: s.classification?.manualPrice ?? null, units: s.units, revenue: s.revenue, orders: s.orders, packSize: s.classification?.packSize ?? null, boxesPerSale: s.classification?.boxesPerSale ?? null, offerLabel: s.classification?.offerLabel ?? null, variant: s.classification?.variantLabel ?? null, components: s.components ?? [], needsReview: everSold && s.classification?.status === "needs_review", inferenceNote: s.classification?.inferenceNote ?? null, removedFromGroupId: gid ?? null };
      // Empty bundle → needs review so it blocks Continue (see note above).
      if (everSold && gid && byId.get(gid)?.kind === "bundle" && (sk.components?.length ?? 0) === 0) sk.needsReview = true;
      if (s.classification?.status === "retired") removed.push(sk);
      else if (s.classification?.status === "excluded" || !gid || !byId.has(gid)) aside.push(sk);
      else byId.get(gid)!.skus.push(sk);
    }
    setModel({ products: [...byId.values()].filter((p) => p.skus.length > 0), aside, removed });
  }

  /* ---- natural-language command engine (preview → confirm → undo) ---- */

  type Placement = { skuId: string; dest: string; variant: string | null };

  // Apply a set of placements ATOMICALLY: each SKU lands at {dest, variant} in a
  // single model update + a single batched API write — so product and variant can
  // never reorder, and Undo restores both (incl. a null variant) exactly. A dest
  // product that no longer exists falls back to set-aside so a SKU is never lost.
  const applyPlacements = (placements: Placement[]): void => {
    if (placements.length === 0) return;
    const currentGroupIds = new Set(model.products.map((product) => product.id));
    const normalizedPlacements = placements.map((placement) => ({
      ...placement,
      dest: placement.dest === ASIDE || !currentGroupIds.has(placement.dest) ? ASIDE : placement.dest,
    }));
    const byId = new Map(normalizedPlacements.map((p) => [p.skuId, p]));
    setModel((m) => {
      const validIds = new Set(m.products.map((p) => p.id));
      const pulled: SheetSku[] = [];
      const products = m.products.map((p) => ({ ...p, skus: p.skus.filter((s) => { if (byId.has(s.skuId)) { pulled.push(s); return false; } return true; }) }));
      let aside = m.aside.filter((s) => { if (byId.has(s.skuId)) { pulled.push(s); return false; } return true; });
      for (const s of pulled) {
        const pl = byId.get(s.skuId)!;
        const toAside = pl.dest === ASIDE || !validIds.has(pl.dest);
        const sku: SheetSku = { ...s, variant: toAside ? null : pl.variant };
        if (toAside) aside = [...aside, sku];
        else { const i = products.findIndex((p) => p.id === pl.dest); if (products[i]) products[i] = { ...products[i]!, skus: [...products[i]!.skus, sku] }; }
      }
      return { ...m, products: products.filter((p) => p.skus.length > 0 || p.kind !== "product"), aside };
    });
    if (real) {
      void queueCatalogSave(
        () => api.post("/catalog/placements", {
          placements: normalizedPlacements.map((placement) => ({
            skuId: placement.skuId,
            dest: placement.dest === ASIDE ? null : placement.dest,
            variant: placement.dest === ASIDE ? null : placement.variant,
          })),
        }),
        "Couldn't save that change. I put the sheet back to its saved version.",
        refreshGate,
      );
    }
  };

  // Move SKUs to a product/set-aside (clears the old variant).
  const relocate = (skuIds: string[], dest: string): void => applyPlacements(skuIds.map((skuId) => ({ skuId, dest, variant: null })));

  // Set/clear the variant on SKUs in place (no move). One batched write.
  const setVariantOf = (skuIds: string[], variant: string | null): void => {
    const ids = new Set(skuIds);
    setModel((m) => {
      const apply = (s: SheetSku): SheetSku => (ids.has(s.skuId) ? { ...s, variant } : s);
      return { ...m, products: m.products.map((p) => ({ ...p, skus: p.skus.map(apply) })), aside: m.aside.map(apply) };
    });
    if (real) void queueCatalogSave(
      () => api.patch("/catalog/classifications", { edits: skuIds.map((skuId) => ({ skuId, variantLabel: variant })) }),
      "Couldn't save that variant. I put the sheet back to its saved version.",
    );
  };

  // Capture each SKU's current {dest, variant} so we can restore it on Undo.
  const snapshotLocations = (skuIds: string[]): Placement[] => {
    const ids = new Set(skuIds);
    const out: Placement[] = [];
    for (const p of model.products) for (const s of p.skus) if (ids.has(s.skuId)) out.push({ skuId: s.skuId, dest: p.id, variant: s.variant });
    for (const s of model.aside) if (ids.has(s.skuId)) out.push({ skuId: s.skuId, dest: ASIDE, variant: s.variant });
    return out;
  };
  const restoreLocations = (snap: Placement[]): void => applyPlacements(snap);

  const mergeProducts = (sourceId: string, targetId: string): void => {
    setModel((m) => {
      const src = m.products.find((p) => p.id === sourceId);
      if (!src) return m;
      return { ...m, products: m.products.filter((p) => p.id !== sourceId).map((p) => (p.id === targetId ? { ...p, skus: [...p.skus, ...src.skus] } : p)) };
    });
    if (real) void queueCatalogSave(
      () => api.post("/catalog/groups/merge", { sourceId, targetId }),
      "Couldn't merge those products. I put the sheet back to its saved version.",
    );
  };

  const previewCommand = (action: CatalogAction): Preview => {
    const fail = (summary: string): Preview => ({ ok: false, summary, count: 0, sample: [], run: () => null });
    const matchProduct = (name?: string): Product | undefined => {
      if (!name) return undefined;
      const n = name.toLowerCase();
      const ps = model.products;
      return ps.find((p) => p.name.toLowerCase() === n) ?? ps.find((p) => p.name.toLowerCase().includes(n)) ?? ps.find((p) => n.includes(p.name.toLowerCase()));
    };
    const resolveSkus = (): SheetSku[] => {
      const sel = action.selector ?? {};
      if (!sel.product && !sel.variant && !sel.contains) return [];
      let pool: SheetSku[] = action.op === "restore"
        ? model.removed
        : action.op === "include"
          ? model.aside
          : action.op === "retire"
            ? model.products.flatMap((p) => p.skus)
            : [...model.products.flatMap((p) => p.skus), ...model.aside];
      if (sel.product) { const p = matchProduct(sel.product); pool = p ? p.skus : []; }
      if (sel.variant) pool = pool.filter((s) => (s.variant ?? "").toLowerCase().includes(sel.variant!.toLowerCase()));
      if (sel.contains) pool = pool.filter((s) => `${s.label} ${s.skuCode}`.toLowerCase().includes(sel.contains!.toLowerCase()));
      if (sel.codes?.length) {
        const codes = new Set(sel.codes.map((code) => code.trim().toLowerCase()));
        pool = pool.filter((s) => codes.has(s.skuCode.trim().toLowerCase()));
      }
      return pool;
    };
    const sampleOf = (skus: SheetSku[]): string[] => skus.slice(0, 4).map((s) => s.label);

    switch (action.op) {
      case "move":
      case "exclude":
      case "include": {
        const skus = resolveSkus();
        if (skus.length === 0) return fail("I couldn't find any SKUs matching that — can you be more specific?");
        const toAside = action.op === "exclude" || (action.op === "move" && action.toAside);
        const dest = toAside ? ASIDE : matchProduct(action.targetProduct ?? action.intoProduct);
        if (!toAside && !dest) return fail(`Which product should I move ${skus.length} SKU${skus.length === 1 ? "" : "s"} into?`);
        const destId = toAside ? ASIDE : (dest as Product).id;
        const ids = skus.map((s) => s.skuId);
        return {
          ok: true, count: skus.length, sample: sampleOf(skus),
          summary: toAside ? `Set aside ${skus.length} SKU${skus.length === 1 ? "" : "s"}` : `Move ${skus.length} SKU${skus.length === 1 ? "" : "s"} into “${(dest as Product).name}”`,
          run: () => { const snap = snapshotLocations(ids); relocate(ids, destId); return () => restoreLocations(snap); },
        };
      }
      case "retire":
      case "restore": {
        const skus = resolveSkus();
        if (skus.length === 0) {
          const requested = new Set((action.selector?.codes ?? []).map((code) => code.trim().toLowerCase()));
          const inAside = action.op === "retire" && model.aside.some((s) => requested.has(s.skuCode.trim().toLowerCase()));
          return fail(action.op === "restore"
            ? "I couldn't find those SKUs in the removed list."
            : inAside
              ? "That SKU is in Set aside. Move it back to its product first, then use Remove so its past sales stay in the right place."
              : "I couldn't find any SKUs matching that. You can also use Remove beside the SKU in the sheet.");
        }
        const ids = skus.map((s) => s.skuId);
        const retiring = action.op === "retire";
        return {
          ok: true,
          count: skus.length,
          sample: sampleOf(skus),
          summary: retiring
            ? `Remove ${skus.length} SKU${skus.length === 1 ? "" : "s"} from current setup (past sales stay; old costs are still needed for historical profit)`
            : `Restore ${skus.length} SKU${skus.length === 1 ? "" : "s"} to setup`,
          // Restore is always available in the Removed section. Do not expose an
          // instant Undo that could race the first save and leave the server in
          // the opposite state from the sheet.
          run: () => { retireSkus(ids, retiring); return null; },
        };
      }
      case "setVariant": {
        const skus = resolveSkus();
        if (skus.length === 0) return fail("I couldn't find any SKUs matching that.");
        const variant = action.variant?.trim() || null;
        const ids = skus.map((s) => s.skuId);
        return {
          ok: true, count: skus.length, sample: sampleOf(skus),
          summary: variant ? `Label ${skus.length} SKU${skus.length === 1 ? "" : "s"} as “${variant}”` : `Clear the variant on ${skus.length} SKU${skus.length === 1 ? "" : "s"}`,
          run: () => { const snap = snapshotLocations(ids); setVariantOf(ids, variant); return () => restoreLocations(snap); },
        };
      }
      case "rename": {
        const p = matchProduct(action.selector?.product);
        if (!p) return fail("Which product should I rename?");
        if (!action.name) return fail("What should I rename it to?");
        const newName = action.name, oldName = p.name, pid = p.id;
        return { ok: true, count: 1, sample: [oldName], summary: `Rename “${oldName}” → “${newName}”`, run: () => { renameProduct(pid, newName); return () => renameProduct(pid, oldName); } };
      }
      case "setKind": {
        const p = matchProduct(action.selector?.product);
        if (!p || !action.kind) return fail("Which product, and what should it become (product, bundle, or bonus)?");
        const pid = p.id, prev = p.kind, kind = action.kind;
        return { ok: true, count: 1, sample: [p.name], summary: `Mark “${p.name}” as a ${kind}`, run: () => { setProductKind(pid, kind); return () => setProductKind(pid, prev); } };
      }
      case "split": {
        const p = matchProduct(action.selector?.product);
        if (!p) return fail("Which product should I split?");
        if (variantsOf(p).length < 2) return fail(`“${p.name}” doesn't have multiple variants to split.`);
        const pid = p.id;
        return { ok: true, count: variantsOf(p).length, sample: variantsOf(p), summary: `Split “${p.name}” into ${variantsOf(p).length} separate products`, run: () => { void onSplitId(pid); return null; } };
      }
      case "merge": {
        const src = matchProduct(action.selector?.product), dst = matchProduct(action.intoProduct ?? action.targetProduct);
        if (!src || !dst || src.id === dst.id) return fail("Which two products should I merge (and into which)?");
        const sid = src.id, tid = dst.id;
        return { ok: true, count: src.skus.length, sample: [src.name], summary: `Merge “${src.name}” into “${dst.name}”`, run: () => { mergeProducts(sid, tid); return null; } };
      }
      case "setField": {
        // Don't silently floor a missing/mis-parsed value to 0 — that zeroed the
        // cost driver (# Units/Sale) on every targeted SKU ("typed 1, got 0").
        // Re-ask instead; units/sale must be a positive number. (founder 2026-06-22)
        const value = action.value;
        if (value === undefined || value === null || !Number.isFinite(value) || value <= 0) {
          return fail(`Tell me how many units ship per sale — like "set units to 1" or "set ${model.products[0]?.name ?? "your product"} to 6".`);
        }
        // The categorize sheet has exactly ONE fillable number: # Units/Sale. Qty and
        // Price were removed (price comes from orders), so a fill command can only ever
        // touch # Units/Sale — never the phantom Qty/Price the old multi-field command
        // counted (that's why "fill the blanks" was hitting 52 SKUs instead of 17).
        if (action.field === "price") return fail("Price comes straight from your orders — there's nothing to set here.");
        if (action.field === "qty") return fail("Qty isn't part of this step — # Units/Sale is the only number to set.");
        const sel = action.selector ?? {};
        const hasSel = Boolean(sel.product || sel.variant || sel.contains);
        // No selector = "everything that needs it": only PRODUCT SKUs have an editable
        // # Units/Sale (bundles derive it from their parts; bonus/misc/wholesale = 1).
        const pool = hasSel ? resolveSkus() : model.products.filter((p) => p.kind === "product").flatMap((p) => p.skus);
        const targets = pool.filter((s) => !action.onlyBlank || s.boxesPerSale === null);
        if (targets.length === 0) return fail(action.onlyBlank ? "Nothing's blank in # Units/Sale — you're all set there." : "I couldn't find any SKUs to fill.");
        return {
          ok: true, count: targets.length, sample: targets.slice(0, 4).map((s) => s.label),
          summary: `${action.onlyBlank ? "Fill blank " : "Set "}# Units/Sale to ${value} on ${targets.length} SKU${targets.length === 1 ? "" : "s"}`,
          run: () => {
            const prevById = new Map(targets.map((s) => [s.skuId, { boxesPerSale: s.boxesPerSale } as FieldPatch]));
            bulkEdit(new Map(targets.map((s) => [s.skuId, { boxesPerSale: value } as FieldPatch])));
            return () => bulkEdit(prevById);
          },
        };
      }
      default:
        return fail("I'm not sure how to do that yet.");
    }
  };
  const onSplitId = (id: string): Promise<void> | void => splitProductByVariant(id);

  if (!accountId && accountQ.isError) return (
    <div style={{ maxWidth: 1080 }}><Header mode={mode} />
      <div style={card}>
        <p style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>I couldn't open this account's product setup. Nothing was changed.</p>
        <button style={{ ...primaryBtn, marginTop: 12 }} onClick={() => { void accountQ.refetch(); }}>Try again</button>
      </div>
    </div>
  );
  if (!loaded) return <CatalogLoading mode={mode} />;
  if (loadError) return (
    <div style={{ maxWidth: 1080 }}><Header mode={mode} />
      <div style={card}>
        <p style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>{loadError}</p>
        <button style={{ ...primaryBtn, marginTop: 12 }} onClick={() => setCatalogRetry((n) => n + 1)}>Try again</button>
      </div>
    </div>
  );
  if (noCatalog && fromGuided) {
    const health = importHealthQ.data;
    const active = health?.activeJobs[0];
    const importing = importHealthQ.isLoading || health?.status === "pending" || Boolean(active);
    const progress = active?.progressCurrent !== null && active?.progressCurrent !== undefined && active.progressTotal
      ? `${whole(active.progressCurrent)} of ${whole(active.progressTotal)} items imported`
      : "Your products are still being imported.";
    return (
      <div style={{ maxWidth: 1080 }}><Header mode={mode} />
        <div style={card}>
          {importing ? (
            <>
              <p style={{ margin: 0, fontWeight: 700 }}>Your products are still loading.</p>
              <p style={{ margin: "7px 0 0", color: "var(--body)" }}>{progress} You can leave this page. We will keep working and open Product Setup when they are ready.</p>
            </>
          ) : health?.status === "error" || importHealthQ.isError ? (
            <>
              <p style={{ margin: 0, color: "var(--bad)", fontWeight: 700 }}>We could not finish importing your products.</p>
              <p style={{ margin: "7px 0 0", color: "var(--body)" }}>{health?.errorMessage ?? "Your saved setup is safe."}</p>
              <button style={{ ...primaryBtn, marginTop: 12 }} onClick={() => { void importHealthQ.refetch(); setCatalogRetry((value) => value + 1); }}>Try again</button>
              <button style={{ ...ghostBtn, marginTop: 12, marginLeft: 8 }} onClick={() => navigate("/welcome?step=4")}>Check the import</button>
            </>
          ) : (
            <>
              <p style={{ margin: 0, fontWeight: 700 }}>The import finished, but no products came through.</p>
              <p style={{ margin: "7px 0 0", color: "var(--body)" }}>Go back to the import check so we can show what needs attention. You do not need to reconnect everything.</p>
              <button style={{ ...primaryBtn, marginTop: 12 }} onClick={() => navigate("/welcome?step=4")}>Check the import</button>
            </>
          )}
        </div>
      </div>
    );
  }
  if (noCatalog) return (
    <div style={{ maxWidth: 1080 }}><Header mode={mode} />
      <div style={card}><p style={{ margin: 0, color: "var(--body)" }}>I don't see any products yet — connect and sync your store first, then come back.</p>
      <a href="/connections" style={{ ...primaryBtn, display: "inline-block", marginTop: 12, textDecoration: "none" }}>Connect your store →</a></div>
    </div>
  );

  // Types tagged in Categorize, keyed by normalized name so the Costs step can read
  // them even in sandbox (where nothing is persisted and the ids don't match). PRODUCTS
  // only — bonus/misc are never typed and must keep their own cost wording. If two
  // products normalize to the same name with different types, drop it as ambiguous so
  // Costs falls back to asking rather than silently picking the last one.
  const categorizeTypes: Record<string, ProductType> = {};
  const typeKeyClash = new Set<string>();
  for (const p of model.products) {
    if (p.kind !== "product" || !p.type) continue;
    const k = typeKeyOf(p.name);
    if (categorizeTypes[k] && categorizeTypes[k] !== p.type) typeKeyClash.add(k);
    categorizeTypes[k] = p.type;
  }
  for (const k of typeKeyClash) delete categorizeTypes[k];

  // SANDBOX bridge: categorize edits never persist, so the costs engine gets the
  // exact catalog the customer just organized, handed over with the conversation.
  // Real mode reads the persisted catalog server-side — no payload needed.
  // Never-sold SKUs stay out of CATEGORIZE too (founder 2026-07-03: "they're
  // still showing up") — same rule the costs flow already applies. Their blank
  // # Units/Sale boxes were also blocking Continue. If the store has NO sales
  // at all (pre-launch), keep everything — there'd be nothing to organize.
  const bundleReferences = bundleReferenceIndex(model.products);
  const soldSku = (s: SheetSku): boolean => (s.units ?? 0) > 0 || (s.revenue ?? 0) > 0;
  const neededSku = (productId: string | null, s: SheetSku): boolean => skuNeedsProductSetup(s, productId, bundleReferences);
  const anySold = model.products.some((p) => p.skus.some(soldSku)) || model.aside.some(soldSku);
  const catModel: Model = anySold
    ? { products: model.products.map((p) => ({ ...p, skus: p.skus.filter((s) => neededSku(p.id, s)) })).filter((p) => p.skus.length > 0), aside: model.aside.filter((s) => neededSku(s.removedFromGroupId, s)), removed: model.removed }
    : model;
  const catHiddenCount = anySold ? model.products.reduce((n, p) => n + p.skus.filter((s) => !neededSku(p.id, s)).length, 0) + model.aside.filter((s) => !neededSku(s.removedFromGroupId, s)).length : 0;

  const costsCatalog = mode !== "sandbox" ? undefined : {
    unitLabel: model.products[0]?.unit || "units",
    groups: model.products
      .filter((p) => p.kind === "product" || p.kind === "bundle")
      .map((p) => ({
        id: p.id, name: p.name, unit: p.unit, kind: p.kind, type: p.type,
        // Never-sold SKUs stay out of the costs conversation entirely (blank
        // rows tripped the founder up) — they surface once they have a sale.
        skus: p.skus.filter((s) => neededSku(p.id, s)).map((s) => ({
          skuId: s.skuId, label: s.label, skuCode: s.skuCode,
          price: s.manualPrice ?? s.orderPrice ?? 0,
          boxesPerSale: s.boxesPerSale ?? s.packSize ?? 1,
          offerLabel: s.offerLabel, needsReview: s.needsReview,
          components: (s.components ?? []).map((c) => ({ name: c.label, skuId: c.componentSkuId, groupId: c.componentProductGroupId, qty: c.quantity })),
        })),
      }))
      .filter((p) => p.skus.length > 0),
  };

  return (
    <div style={{ maxWidth: 1100 }}>
      <Header mode={mode} onExit={exitSandbox} />
      <StageBar stage={stage} onJump={setStage} allowForwardJumps={mode === "sandbox"} navigationLocked={productUploadBusy || catalogSavesPending > 0 || stageChildBusy} />
      {stage === "upload" && <UploadStep onSkip={() => setStage("categorize")} onContinueWithoutFailedFiles={() => { setPendingUploads([]); setProductUploadError(null); setStage("categorize"); }} onAttach={(files) => { void uploadProductSetupFiles(productUploadError ? [...pendingUploads, ...files] : files); }} busy={productUploadBusy} error={productUploadError} onRetry={() => { void uploadProductSetupFiles(pendingUploads); }} />}
      {stage === "categorize" && <Categorize model={catModel} hiddenCount={catHiddenCount} mode={mode} accountId={accountId} currency={accountCurrency} startAtReview={startAtReview} fresh={freshRequested} onMove={moveSku} onRetire={retireSkus} onRename={renameProduct} onEdit={editSku} onEditLocal={editLocal} onSplit={splitProductByVariant} onSetKind={setProductKind} onSetType={setProductType} onPreview={previewCommand} onSaveComponents={setSkuComponents} onConfirm={confirmSku} onConfirmGroup={confirmGroup} savePending={catalogSavesPending > 0} saveError={catalogSaveError} onRetrySave={() => { void retryCatalogReload(); }} onWaitForSaves={waitForCatalogSaves} onContinue={() => setStage("costs")} />}
      {stage === "costs" && <CostsChat mode={mode} accountId={accountId} catalog={costsCatalog} fresh={freshRequested} resumeAfterOverhead={resumeAfterOverhead} fromGuided={fromGuided} pendingUploads={mode === "sandbox" ? pendingUploads : undefined} onUploadsConsumed={() => setPendingUploads([])} onConsumedFresh={() => { freshOnce.current = false; setParams((prev) => { const next = new URLSearchParams(prev); next.delete("session"); return next; }, { replace: true }); }} onConsumedOverheadResume={() => setParams((prev) => { const next = new URLSearchParams(prev); next.delete("overheadReviewed"); return next; }, { replace: true })} onDone={updateMode ? () => { refreshGate(); navigate("/welcome"); } : () => setStage("review")} doneCtaLabel={updateMode ? "Back to setup →" : undefined} onBack={() => setStage("categorize")} onBusyChange={setStageChildBusy} />}
      {stage === "review" && <Review model={model} mode={mode} accountId={accountId} fromGuided={fromGuided} resetConfirmed={resetReview} onBack={() => setStage("costs")} onCatalogReview={() => setStage("categorize")} onBusyChange={setStageChildBusy} />}
    </div>
  );
}

function Header({ mode, onExit }: { mode: string; onExit?: () => void }): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
      <div style={{ color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Product setup</div>
      {mode === "sandbox" && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--c2)", background: "color-mix(in srgb, var(--c2) 16%, transparent)", padding: "2px 9px", borderRadius: 999 }}>SANDBOX · nothing saves</span>}
      {mode === "sandbox" && onExit && <button onClick={onExit} style={{ fontSize: 11, fontWeight: 600, color: "var(--faint)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Exit sandbox</button>}
      <h1 style={{ margin: "4px 0", fontSize: 26, width: "100%" }}>Set up products &amp; margin</h1>
    </div>
  );
}

function StageBar({ stage, onJump, allowForwardJumps = false, navigationLocked = false }: { stage: Stage; onJump?: (s: Stage) => void; allowForwardJumps?: boolean; navigationLocked?: boolean }): React.ReactElement {
  const steps: Array<[Stage, string]> = [["upload", "Upload"], ["categorize", "Categorize"], ["costs", "Costs & margin"], ["review", "Review"]];
  const idx = steps.findIndex((s) => s[0] === stage);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "var(--body)", margin: "10px 0 16px", flexWrap: "wrap" }}>
      {steps.map(([s, label], i) => (
        <span key={s} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Clients can revisit completed stages, but cannot jump past unsaved
              work. Sandbox keeps free jumping; direct ?stage links still work
              for founder testing because this only limits the visible buttons. */}
          <button type="button" disabled={!onJump || navigationLocked || (!allowForwardJumps && i > idx)} onClick={() => onJump?.(s)} title={!navigationLocked && onJump && (allowForwardJumps || i <= idx) ? `Go to ${label}` : undefined}
            style={{ border: "none", cursor: !navigationLocked && onJump && (allowForwardJumps || i <= idx) ? "pointer" : "default", opacity: navigationLocked && i !== idx ? 0.55 : 1, font: "inherit", borderRadius: 6, ...(i === idx ? { background: "color-mix(in srgb, var(--accent) 15%, transparent)", color: "var(--accent)", padding: "3px 10px", fontWeight: 600 } : { background: "none", color: i < idx ? "var(--good)" : "var(--faint)", padding: 0 }) }}>
            {i < idx ? "✓ " : ""}{label}
          </button>
          {i < steps.length - 1 && <span style={{ color: "var(--faint)" }}>›</span>}
        </span>
      ))}
    </div>
  );
}

/* ----------------------------- paperwork ----------------------------- */
// "Paperwork first" (founder 2026-07-02): the FIRST step is getting whatever
// documents they already have — the more real paperwork we ingest, the fewer
// questions the costs chat has to ask. The old version of this step showed a
// file picker that silently discarded the file (nothing parsed it); now it's
// an honest prep checklist — the actual upload happens inside the costs chat,
// where the numbers land directly in their cost model.
function UploadStep({ onSkip, onContinueWithoutFailedFiles, onAttach, busy = false, error, onRetry }: { onSkip: () => void; onContinueWithoutFailedFiles: () => void; onAttach: (files: File[]) => void; busy?: boolean; error?: string | null; onRetry?: () => void }): React.ReactElement {
  const fileRef = useRef<HTMLInputElement>(null);
  const item = (label: string, why: string): React.ReactElement => (
    <li style={{ marginBottom: 6 }}><strong style={{ fontWeight: 600 }}>{label}</strong> <span style={{ color: "var(--body)" }}>— {why}</span></li>
  );
  return (
    <div style={{ ...card, display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
      <span style={asstAvatar}><SparkleIcon size={14} /></span>
      <div style={{ flex: 1, minWidth: 280 }}>
        <div style={{ fontSize: 14, lineHeight: 1.5, fontWeight: 600 }}>Before we start: grab any cost or shipping files you have handy. Real reports beat memory — whatever you can share, I'll pull the numbers out and only ask about what's left.</div>
        <ul style={{ fontSize: 13, lineHeight: 1.5, margin: "10px 0 0", paddingLeft: 18 }}>
          {item("Shipping / fulfillment report", "from your fulfillment center, last ~6 months — gives me real per-product shipping costs")}
          {item("Cost sheet or supplier invoices", "what each product costs you to make or buy")}
          {item("Processor statements", "just knowing each processor's rate (e.g. 2.9% + 30¢) is enough")}
        </ul>
        <div style={{ fontSize: 12.5, color: "var(--body)", marginTop: 10 }}>Spreadsheets (CSV/Excel), PDFs, and even photos of cost or shipping reports all work. Attach what you have now, or just start and we'll build it together by talking it through. You can also upload anytime inside the chat.</div>
        <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xls,.xlsx,.pdf,.png,.jpg,.jpeg,.webp,.gif,text/csv,application/pdf,image/*,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" multiple style={{ display: "none" }}
          disabled={busy}
          onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.currentTarget.value = ""; if (fs.length) onAttach(fs); }} />
        {error && (
          <div style={{ marginTop: 12, padding: "9px 11px", borderRadius: 8, color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--bad) 30%, var(--border))", fontSize: 12.5 }}>
            <div>{error}</div>
            <button style={{ ...ghostBtn, marginTop: 8, color: "var(--bad)" }} disabled={busy} onClick={onRetry}>{busy ? "Saving…" : "Try the failed files again"}</button>
            <button style={{ ...ghostBtn, marginTop: 8, marginLeft: 12 }} disabled={busy} onClick={onContinueWithoutFailedFiles}>Continue without these files</button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <button style={{ ...primaryBtn, opacity: busy || Boolean(error) ? 0.55 : 1 }} disabled={busy || Boolean(error)} onClick={onSkip}>Start now →</button>
          <button style={{ ...ghostBtn, opacity: busy ? 0.55 : 1 }} disabled={busy} onClick={() => fileRef.current?.click()}><PaperclipIcon />&nbsp;{busy ? "Saving files…" : "Attach files & start"}</button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- categorize --------------------------- */
const KIND_ORDER: Record<GroupKind, number> = { product: 0, bundle: 1, bonus: 2, misc: 3, wholesale: 4 };

/** A bundle's # Units/Sale is the sum of its parts' quantities (2+2+2+1 = 7) — it's
 *  derived, never typed. (The cost still rolls up per-part; this is just the count.) */
const bundleUnitCount = (s: SheetSku): number => s.components.reduce((n, c) => n + (c.quantity || 0), 0);
const bundlePartsComplete = (s: SheetSku): boolean => s.components.length > 0
  && s.components.every((component) => component.label.trim().length > 0 && Number.isFinite(component.quantity) && component.quantity > 0);

// What still blocks Continue, split by FIX: product SKUs need a # Units/Sale number
// (a fill can clear these); bundles need their PARTS added (a number can't). Bonuses
// are always 1, so they never block.
const countMissingProductUnits = (products: Product[]): number =>
  products.filter((p) => p.kind === "product").flatMap((p) => p.skus).filter((s) => s.boxesPerSale === null || !Number.isFinite(s.boxesPerSale) || s.boxesPerSale <= 0).length;
const countMissingBundleParts = (products: Product[]): number =>
  products.filter((p) => p.kind === "bundle").flatMap((p) => p.skus).filter((s) => !bundlePartsComplete(s)).length;
const countMissingUnits = (products: Product[]): number => countMissingProductUnits(products) + countMissingBundleParts(products);

/** Builds the "what's left before Costs" sentence, distinguishing fillable product
 *  blanks from bundles that need their parts (which a number-fill can't clear). */
const missingUnitsHint = (products: Product[]): string => {
  const p = countMissingProductUnits(products), b = countMissingBundleParts(products);
  const parts: string[] = [];
  if (p > 0) parts.push(`${p} product SKU${p === 1 ? "" : "s"} still need # Units/Sale (fill them above, or tell me “set the blanks to 1”)`);
  if (b > 0) parts.push(`${b} bundle${b === 1 ? "" : "s"} still need their parts added (the products inside — a number can’t fill those)`);
  return parts.join("; ");
};

type Preview = { ok: boolean; summary: string; count: number; sample: string[]; run: () => (() => void) | null };
interface Decision { id: string; message: string; detail?: string; chips: Array<{ label: string; value: string }>; apply: (value: string) => void | Promise<void>; ackText: (value: string) => string; }
interface ChatMsg { role: "user" | "assistant"; text: string }
interface CatalogInterviewProgress { started: boolean; resolvedDecisionIds: string[]; reviewReached: boolean }

const chatList = (items: string[]): string => items.map((item) => `- ${item}`).join("\n");
const readableList = (items: string[], inlineLimit = 2): string => (
  items.length <= inlineLimit ? items.join(", ") : `\n\n${chatList(items)}`
);

// Chat polish: fade-in messages + a blinking typing indicator (real chat feel).
const CHAT_STYLE = (
  <style>{`
@keyframes ocpFade{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
@keyframes ocpBlink{0%,80%,100%{opacity:.2}40%{opacity:1}}
@keyframes ocpSpin{to{transform:rotate(360deg)}}
@keyframes ocpBar{0%{left:-40%}100%{left:100%}}
.ocp-msg{animation:ocpFade .22s ease both}
.ocp-cta{transition:transform .15s ease, box-shadow .15s ease, filter .15s ease}
.ocp-cta:not(:disabled):hover{transform:translateY(-1px);box-shadow:0 10px 26px color-mix(in srgb, var(--accent) 42%, transparent);filter:brightness(1.05)}
.ocp-cta:not(:disabled):active{transform:translateY(0)}
.ocp-dot{display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--faint);margin:0 2px;animation:ocpBlink 1.3s infinite both}
.ocp-dot:nth-child(2){animation-delay:.18s}.ocp-dot:nth-child(3){animation-delay:.36s}
.ocp-spin{animation:ocpSpin .8s linear infinite}
.ocp-bar{position:absolute;top:0;height:100%;width:40%;border-radius:inherit;background:var(--accent);animation:ocpBar 1.25s ease-in-out infinite}
`}</style>
);

/** Animated loader for the ~20-30s first paint (we read every product name with AI). */
function CatalogLoading({ mode }: { mode: string }): React.ReactElement {
  const steps = ["Reading your catalog…", "Grouping products by family…", "Spotting bundles and reading what's inside…", "Putting your sheet together…"];
  const [i, setI] = useState(0);
  // Honest time expectations (QA round 8: a 251-product catalog took minutes
  // while the copy promised "a few seconds" — a first-time user assumes it's
  // broken). The sub-line levels with them as real time passes; the step line
  // keeps cycling so it never looks frozen.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % steps.length), 4500);
    const e = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => { clearInterval(t); clearInterval(e); };
  }, []);
  const sub =
    elapsed < 25
      ? "We read every product name with AI to group them right — small catalogs take seconds, big ones a couple of minutes."
      : elapsed < 75
        ? "Still working — yours is a bigger catalog, so the AI read takes a bit. This only ever happens once."
        : "Big catalog — this can run a couple of minutes total. Safe to grab a coffee; it finishes on its own and never repeats.";
  return (
    <div style={{ maxWidth: 1080 }}>
      {CHAT_STYLE}
      <Header mode={mode} />
      <div style={{ ...card, padding: "22px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span className="ocp-spin" style={{ width: 22, height: 22, border: "2.5px solid var(--hair)", borderTopColor: "var(--accent)", borderRadius: "50%", flexShrink: 0, display: "inline-block" }} aria-hidden="true" />
          <div style={{ minWidth: 0 }}>
            <div className="ocp-msg" key={i} style={{ fontSize: 14, color: "var(--body)", fontWeight: 600 }}>{steps[i]}</div>
            <div style={{ fontSize: 12, color: "var(--faint)", marginTop: 3 }}>{sub}</div>
          </div>
        </div>
        <div style={{ position: "relative", overflow: "hidden", height: 4, borderRadius: 999, background: "var(--hair)", marginTop: 16 }}><span className="ocp-bar" /></div>
      </div>
    </div>
  );
}
const TypingDots = (): React.ReactElement => <span aria-label="typing"><span className="ocp-dot" /><span className="ocp-dot" /><span className="ocp-dot" /></span>;
const SendIcon = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
);
// Inline SVGs (like SendIcon/chevron): the ti-* webfont classes render EMPTY —
// the Tabler font is never loaded by this app, so every <i className="ti ti-…">
// was an invisible glyph (the assistant avatar showed as a blank circle).
const SparkleIcon = ({ size = 13 }: { size?: number }): React.ReactElement => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3l1.9 5.7L19 10.5l-5.1 1.8L12 18l-1.9-5.7L5 10.5l5.1-1.8L12 3z" /><path d="M19 3l.7 2.1L22 5.8l-2.3.8L19 8.7l-.7-2.1-2.3-.8 2.3-.7L19 3z" opacity=".7" /></svg>
);
const PaperclipIcon = (): React.ReactElement => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 4, verticalAlign: -1 }}><path d="M21.4 11.05l-9.2 9.2a6 6 0 01-8.49-8.49l9.2-9.2a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.82-2.82l8.49-8.49" /></svg>
);
// The rest of the dead ti-* usages, as one tiny stroke-icon set (24×24 paths).
const STROKE_ICONS = {
  upload: "M12 17V5M7 10l5-5 5 5M4 20h16",
  undo: "M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 010 11H11",
  archive: "M4 5h16v4H4zM6 9v10h12V9M10 13h4",
  warehouse: "M3 21V9l9-6 9 6v12M9 21v-8h6v8M3 21h18",
  puzzle: "M10 4a2 2 0 114 0v1h3a2 2 0 012 2v3h1a2 2 0 110 4h-1v3a2 2 0 01-2 2h-3v-1a2 2 0 10-4 0v1H7a2 2 0 01-2-2v-3H4a2 2 0 110-4h1V7a2 2 0 012-2h3V4z",
  cornerDownRight: "M5 4v6a3 3 0 003 3h11M15 9l4 4-4 4",
  alert: "M12 3.5L21.5 20h-19L12 3.5zM12 10v4.5M12 17.5v.01",
  info: "M12 21a9 9 0 110-18 9 9 0 010 18zM12 11v6M12 7.5v.01",
  plus: "M12 5v14M5 12h14",
  pointer: "M5 3l14 8-6.5 1.5L9 19 5 3z",
  chevronRight: "M9 6l6 6-6 6",
} as const;
function Ico({ name, size = 13, style }: { name: keyof typeof STROKE_ICONS; size?: number; style?: React.CSSProperties }): React.ReactElement {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={style}><path d={STROKE_ICONS[name]} /></svg>;
}
const GripIcon = (): React.ReactElement => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="5" r="1.7" /><circle cx="9" cy="12" r="1.7" /><circle cx="9" cy="19" r="1.7" /><circle cx="15" cy="5" r="1.7" /><circle cx="15" cy="12" r="1.7" /><circle cx="15" cy="19" r="1.7" /></svg>
);

// App-wide "guided" chat design (founder picked this 2026-06-22): every
// multiple-choice option in chat is a full-width tappable row with a chevron, and
// the send control is a labeled button (not a bare icon — a non-technical operator
// shouldn't have to guess). The chevron + arrow are inline SVG so they never
// depend on the icon webfont (a missing ti-send glyph is what made the old send
// button look like a blank blue square). Used by BOTH Categorize and Costs.
function ChoiceRow({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }): React.ReactElement {
  const [hover, setHover] = useState(false);
  const active = hover && !disabled;
  return (
    <button type="button" onClick={onClick} disabled={disabled} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 10, border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`, background: active ? "var(--surface-2)" : "var(--surface)", color: "var(--text)", fontSize: 14, fontWeight: 500, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1, transition: "background .12s, border-color .12s" }}>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: "var(--faint)", flexShrink: 0 }}><path d="M9 6l6 6-6 6" /></svg>
    </button>
  );
}
function ChoiceList({ children, indent = 0 }: { children: React.ReactNode; indent?: number }): React.ReactElement {
  // Cap the width so rows read as tidy buttons, not full-bleed bars on a wide
  // chat panel (founder 2026-06-22: "the bars are way too long").
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, marginTop: 8, marginBottom: 6, marginLeft: indent }}>
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", maxWidth: 440 }}>{children}</div>
  </div>;
}
function SendButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }): React.ReactElement {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label="Send"
      style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 40, flexShrink: 0, padding: "0 16px", background: "var(--accent)", color: "var(--accent-contrast)", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>
      Send<SendIcon />
    </button>
  );
}

const ChatBubbleIcon = ({ size = 15 }: { size?: number }): React.ReactElement => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 1 1 16.1-3.8z" /></svg>
);

/**
 * ChatDock — the founder-specced chat behavior (2026-07-03, third attempt, his
 * exact words): at the top of the page the chat shows NORMALLY in the flow.
 * Scroll past it → it becomes a small floating "Show chat box" button that
 * follows you down. Click → the chat pops open as a floating panel right where
 * you are, RESIZABLE by dragging its handle. Scroll back to the top → the
 * normal inline box again. No sticky, no "Hide" bar.
 *
 * Mechanics: the in-flow wrapper keeps its measured height while the card
 * floats/hides (no layout jump, and it stays observable); an
 * IntersectionObserver on the wrapper flips the mode. Chat content renders via
 * children(chatH) so the SAME conversation (state lives in the parent chat
 * component) appears inline or in the popup.
 */
function ChatDock({ heightKey, defaultH, floatBottom = 16, children }: { heightKey: string; defaultH: number; floatBottom?: number; children: (chatH: number) => React.ReactNode }): React.ReactElement {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<"inline" | "button" | "popup">("inline");
  const [cardH, setCardH] = useState<number>(0);
  const [chatH, setChatH] = useState<number>(() => {
    const saved = Number(window.localStorage.getItem(heightKey));
    return Number.isFinite(saved) && saved >= 140 ? Math.min(saved, 620) : defaultH;
  });
  useEffect(() => { try { window.localStorage.setItem(heightKey, String(Math.round(chatH))); } catch { /* non-fatal */ } }, [heightKey, chatH]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = (): void => { const h = el.getBoundingClientRect().height; if (h > 40) setCardH(h); };
    measure();
    // "Scrolled past" = the chat's spot is fully above the visible area (under
    // the sticky window bar). Below-the-viewport never counts — the chat lives
    // at the top of the page.
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      const past = !e.isIntersecting && e.boundingClientRect.bottom < 90;
      setMode((m) => (past ? (m === "inline" ? "button" : m) : "inline"));
    }, { rootMargin: "-80px 0px 0px 0px", threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Re-measure while inline so the placeholder height tracks content growth.
  useEffect(() => {
    if (mode !== "inline") return;
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { const h = el.getBoundingClientRect().height; if (h > 40) setCardH(h); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mode]);

  // Un-hiding can reset a scroll container — re-pin the thread to the latest
  // message whenever the chat re-appears (popup opened, or back inline).
  useEffect(() => {
    if (mode === "button") return;
    const t = wrapRef.current?.querySelector("[data-chat-thread]");
    if (t) t.scrollTop = t.scrollHeight;
  }, [mode]);

  // sign +1: handle below the thread (inline) — drag down grows. sign -1:
  // handle above the thread (popup, anchored to the bottom of the screen) —
  // drag up grows.
  const startResize = (e: React.MouseEvent, sign: 1 | -1): void => {
    e.preventDefault();
    const startY = e.clientY, startH = chatH;
    const move = (ev: MouseEvent): void => setChatH(Math.min(620, Math.max(140, startH + sign * (ev.clientY - startY))));
    const up = (): void => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  const grip = <span style={{ width: 44, height: 4, borderRadius: 999, background: "var(--border)" }} />;

  const floating = mode !== "inline";
  const cardStyle: React.CSSProperties = floating
    ? mode === "popup"
      ? { ...card, position: "fixed", right: 16, bottom: floatBottom, width: "min(600px, calc(100vw - 32px))", zIndex: 40, boxShadow: "0 12px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" }
      : { display: "none" }
    : { ...card };

  return (
    <div ref={wrapRef} style={{ marginBottom: 14, height: floating ? (cardH || undefined) : undefined }}>
      {mode === "button" && (
        <button type="button" onClick={() => setMode("popup")}
          style={{ position: "fixed", right: 16, bottom: floatBottom, zIndex: 40, display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 999, border: "none", background: "var(--accent)", color: "var(--accent-contrast)", fontSize: 13.5, fontWeight: 600, cursor: "pointer", boxShadow: "0 6px 20px rgba(0,0,0,0.22)" }}>
          <ChatBubbleIcon />Show chat box
        </button>
      )}
      <div style={cardStyle}>
        {mode === "popup" && (
          // Popup header — a real chat-app header bar (founder 2026-07-05: "make
          // it slightly more noticeable how to hide it, the arrow is tiny"):
          // labeled Hide button like Intercom/Drift, whole bar drags to resize.
          <div onMouseDown={(e) => startResize(e, -1)} title="Drag to resize"
            style={{ display: "flex", alignItems: "center", gap: 8, margin: "-6px -6px 4px", padding: "6px 8px 6px 12px", cursor: "ns-resize", borderBottom: "1px solid var(--border)", background: "var(--surface-2)", borderRadius: "10px 10px 0 0" }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--body)", display: "inline-flex", alignItems: "center", gap: 7 }}>
              <ChatBubbleIcon />Chat
            </span>
            <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>{grip}</div>
            <button type="button" onClick={() => setMode("button")} onMouseDown={(e) => e.stopPropagation()} aria-label="Hide the chat"
              style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "1px solid var(--border)", background: "var(--surface)", cursor: "pointer", color: "var(--body)", padding: "4px 11px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
              Hide<Ico name="chevronRight" size={13} style={{ transform: "rotate(90deg)" }} />
            </button>
          </div>
        )}
        {children(chatH)}
        {mode === "inline" && (
          <div onMouseDown={(e) => startResize(e, 1)} title="Drag to resize the chat" style={{ height: 14, margin: "4px 0 -10px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "ns-resize" }}>{grip}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Resizable, fit-to-width table columns (founder 2026-07-03, his spec verbatim:
 * "fit to width on the product column, then have the rest be equal, with the
 * ability to adjust"). Defaults: the name column is measured to fit the longest
 * real product label (capped at 45% of the table); the numeric columns split
 * the remaining width EQUALLY, filling to the right edge. Every column boundary
 * is a drag handle; double-click a handle to go back to auto-fit. Widths are
 * plain pixels shared by the header grid and every row grid, so they can never
 * drift apart. Custom widths persist per table.
 */
function useColWidths(storageKey: string, containerRef: React.RefObject<HTMLDivElement | null>, labels: string[], numericCount: number, fallback: string, opts?: { minNumeric?: number; nameCapFrac?: number; nameMaxPx?: number; gap?: number; narrowCols?: number[] }): { template: string; dividers: React.CSSProperties; startDrag: (i: number, e: React.MouseEvent) => void; reset: () => void } {
  const gap = opts?.gap ?? 8;
  const [widths, setWidths] = useState<number[] | null>(() => {
    try {
      const raw = JSON.parse(window.localStorage.getItem(storageKey) ?? "null") as unknown;
      return Array.isArray(raw) && raw.length === numericCount + 1 && raw.every((x) => typeof x === "number" && x >= 40) ? (raw as number[]) : null;
    } catch { return null; }
  });
  const customized = useRef<boolean>(widths !== null);
  const [epoch, setEpoch] = useState(0);

  // Re-key after mount: the column COUNT can change once data arrives (the
  // review table only shows an Other column when some product has extras).
  // Without this, a stale widths array would style the wrong number of
  // columns. Runs before the auto-fit effect so customized is settled first.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    let saved: number[] | null = null;
    try {
      const raw = JSON.parse(window.localStorage.getItem(storageKey) ?? "null") as unknown;
      saved = Array.isArray(raw) && raw.length === numericCount + 1 && raw.every((x) => typeof x === "number" && x >= 40) ? (raw as number[]) : null;
    } catch { saved = null; }
    customized.current = saved !== null;
    setWidths(saved);
    setEpoch((n) => n + 1);
  }, [storageKey, numericCount]);
  const labelsKey = labels.join("");

  useEffect(() => {
    if (customized.current) return;
    const el = containerRef.current;
    if (!el) return;
    const compute = (): void => {
      const cw = el.clientWidth;
      if (cw < 160) return; // was 240 — narrow phones still deserve a working table
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let longest = 120;
      if (ctx) {
        ctx.font = `12px ${getComputedStyle(el).fontFamily}`;
        for (const l of labels) longest = Math.max(longest, ctx.measureText(l).width);
      }
      const nameCap = Math.max(140, Math.min(cw * (opts?.nameCapFrac ?? 0.45), opts?.nameMaxPx ?? Number.POSITIVE_INFINITY));
      const nameW = Math.round(Math.min(Math.max(longest + 26, 140), nameCap));
      // Narrow columns (tiny values like Units) take ~55% of a regular column's
      // share, so wide-value columns get the room (founder 2026-07-05: "units
      // can def be shrunk").
      const narrow = new Set(opts?.narrowCols ?? []);
      const weights = Array.from({ length: numericCount }, (_, i) => (narrow.has(i + 1) ? 0.55 : 1));
      const wSum = weights.reduce((a, b) => a + b, 0);
      const unit = Math.max(1, (cw - nameW - gap * numericCount) / wSum);
      const numWs = weights.map((w) => Math.max(w < 1 ? 44 : (opts?.minNumeric ?? 56), Math.floor(unit * w)));
      setWidths([nameW, ...numWs]);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [labelsKey, numericCount, epoch]);

  const startDrag = (i: number, e: React.MouseEvent): void => {
    if (!widths) return;
    e.preventDefault();
    const startX = e.clientX, startW = widths[i]!;
    const move = (ev: MouseEvent): void => {
      setWidths((w) => {
        if (!w) return w;
        const next = [...w];
        next[i] = Math.min(680, Math.max(i === 0 ? 110 : 48, startW + (ev.clientX - startX)));
        return next;
      });
    };
    const up = (): void => {
      customized.current = true;
      setWidths((w) => { if (w) { try { window.localStorage.setItem(storageKey, JSON.stringify(w)); } catch { /* non-fatal */ } } return w; });
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const reset = (): void => {
    try { window.localStorage.removeItem(storageKey); } catch { /* non-fatal */ }
    customized.current = false;
    setWidths(null);
    setEpoch((n) => n + 1);
  };

  // Visible column divider lines drawn at every resize boundary (founder
  // 2026-07-03: "so they can see that they can resize them"): 1px stripes
  // painted mid-gap via layered background gradients, on header AND rows.
  let dividers: React.CSSProperties = {};
  if (widths) {
    const xs: number[] = [];
    let acc = 0;
    for (let i = 0; i < widths.length - 1; i++) { acc += widths[i]!; xs.push(acc + i * gap + gap / 2); }
    dividers = {
      backgroundImage: xs.map(() => "linear-gradient(var(--hair), var(--hair))").join(", "),
      backgroundPosition: xs.map((x) => `${x}px 0`).join(", "),
      backgroundSize: "1px 100%",
      backgroundRepeat: "no-repeat",
    };
  }

  return { template: widths ? widths.map((w) => `${w}px`).join(" ") : fallback, dividers, startDrag, reset };
}

/** One header cell with a draggable right-edge divider (double-click = auto-fit). */
function ColHead({ label, title, alignRight, i, startDrag, reset, last }: { label: string; title?: string; alignRight?: boolean; i: number; startDrag: (i: number, e: React.MouseEvent) => void; reset: () => void; last?: boolean }): React.ReactElement {
  return (
    <span title={title} style={{ position: "relative", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: alignRight ? "right" : undefined, paddingRight: last ? 0 : 4 }}>
      {label}
      {!last && (
        <span onMouseDown={(e) => startDrag(i, e)} onDoubleClick={reset} title="Drag to resize — double-click to auto-fit"
          style={{ position: "absolute", right: -7, top: -6, bottom: -6, width: 12, cursor: "col-resize", display: "flex", justifyContent: "center", zIndex: 2 }}>
          <span style={{ width: 1, height: "100%", background: "var(--border)" }} />
        </span>
      )}
    </span>
  );
}

function Categorize({ model, hiddenCount = 0, mode, accountId, currency = "USD", startAtReview = false, fresh = false, onMove, onRetire, onRename, onEdit, onEditLocal, onSplit, onSetKind, onSetType, onPreview, onSaveComponents, onConfirm, onConfirmGroup, savePending, saveError, onRetrySave, onWaitForSaves, onContinue }: {
  model: Model;
  hiddenCount?: number;
  mode: string;
  accountId: string;
  currency?: string;
  /** Deep-linked from the hub/box "Organize" → jump straight to the review sheet. */
  startAtReview?: boolean;
  fresh?: boolean;
  onMove: (skuId: string, toGroupId: string) => void;
  onRetire: (skuIds: string[], retired: boolean) => void;
  onRename: (id: string, name: string) => void;
  onEdit: (skuId: string, patch: { packSize?: number | null; boxesPerSale?: number | null; offerLabel?: string | null; manualPrice?: number | null }) => void;
  onEditLocal: (skuId: string, patch: { packSize?: number | null; boxesPerSale?: number | null; offerLabel?: string | null; manualPrice?: number | null }) => void;
  onSplit: (id: string) => void | Promise<void>;
  onSetKind: (id: string, kind: GroupKind) => void;
  onSetType: (id: string, type: ProductType) => void;
  onPreview: (action: CatalogAction) => Preview;
  onSaveComponents: (bundleSkuId: string, components: SkuComponent[]) => void;
  onConfirm: (skuId: string) => void;
  onConfirmGroup: (groupId: string) => void;
  savePending: boolean;
  saveError: string | null;
  onRetrySave: () => void;
  onWaitForSaves: () => Promise<boolean>;
  onContinue: () => void;
}): React.ReactElement {
  // The sheet stays hidden through the welcome + questions (less busy, more
  // intuitive) and appears once the assistant is done asking — that's when the
  // client reviews everything and the "Looks good" button lives at its bottom.
  const [reviewReady, setReviewReady] = useState(false);
  return (
    <div>
      <CategorizeChat model={model} mode={mode} accountId={accountId} startAtReview={startAtReview} fresh={fresh} onSplit={onSplit} onSetKind={onSetKind} onSetType={onSetType} onPreview={onPreview} onWaitForSaves={onWaitForSaves} onReviewReady={setReviewReady} />
      {reviewReady && <Sheet model={model} currency={currency} startAtReview={startAtReview} onMove={onMove} onRetire={onRetire} onRename={onRename} onSetType={onSetType} onEdit={onEdit} onEditLocal={onEditLocal} onSaveComponents={onSaveComponents} onConfirm={onConfirm} onConfirmGroup={onConfirmGroup} savePending={savePending} saveError={saveError} onRetrySave={onRetrySave} onContinue={() => { void onWaitForSaves().then((saved) => { if (saved) onContinue(); }); }} />}
      {reviewReady && hiddenCount > 0 && (
        <div style={{ fontSize: 11.5, color: "var(--faint)", marginTop: 8 }}>
          {hiddenCount} SKU{hiddenCount === 1 ? " that has" : "s that have"} never sold {hiddenCount === 1 ? "is" : "are"} tucked away to keep this clean — the moment one sells, your dashboard flags it to categorize and price.
        </div>
      )}
    </div>
  );
}

/**
 * The conversation. Welcome gate → guided steps (chips, one question at a time) +
 * a single smart box: type anything and the server classifies it as an answer, a
 * command (move/rename/merge/split/exclude…), or a question. Edits PREVIEW first
 * and only apply on confirm, with Undo — so a misread never changes the catalog.
 */
function CategorizeChat({ model, mode, accountId, startAtReview = false, fresh = false, onSplit, onSetKind, onSetType, onPreview, onWaitForSaves, onReviewReady }: {
  model: Model; mode: string; accountId: string; startAtReview?: boolean; fresh?: boolean; onSplit: (id: string) => void | Promise<void>; onSetKind: (id: string, kind: GroupKind) => void; onSetType: (id: string, type: ProductType) => void; onPreview: (action: CatalogAction) => Preview; onWaitForSaves: () => Promise<boolean>; onReviewReady: (ready: boolean) => void;
}): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [boot, setBoot] = useState(0); // brief "reading your catalog" sequence before the welcome
  const [started, setStarted] = useState(false);
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [resolved, setResolved] = useState<Set<string>>(new Set());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [assistantTyping, setAssistantTyping] = useState(false);
  const [resolvingDecision, setResolvingDecision] = useState<string | null>(null);
  const [pending, setPending] = useState<Preview | null>(null);
  const [undoFn, setUndoFn] = useState<{ run: () => void } | null>(null);
  const [interviewHydrating, setInterviewHydrating] = useState(mode === "real");
  const [interviewSaveError, setInterviewSaveError] = useState<string | null>(null);
  const [reviewProgressSaved, setReviewProgressSaved] = useState(mode !== "real");
  const [reviewProgressSaving, setReviewProgressSaving] = useState(false);
  const thread = useRef<HTMLDivElement>(null);
  const applyingRef = useRef(false);
  const decisionBusyRef = useRef(false);
  const askedDecisionIds = useRef<Set<string>>(new Set());
  const interviewQueryKey = ["onboarding", "journey", "catalog-interview", accountId] as const;
  const interviewQ = useQuery({
    queryKey: interviewQueryKey,
    enabled: mode === "real" && Boolean(accountId),
    queryFn: () => api.get<CatalogInterviewQueryData>("/onboarding/journey"),
  });
  const interviewReady = mode !== "real" || (interviewQ.isSuccess && !interviewHydrating);
  const saveInterview = async (progress: CatalogInterviewProgress): Promise<boolean> => {
    if (mode !== "real") return true;
    try {
      await api.patch("/onboarding/journey", { catalogInterview: progress });
      queryClient.setQueryData<CatalogInterviewQueryData>(
        interviewQueryKey,
        (current) => withSavedCatalogInterviewProgress(current, progress),
      );
      // Guided onboarding reads the same server record through its canonical
      // journey query. Keep that mounted/remounted consumer current too, while
      // leaving an uncached query undefined so its first load still fetches the
      // complete response instead of receiving a partial placeholder.
      queryClient.setQueryData<CatalogInterviewQueryData>(
        ["onboarding", "journey"],
        (current) => current ? withSavedCatalogInterviewProgress(current, progress) : current,
      );
      return true;
    } catch {
      return false;
    }
  };

  const products = model.products.filter((p) => p.kind === "product");
  const untypedProductIds = untypedRegularProductIds(products);
  const bundles = model.products.filter((p) => p.kind === "bundle");
  const bonuses = model.products.filter((p) => p.kind === "bonus");
  const miscGroups = model.products.filter((p) => p.kind === "misc");
  const wholesaleGroups = model.products.filter((p) => p.kind === "wholesale");

  const decisions = useMemo<Decision[]>(() => {
    const out: Decision[] = [];
    for (const p of products) {
      const vs = variantsOf(p);
      const id = `variant:${p.id}`;
      if (vs.length >= 2 && !resolved.has(id)) out.push({
        id,
        message: `“${p.name}” comes in ${vs.length} variants:${readableList(vs)}\n\nKeep them together as variants under one ${p.name}, or split into separate products?`,
        chips: [{ label: "Keep as one", value: "keep" }, { label: "Split into separate", value: "split" }],
        apply: (v) => v === "split" ? onSplit(p.id) : undefined,
        ackText: (v) => (v === "split" ? `Done — split ${p.name} into separate products.` : `Got it — keeping ${p.name} as one product with its variants.`),
      });
    }
    if (bundles.length && !resolved.has("bundles")) out.push({
      id: "bundles",
      message: `I flagged ${bundles.length === 1 ? "this as a bundle" : "these as bundles"} - kits that mix different products:${readableList(bundles.map((b) => b.name), 1)}\n\nTreat ${bundles.length === 1 ? "it" : "them"} as ${bundles.length === 1 ? "a bundle" : "bundles"}?`,
      chips: [{ label: "Yes, bundles", value: "yes" }, { label: "Not bundles", value: "no" }],
      apply: (v) => { if (v === "no") bundles.forEach((b) => onSetKind(b.id, "product")); },
      ackText: (v) => (v === "no" ? "Got it — treating those as regular products." : "Great — keeping those as bundles."),
    });
    const miscSkus = miscGroups.flatMap((g) => g.skus);
    if (miscSkus.length && !resolved.has("misc")) out.push({
      id: "misc",
      message: `I put ${miscSkus.length === 1 ? "this add-on" : `these ${miscSkus.length} add-ons`} in Misc:${readableList(miscSkus.map((s) => `${s.label} — SKU ${s.skuCode}`), 1)}\n\nThese are paid extras, such as warranties or order bumps. They count toward your profit, but they are not products. Right call, or ${miscSkus.length === 1 ? "is it" : "are they"} actually a product?`,
      chips: [{ label: "Yes, Misc", value: "yes" }, { label: "It's a product", value: "product" }],
      apply: (v) => { if (v === "product") miscGroups.forEach((g) => onSetKind(g.id, "product")); },
      ackText: (v) => (v === "product" ? "Got it — moved out of Misc into your products." : "Great — keeping those in Misc."),
    });
    const wholesaleSkus = wholesaleGroups.flatMap((g) => g.skus);
    if (wholesaleSkus.length && !resolved.has("wholesale")) out.push({
      id: "wholesale",
      message: `I found ${wholesaleSkus.length === 1 ? "a wholesale SKU" : `${wholesaleSkus.length} wholesale SKUs`} — the SKU code has "wholesale" in it:${readableList(wholesaleSkus.map((s) => `${s.label} — SKU ${s.skuCode}`), 1)}\n\nThat code tag usually means a separate B2B channel, so I'm keeping ${wholesaleSkus.length === 1 ? "it" : "them"} OUT of your DTC numbers by default. Keep as wholesale, or ${wholesaleSkus.length === 1 ? "is it" : "are they"} a regular product?`,
      chips: [{ label: "Keep as wholesale", value: "yes" }, { label: "Regular product", value: "product" }],
      apply: (v) => { if (v === "product") wholesaleGroups.forEach((g) => onSetKind(g.id, "product")); },
      ackText: (v) => (v === "product" ? "Done — moved into your regular products." : "Got it — kept as wholesale, out of your numbers."),
    });
    // Product TYPE — physical | software | info | service. Tagged here so the Costs
    // step asks the right cost questions per type (and batches by type) instead of
    // re-asking. One batched question for the common all-physical catalog (one tap);
    // a per-product follow-up only when they say some are digital/a service.
    const untypedProducts = products.filter((p) => p.type === null || p.type === undefined);
    if (untypedProducts.length) {
      if (!resolved.has("type:scope")) {
        const looksMixed = untypedProducts.some((p) => guessType(p.name) !== "physical");
        out.push({
          id: "type:scope",
          message: `One quick thing before we get to costs. ${looksMixed ? "It looks like you have a mix of physical products, digital products, and services." : "These all look like physical products that you ship."}\n\nIs that right?`,
          chips: looksMixed
            ? [{ label: "Yes, it's a mix", value: "some" }, { label: "No, they're all physical", value: "all" }]
            : [{ label: "Yes, they're all physical", value: "all" }, { label: "No, it's a mix", value: "some" }],
          apply: (v) => { if (v === "all") untypedProducts.forEach((p) => onSetType(p.id, "physical")); },
          ackText: (v) => (v === "all" ? "Perfect — I'll ask the usual make / ship / pack costs for each." : "No problem — tell me which is which and I'll ask the right costs for each one."),
        });
      } else {
        // They said some aren't physical → tag each one, ordered easiest (most-likely
        // physical) first so it reads like "the physical ones, then the rest." A
        // "rest are all physical" shortcut means a mostly-physical catalog with a few
        // digital/service items isn't 40 taps — mark the odd ones, bulk-finish the rest.
        const shortType = (v: string): string => (PRODUCT_TYPES.find((t) => t.type === v)?.label ?? String(v)).replace(/\s*\(.*\)$/, "").toLowerCase();
        const ordered = [...untypedProducts].sort((a, b) => TYPE_ORDER[guessType(a.name)] - TYPE_ORDER[guessType(b.name)] || a.name.localeCompare(b.name));
        for (const p of ordered) {
          const id = `type:${p.id}`;
          if (resolved.has(id)) continue;
          const guess = guessType(p.name);
          const sorted = [...PRODUCT_TYPES].sort((a, b) => (a.type === guess ? -1 : 0) - (b.type === guess ? -1 : 0));
          const typeChips = sorted.map((t) => ({ label: t.label, value: t.type as string }));
          // Only offer the bulk shortcut while more than one product is still untagged.
          if (untypedProducts.length > 1) typeChips.push({ label: "The rest are all physical", value: "__rest_physical__" });
          out.push({
            id,
            message: `What kind of thing is “${p.name}”?${guess !== "physical" ? ` (Looks like ${guess} to me — pick another if not.)` : ""}`,
            chips: typeChips,
            // The shortcut tags this product + every remaining PHYSICAL-looking one.
            // Items that look digital/service still get their own question — a bulk
            // "all physical" must never silently mis-tag the ebook.
            apply: (v) => { if (v === "__rest_physical__") untypedProducts.filter((q) => q.id === p.id || guessType(q.name) === "physical").forEach((q) => onSetType(q.id, "physical")); else onSetType(p.id, v as ProductType); },
            ackText: (v) => (v === "__rest_physical__"
              ? (untypedProducts.some((q) => q.id !== p.id && guessType(q.name) !== "physical")
                ? "Got it — marking those as physical. A couple look digital to me though, so let me double-check just those:"
                : "Got it — the rest are physical. I'll ask the usual make / ship / pack costs for those.")
              : `Got it — ${p.name} is ${shortType(v)}.`),
          });
        }
      }
    }
    if ((model.aside.length || bonuses.length) && !resolved.has("aside")) {
      const parts: string[] = [];
      if (bonuses.length) parts.push("free bonuses (ebooks, recipes)");
      if (model.aside.length) parts.push(`${model.aside.length} set aside (coupons, shipping)`);
      out.push({
        id: "aside",
        message: `Last thing — I kept ${parts.join(" and ")} out of your products. Look right? (You can always drag something back in the sheet, or tell me.)`,
        chips: [{ label: "Looks right", value: "ok" }, { label: "I'll fix some", value: "fix" }],
        apply: () => undefined,
        ackText: () => "👍 You're all organized.",
      });
    }
    return out;
  }, [model, resolved, products, bundles, bonuses, miscGroups, wholesaleGroups]);

  const current = decisions[0] ?? null;
  // The type questions ("type:scope" + per-product "type:<id>") are an interstitial:
  // answering "some" expands one question into many, which would make a shared
  // "Question N of M" counter jump around. So they're excluded from the count and
  // shown without a number; the structural questions keep a stable count.
  const isTypeDecision = (id: string): boolean => id.startsWith("type:");
  const total = decisions.filter((d) => !isTypeDecision(d.id)).length + [...resolved].filter((id) => !isTypeDecision(id)).length;
  const stepNum = [...resolved].filter((id) => !isTypeDecision(id)).length + 1;

  const say = (role: "user" | "assistant", text: string): void => setMsgs((m) => [...m, { role, text }]);
  const currentQuestionText = (d: Decision): string => `${!isTypeDecision(d.id) && total > 1 ? `Question ${stepNum} of ${total}\n` : ""}${d.message}`;
  const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
  const waitLikeChat = (): Promise<void> => wait(1400 + Math.round(Math.random() * 450));
  useEffect(() => { requestAnimationFrame(() => { thread.current?.scrollTo({ top: thread.current.scrollHeight, behavior: "smooth" }); }); }, [msgs, current, pending, started, sending, assistantTyping]);

  useEffect(() => {
    if (!interviewReady || !started || !current || everReady.current || resolvingDecision === current.id || askedDecisionIds.current.has(current.id)) return;
    askedDecisionIds.current.add(current.id);
    say("assistant", currentQuestionText(current));
  }, [started, current, resolvingDecision, stepNum, total]);

  // A short "reading your catalog" sequence so it feels like it's working, then the welcome.
  useEffect(() => {
    if (boot >= 2) return;
    const t = setTimeout(() => setBoot((b) => b + 1), boot === 0 ? 850 : 1100);
    return () => clearTimeout(t);
  }, [boot]);
  const totalSkus = model.products.reduce((n, p) => n + p.skus.length, 0) + model.aside.length;

  // Once we're done asking, reveal the sheet, post the review instructions, and
  // flag any SKUs missing data (don't let it pass silently).
  // Review-readiness is STICKY: a post-review edit that creates a first-of-its-
  // kind condition (first variant pair, first bundle) must never yank the sheet
  // away and re-open the interview — the sheet is the tool at that point.
  const everReady = useRef(false);
  if (started && current === null) everReady.current = true;
  const reviewReady = started && (current === null || everReady.current);
  // The one thing the merchant sets is # Units/Sale (drives cost). Bundles get it
  // from their parts, bonuses are 1, price comes from orders — none of those block.
  const missingUnits = countMissingUnits(model.products);
  const wrapped = useRef(false);
  const interviewHydrated = useRef(false);
  useEffect(() => {
    if (interviewHydrated.current || mode !== "real" || !interviewQ.isSuccess) return;
    interviewHydrated.current = true;
    const saved = interviewQ.data.journey.catalogInterview ?? { started: false, resolvedDecisionIds: [], reviewReached: false };
    const progress = fresh ? { started: false, resolvedDecisionIds: [], reviewReached: false } : saved;
    void (async () => {
      if (fresh && !await saveInterview(progress)) {
        setInterviewSaveError("I couldn't reset this walkthrough yet. Nothing was changed.");
        setInterviewHydrating(false);
        return;
      }
      setInterviewSaveError(null);
      if ((startAtReview || progress.reviewReached) && untypedProductIds.length === 0) {
        everReady.current = true;
        wrapped.current = true;
        // A normal resume already has this saved. A deep link to Review still
        // has to save the milestone before the sheet is shown.
        setReviewProgressSaved(progress.reviewReached);
        setBoot(2);
        setStarted(true);
        setResolved(new Set(decisions.map((decision) => decision.id)));
        setMsgs([{ role: "assistant", text: "Here's your organized catalog. Anything that needs a quick look is highlighted below. Adjust anything in the sheet, then continue to Costs whenever you're ready." }]);
        setInterviewHydrating(false);
        return;
      }
      setStarted(startAtReview || progress.reviewReached || progress.started);
      setResolved(new Set(progress.resolvedDecisionIds));
      if (progress.started && progress.resolvedDecisionIds.length > 0) {
        setMsgs([{ role: "assistant", text: "Picking up where you left off. Your saved choices are still here." }]);
      }
      setInterviewHydrating(false);
    })();
  }, [interviewQ.isSuccess]);

  useEffect(() => {
    const savedReviewReady = reviewReady && (mode !== "real" || reviewProgressSaved);
    onReviewReady(savedReviewReady);
    if (savedReviewReady) {
      if (mode === "sandbox") {
        try { window.sessionStorage.setItem(sandboxCategoryReviewKey(accountId), "1"); } catch { /* non-fatal */ }
      }
    }
    if (savedReviewReady && !wrapped.current) {
      wrapped.current = true;
      let msg = `Good — the categorization's done. Take a look at the full sheet below. The one thing to set is # Units/Sale — how many units ship when that SKU sells (it's what drives the cost). Price comes straight from your orders, so there's nothing to enter there.`;
      if (missingUnits > 0) msg += `\n\n${missingUnitsHint(model.products)} before moving on. Costs unlocks once they're all set.`;
      else msg += `\n\nEverything's set — scroll down for a final look, then continue to Costs whenever you're ready.`;
      say("assistant", msg);
    }
  }, [reviewProgressSaved, reviewReady]);

  const saveReviewProgress = async (): Promise<void> => {
    if (mode !== "real" || reviewProgressSaved || reviewProgressSaving) return;
    setInterviewSaveError(null);
    setReviewProgressSaving(true);
    const saved = await saveInterview({ started: true, resolvedDecisionIds: [...resolved], reviewReached: true });
    setReviewProgressSaving(false);
    if (saved) {
      setInterviewSaveError(null);
      setReviewProgressSaved(true);
    } else {
      setInterviewSaveError("I couldn't save your place before the review sheet. Try again so a refresh never sends you backward.");
    }
  };
  useEffect(() => {
    if (reviewReady && mode === "real" && !reviewProgressSaved && !reviewProgressSaving && !interviewSaveError) void saveReviewProgress();
  }, [interviewSaveError, reviewProgressSaved, reviewProgressSaving, reviewReady]);

  // Sandbox never writes account progress, so this tab's review marker is its
  // resume source. Real accounts use the server-saved interview above.
  const fastForwarded = useRef(false);
  useEffect(() => {
    if (fastForwarded.current || mode !== "sandbox") return;
    let reviewedBefore = false;
    try {
      reviewedBefore = window.sessionStorage.getItem(sandboxCategoryReviewKey(accountId)) === "1";
    } catch { /* ignore */ }
    if (!reviewedBefore && !startAtReview) return;
    if (untypedProductIds.length > 0) {
      setBoot(2);
      setStarted(true);
      return;
    }
    fastForwarded.current = true;
    everReady.current = true;
    wrapped.current = true;
    setBoot(2);
    setStarted(true);
    setResolved(new Set(decisions.map((d) => d.id)));
    setMsgs([{ role: "assistant", text: "Here's your organized catalog. Anything that needs a quick look is highlighted below. Adjust anything in the sheet, then continue to Costs whenever you're ready." }]);
  }, []);

  const resolveCurrent = async (value: string, lockHeld = false): Promise<void> => {
    if (!current || (!lockHeld && decisionBusyRef.current)) return;
    if (!lockHeld) decisionBusyRef.current = true;
    setPending(null); // a guided action changes the model — drop any stale command preview
    setUndoFn(null);
    const activeId = current.id;
    setResolvingDecision(activeId);
    setAssistantTyping(true);
    try {
      await waitLikeChat();
      setAssistantTyping(false);
      await current.apply(value);
      const saved = await onWaitForSaves();
      if (!saved) {
        say("assistant", "That choice did not save. I put your catalog back to its last saved version. Try that choice again.");
        return;
      }
      const nextResolved = new Set(resolved).add(activeId);
      const progress = { started: true, resolvedDecisionIds: [...nextResolved], reviewReached: false };
      const progressSaved = await saveInterview(progress) || await saveInterview(progress);
      if (!progressSaved) {
        say("assistant", "That catalog change saved, but I couldn't save your place in the walkthrough. Try the choice once more before leaving this page.");
        return;
      }
      // Only acknowledge the choice after both the catalog and the resume
      // checkpoint are durable. The earlier flow said "got it" before a failed
      // save, which made the repeated question look like the chat forgot.
      say("assistant", current.ackText(value));
      setResolved(nextResolved);
    } catch {
      say("assistant", "That choice did not finish. I left you on the same question so you can try again.");
    } finally {
      if (!lockHeld) decisionBusyRef.current = false;
      setAssistantTyping(false);
      setResolvingDecision(null);
    }
  };
  const tapChip = (value: string, label: string): void => { if (sending || assistantTyping || resolvingDecision || decisionBusyRef.current) return; say("user", label); void resolveCurrent(value); };

  const matchChip = (answer: string | undefined): string | null => {
    if (!current || !answer) return null;
    const a = answer.toLowerCase();
    // The bulk "the rest are all physical" shortcut must win before the generic
    // "physical" substring test, which would otherwise tag only the current product.
    if (current.chips.some((c) => c.value === "__rest_physical__") && /\b(rest|all|others?|remaining)\b/.test(a) && /physical/.test(a)) return "__rest_physical__";
    const hit = current.chips.find((c) => c.value === a || c.label.toLowerCase() === a || c.label.toLowerCase().includes(a) || a.includes(c.value));
    return hit?.value ?? null;
  };

  const submit = async (): Promise<void> => {
    const t = input.trim();
    if (!t || sending || decisionBusyRef.current || assistantTyping || resolvingDecision !== null) return;
    decisionBusyRef.current = true;
    setInput(""); say("user", t); setSending(true);
    try {
      // Exact SKU-code commands are resolved against the COMPLETE browser model.
      // Large catalogs only send a sample to the chat model, so this guarantees
      // a bulk removal never loops just because code 741 was outside that sample.
      const direct = inferDirectSkuCommand(t, [
        ...model.products.flatMap((p) => p.skus),
        ...model.aside,
        ...model.removed,
      ].map((s) => s.skuCode));
      if (direct) {
        await waitLikeChat();
        const pv = onPreview({ op: direct.op, selector: { codes: direct.codes } });
        if (!pv.ok) say("assistant", pv.summary);
        else { say("assistant", "I found them. Check the preview below before anything changes."); setPending(pv); }
        return;
      }
      const ctx = {
        products: model.products.map((p) => ({ name: p.name, kind: p.kind, variants: variantsOf(p), skuCount: p.skus.length, skus: p.skus.slice(0, 30).map((s) => ({ name: s.label, code: s.skuCode })) })),
        asideCount: model.aside.length,
        asideSkus: model.aside.slice(0, 30).map((s) => ({ name: s.label, code: s.skuCode })),
        retiredCount: model.removed.length,
        unitLabel: model.products[0]?.unit ?? "units",
        currentStep: current ? { question: current.message, options: current.chips.map((c) => c.label) } : undefined,
      };
      const [r] = await Promise.all([
        api.post<ParsedCommand>("/catalog/command", { message: t, context: ctx }),
        waitLikeChat(),
      ]);
      if (r.intent === "answer" && current) {
        // A reply is often an answer PLUS more ("Yes but I have other bundles").
        // Resolve the chip, then ADDRESS the rest — never drop it on the floor.
        const v = matchChip(r.answer);
        if (v) await resolveCurrent(v, true); else say("assistant", r.reply || "Got it.");
        if (r.action) {
          const pv = onPreview(r.action);
          if (!pv.ok) say("assistant", pv.summary);
          else { if (r.followUp) say("assistant", r.followUp); setPending(pv); }
        } else if (r.followUp) {
          say("assistant", r.followUp);
        }
      } else if (r.intent === "command" && r.action) {
        const pv = onPreview(r.action);
        if (!pv.ok) say("assistant", pv.summary);
        else { if (r.reply) say("assistant", r.reply); setPending(pv); }
      } else if (r.intent === "navigate") {
        // Structural questions can be skipped into the review sheet. Product
        // type cannot: null used to silently mean physical downstream, which
        // asked software and service businesses the wrong cost questions.
        const nextResolved = new Set(resolved);
        decisions.filter((decision) => !isTypeDecision(decision.id)).forEach((decision) => nextResolved.add(decision.id));
        const hasUnansweredType = decisions.some((decision) => isTypeDecision(decision.id)) || untypedProductIds.length > 0;
        if (await saveInterview({ started: true, resolvedDecisionIds: [...nextResolved], reviewReached: !hasUnansweredType })) {
          if (hasUnansweredType) {
            setResolved(nextResolved);
            say("assistant", "Before the full review, I still need to know what kind of thing each product is. That keeps me from asking you for costs that do not apply.");
            return;
          }
          everReady.current = true;
          setResolved(nextResolved);
          say("assistant", r.reply || "Sure. Here's your full catalog below to review.");
        } else {
          say("assistant", "I couldn't save your place yet. Nothing was lost, but try that once more before leaving this page.");
        }
      } else {
        say("assistant", r.reply);
      }
    } catch { say("assistant", "Hmm — I had trouble with that. Mind trying again?"); }
    finally { decisionBusyRef.current = false; setSending(false); }
  };

  const applyPending = async (): Promise<void> => {
    if (!pending || applyingRef.current || decisionBusyRef.current) return; // ref guard: a rapid double-click can't apply twice
    applyingRef.current = true;
    decisionBusyRef.current = true;
    setSending(true);
    const p = pending;
    setPending(null);
    try {
      const undo = p.run();
      if (await onWaitForSaves()) {
        say("assistant", `✓ ${p.summary}. Done.`);
        setUndoFn(undo ? { run: undo } : null);
      } else {
        say("assistant", "That change did not save. I reloaded your last saved catalog so you can try again.");
      }
    } catch {
      say("assistant", "That change did not finish. Nothing else was changed, and you can try again.");
    } finally {
      applyingRef.current = false;
      decisionBusyRef.current = false;
      setSending(false);
    }
  };
  const cancelPending = (): void => {
    if (decisionBusyRef.current || applyingRef.current || sending) return;
    setPending(null);
    say("assistant", "No problem. I left everything as it was.");
  };
  const doUndo = async (): Promise<void> => {
    if (!undoFn || decisionBusyRef.current) return;
    decisionBusyRef.current = true;
    setSending(true);
    try {
      undoFn.run();
      if (await onWaitForSaves()) {
        say("assistant", "Undone.");
        setUndoFn(null);
      } else {
        say("assistant", "I couldn't save the undo. I reloaded the saved catalog, and you can try again.");
      }
    } catch {
      say("assistant", "I couldn't finish the undo. Your last saved catalog is still safe.");
    } finally {
      decisionBusyRef.current = false;
      setSending(false);
    }
  };
  const beginInterview = async (): Promise<void> => {
    if (decisionBusyRef.current) return;
    decisionBusyRef.current = true;
    setSending(true);
    try {
      const saved = await saveInterview({ started: true, resolvedDecisionIds: [], reviewReached: false });
      if (!saved) {
        toast.error("I couldn't save your starting point. Try Start again.");
        return;
      }
      setStarted(true);
    } finally {
      decisionBusyRef.current = false;
      setSending(false);
    }
  };
  const retryFreshInterviewReset = async (): Promise<void> => {
    setInterviewHydrating(true);
    const progress = { started: false, resolvedDecisionIds: [], reviewReached: false };
    const saved = await saveInterview(progress);
    if (saved) {
      everReady.current = false;
      wrapped.current = false;
      askedDecisionIds.current.clear();
      setBoot(0);
      setStarted(false);
      setResolved(new Set());
      setMsgs([]);
      setReviewProgressSaved(false);
      setInterviewSaveError(null);
    } else {
      setInterviewSaveError("I couldn't reset this walkthrough yet. Nothing was changed.");
    }
    setInterviewHydrating(false);
  };

  if (mode === "real" && interviewQ.isError) {
    return (
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ fontSize: 13.5, color: "var(--bad)", fontWeight: 600 }}>I couldn't load your saved catalog walkthrough. Nothing was changed.</div>
        <button style={{ ...primaryBtn, marginTop: 10 }} onClick={() => { void interviewQ.refetch(); }}>Try again</button>
      </div>
    );
  }

  if (mode === "real" && interviewSaveError && !started) {
    return (
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ fontSize: 13.5, color: "var(--bad)", fontWeight: 600 }}>{interviewSaveError}</div>
        <button style={{ ...primaryBtn, marginTop: 10 }} disabled={interviewHydrating} onClick={() => { void retryFreshInterviewReset(); }}>{interviewHydrating ? "Trying…" : "Try again"}</button>
      </div>
    );
  }

  if (boot < 2 || !interviewReady) {
    return (
      <div style={{ ...card, marginBottom: 14, display: "flex", gap: 12, alignItems: "center" }}>
        {CHAT_STYLE}
        <span style={asstAvatar}><SparkleIcon size={14} /></span>
        <div className="ocp-msg" key={boot} style={{ fontSize: 14, color: "var(--body)" }}>
          {boot === 0 ? "Reading your catalog" : `Found ${totalSkus} SKUs — sorting them into products`} <TypingDots />
        </div>
      </div>
    );
  }

  if (!started) {
    const summary = `${products.length} product${products.length === 1 ? "" : "s"}${bundles.length ? `, ${bundles.length} bundle${bundles.length === 1 ? "" : "s"}` : ""}${bonuses.length ? ", and a bonuses bucket" : ""}`;
    return (
      <div style={{ ...card, marginBottom: 14, display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
        {CHAT_STYLE}
        <span style={asstAvatar}><SparkleIcon size={14} /></span>
        <div className="ocp-msg" style={{ flex: 1, minWidth: 300 }}>
          <div style={{ fontSize: 14.5, lineHeight: 1.55 }}>
            I made a first pass through your catalog and drafted <strong style={{ fontWeight: 600 }}>{summary}</strong>.
            <div style={{ marginTop: 8 }}>Hit <strong style={{ fontWeight: 600 }}>Start</strong> and I’ll ask only about the parts that look risky: bundles, bonuses, non-products, and products with multiple variants. Then we’ll review the full sheet together before anything moves to Costs. You can talk to me the whole way through, including bulk changes like <em>“move the auto-delivery SKUs into Collagen.”</em></div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
            <button style={{ ...primaryBtn, opacity: sending ? 0.6 : 1 }} disabled={sending} onClick={() => { void beginInterview(); }}>{sending ? "Saving…" : "Start →"}</button>
          </div>
        </div>
      </div>
    );
  }

  const avatar = <span style={{ ...asstAvatar, alignSelf: "flex-start", marginTop: 2 }}><SparkleIcon /></span>;
  const asstRow = (content: React.ReactNode, key?: React.Key): React.ReactElement => (
    <div key={key} className="ocp-msg" style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 9 }}>{avatar}<div style={{ ...bubble, ...asstB, margin: 0, whiteSpace: "pre-line" }}>{content}</div></div>
  );

  return (
    // ChatDock: normal in-flow card at the top; scrolled past → floating
    // "Show chat box" button; click → resizable popup panel (founder spec).
    // floatBottom clears the sticky "Looks good → Costs" bar at the page
    // bottom so the floating button never sits on top of it (founder 2026-07-03).
    <ChatDock heightKey="ccd.onboarding.chatH.categorize" defaultH={340} floatBottom={84}>
      {(chatH) => <>
      {CHAT_STYLE}
      <div ref={thread} data-chat-thread style={{ display: "flex", flexDirection: "column", height: chatH, overflowY: "auto", paddingRight: 4 }}>
        {msgs.map((m, i) => (m.role === "user"
          ? <div key={i} className="ocp-msg" style={{ ...bubble, ...userB }}>{m.text}</div>
          : asstRow(m.text, i)))}

        {current && !pending && !sending && resolvingDecision !== current.id && (
          <ChoiceList indent={40}>
            {current.chips.map((c) => <ChoiceRow key={c.value} label={c.label} onClick={() => tapChip(c.value, c.label)} />)}
          </ChoiceList>
        )}

        {pending && (
          <>
            {asstRow(<><div style={{ fontWeight: 600 }}>{pending.summary}</div>{pending.sample.length > 0 && <div style={{ fontSize: 11.5, color: "var(--faint)", marginTop: 3 }}>{pending.sample.join(" · ")}{pending.count > pending.sample.length ? ` +${pending.count - pending.sample.length} more` : ""}</div>}</>)}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "0 0 10px 40px" }}>
              <button style={primaryBtn} disabled={sending || assistantTyping || resolvingDecision !== null} onClick={() => { void applyPending(); }}>Apply</button>
              <button style={ghostBtn} disabled={sending || assistantTyping || resolvingDecision !== null} onClick={cancelPending}>Cancel</button>
            </div>
          </>
        )}

        {(sending || assistantTyping) && asstRow(<TypingDots />)}
        {reviewReady && mode === "real" && !reviewProgressSaved && asstRow(
          interviewSaveError
            ? <><span>{interviewSaveError}</span><button style={{ ...ghostBtnSm, marginLeft: 8 }} disabled={reviewProgressSaving} onClick={() => { void saveReviewProgress(); }}>{reviewProgressSaving ? "Saving…" : "Try again"}</button></>
            : <>Saving your place <TypingDots /></>,
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "stretch", marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
          placeholder={current ? "Type your answer, or tell me what to change…" : "Tell me anything to change, or review below…"}
          disabled={sending || assistantTyping || resolvingDecision !== null}
          style={smartInput}
        />
        <SendButton onClick={() => void submit()} disabled={sending || assistantTyping || resolvingDecision !== null || !input.trim()} />
        {undoFn && <button disabled={sending || assistantTyping || resolvingDecision !== null} style={{ ...ghostBtnSm, alignSelf: "center" }} onClick={() => { void doUndo(); }} title="Undo last change"><Ico name="undo" style={{ verticalAlign: -1, marginRight: 3 }} />Undo</button>}
      </div>
      {mode === "sandbox" && <div style={{ fontSize: 10.5, color: "var(--faint)", marginTop: 8 }}>Sandbox — changes preview here but never save.</div>}
      </>}
    </ChatDock>
  );
}

/**
 * Robust number box. It owns its DRAFT string and renders exactly that — so what
 * you see can never disagree with the keys you pressed (the "typed 1, got 0" bug
 * came from re-parsing a controlled value on every keystroke). It only converts to
 * a number on blur: onChange fires live for the model, onCommit fires on blur.
 */
function NumberBox({ value, onChange, onCommit, prefix, decimals, resetNonce, resetWhenBlank = false, resetWhenInvalid = false, ...rest }: {
  value: number | null;
  onChange?: (v: number | null) => void;
  onCommit?: (v: number | null) => void;
  prefix?: string; // visual-only "$" overlay — never part of the typed value
  decimals?: number;
  resetNonce?: number;
  resetWhenBlank?: boolean;
  resetWhenInvalid?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "onBlur" | "onFocus" | "prefix">): React.ReactElement {
  const display = (number: number | null): string => number === null ? "" : decimals === undefined ? String(number) : number.toFixed(decimals);
  const [draft, setDraft] = useState(display(value));
  const ref = useRef<HTMLInputElement>(null);
  // Only pull the parent value into the box when the user is NOT typing in it.
  // We check document.activeElement at effect time (the truth post-render) rather
  // than a focus flag set by onFocus — a focus flag loses the race against parent
  // re-renders and StrictMode's mount/unmount/mount, which is what made a typed
  // "1" snap back to 0 (the parent re-render re-derived the draft mid-keystroke).
  useEffect(() => {
    if (ref.current && document.activeElement === ref.current) return; // editing → keep the draft
    setDraft(value === null ? "" : decimals === undefined ? String(value) : value.toFixed(decimals));
  }, [value, decimals, resetNonce]);
  const parse = (raw: string): number | null => { const t = raw.trim(); if (t === "") return null; const n = Number(t); return Number.isFinite(n) ? n : null; };
  const input = (
    <input
      {...rest}
      ref={ref}
      type="number"
      value={draft}
      onChange={(e) => { setDraft(e.target.value); onChange?.(parse(e.target.value)); }}
      onBlur={(e) => {
        const v = parse(e.target.value);
        const min = rest.min === undefined ? null : Number(rest.min);
        const max = rest.max === undefined ? null : Number(rest.max);
        const invalid = resetWhenInvalid && (v === null || (min !== null && Number.isFinite(min) && v < min) || (max !== null && Number.isFinite(max) && v > max));
        setDraft(invalid || (v === null && resetWhenBlank) ? display(value) : display(v));
        onCommit?.(v);
      }}
      style={prefix ? { ...rest.style, paddingLeft: 15 } : rest.style}
    />
  );
  if (!prefix) return input;
  // "$100" reads like money; a bare "100" doesn't (founder 2026-07-02). The $
  // sits over the input's left padding so the number value stays clean.
  return (
    <span style={{ position: "relative", display: "inline-flex", width: rest.style?.width, maxWidth: rest.style?.maxWidth, minWidth: 0 }}>
      <span style={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--faint)", pointerEvents: "none" }}>{prefix}</span>
      {input}
    </span>
  );
}

/* The live sheet: Product ▸ Variant ▸ SKU, full SKU names, drag to override. */
function Sheet({ model, currency = "USD", startAtReview = false, onMove, onRetire, onRename, onSetType, onEdit, onEditLocal, onSaveComponents, onConfirm, onConfirmGroup, savePending, saveError, onRetrySave, onContinue }: {
  model: Model; currency?: string; startAtReview?: boolean; onMove: (skuId: string, toGroupId: string) => void; onRename: (id: string, name: string) => void;
  onRetire: (skuIds: string[], retired: boolean) => void;
  onSetType: (id: string, type: ProductType) => void;
  onEdit: (skuId: string, patch: { packSize?: number | null; boxesPerSale?: number | null; offerLabel?: string | null; manualPrice?: number | null }) => void;
  onEditLocal: (skuId: string, patch: { packSize?: number | null; boxesPerSale?: number | null; offerLabel?: string | null; manualPrice?: number | null }) => void;
  onSaveComponents: (bundleSkuId: string, components: SkuComponent[]) => void;
  onConfirm: (skuId: string) => void;
  onConfirmGroup: (groupId: string) => void;
  savePending: boolean;
  saveError: string | null;
  onRetrySave: () => void;
  onContinue: () => void;
}): React.ReactElement {
  // Deep-linked from "Organize" → bring the first flagged row into view once, so
  // the founder lands looking AT the thing that needs a review (founder 2026-07-12),
  // not at the top of a long sheet. One-shot; manual scrolling afterwards is free.
  const scrolledToFlag = useRef(false);
  useEffect(() => {
    if (!startAtReview || scrolledToFlag.current) return;
    const el = document.querySelector(".ocp-attention");
    if (!el) return;
    scrolledToFlag.current = true;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  });
  // What can sit inside a bundle: real products + bonus items (mirrors the
  // server's candidate set, so an auto-filled part always resolves to an option).
  const productGroups = model.products.filter((p) => p.kind === "product" || p.kind === "bonus");
  const componentSkus = productGroups.flatMap((product) => product.skus.map((sku) => ({ product, sku })));
  // Costs-focused gate: # Units/Sale is the ONLY thing the merchant sets here (it
  // drives cost). A bundle's units come from its parts, bonuses are 1 — so the gate
  // is: every product SKU has # Units/Sale, and every bundle has its parts defined.
  const missingUnitsPerSale = countMissingUnits(model.products);
  // Trust gate: the AI flags rows it's not sure about; the merchant must eyeball +
  // confirm those (the amber ones) so they can't blindly fly through.
  // One JUDGMENT = one confirmation (founder 2026-07-11: one AI doubt about a
  // product fanned out to 18 amber rows): flags that share a product collapse
  // into ONE banner with a confirm-all; only row-specific causes (an empty
  // bundle that needs its parts) stay per-row.
  const emptyBundleFlag = (p: Product, s: SheetSku): boolean => p.kind === "bundle" && s.needsReview && !bundlePartsComplete(s);
  const groupFlags = (p: Product): SheetSku[] => p.skus.filter((s) => s.needsReview && !emptyBundleFlag(p, s));
  const rowFlags = (p: Product): SheetSku[] => p.skus.filter((s) => emptyBundleFlag(p, s));
  const unconfirmed = model.products.reduce((n, p) => n + rowFlags(p).length + (groupFlags(p).length > 0 ? 1 : 0), 0);
  const untypedProducts = untypedRegularProductIds(model.products);
  const canContinue = missingUnitsPerSale === 0 && unconfirmed === 0 && untypedProducts.length === 0 && !savePending && saveError === null;
  const dragId = useRef<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const ordered = [...model.products].sort((a, b) => (KIND_ORDER[a.kind] - KIND_ORDER[b.kind]) || a.name.localeCompare(b.name));
  const lanes = [...ordered, { id: ASIDE, name: "Set aside — not products", unit: "", kind: "product", skus: model.aside } as Product];

  const rename = (p: Product): void => { const name = window.prompt("Rename product", p.name); if (name && name.trim() && name.trim() !== p.name) onRename(p.id, name.trim()); };
  const drop = (toGroupId: string): void => { const id = dragId.current; setOver(null); dragId.current = null; if (id) onMove(id, toGroupId); };
  const moveTargets = (currentId: string): Array<{ id: string; name: string }> => [
    ...model.products.filter((p) => p.id !== currentId).map((p) => ({ id: p.id, name: p.name })),
    ...(currentId !== ASIDE ? [{ id: ASIDE, name: "Set aside (not a product)" }] : []),
  ];
  const removeFromSetup = (s: SheetSku): void => {
    const ok = window.confirm(`Remove ${s.skuCode} from current setup?\n\nIts past sales will stay in your reports. Historical profit is only complete when its old product and shipping costs are on file. Any missing old costs will stay flagged for team review. You can restore it later.`);
    if (ok) onRetire([s.skuId], true);
  };
  // Does this lane carry the one-banner treatment (its flags share one AI judgment)?
  const bannered = (p: Product): boolean => p.id !== ASIDE && groupFlags(p).length > 1;
  // Group a product's SKUs by variant (null variants in a leading "—" group), sorted by qty.
  const byVariant = (p: Product): Array<{ variant: string | null; skus: SheetSku[] }> => {
    const map = new Map<string, SheetSku[]>();
    for (const s of p.skus) { const k = s.variant?.trim() || ""; (map.get(k) ?? map.set(k, []).get(k)!).push(s); }
    const keys = [...map.keys()].sort((a, b) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)));
    // Order rows by something STABLE (revenue, then code). Never sort on an editable
    // field like # Units/Sale — the row would jump the instant you fill it in.
    return keys.map((k) => ({ variant: k || null, skus: map.get(k)!.sort((x, y) => (y.revenue - x.revenue) || x.skuCode.localeCompare(y.skuCode)) }));
  };

  return (
    <div>
      <div style={sheetColumnGuide} aria-hidden="true">
        <span></span>
        <span>SKU</span>
        <span># Units/Sale</span>
        <span></span>
      </div>
      {lanes.map((p) => (
        <div key={p.id} style={{ ...card, padding: 0, marginBottom: 10, ...(over === p.id ? { outline: "2px dashed var(--accent)", outlineOffset: -2 } : {}) }}
          onDragOver={(e) => { e.preventDefault(); if (over !== p.id) setOver(p.id); }} onDragLeave={() => setOver((o) => (o === p.id ? null : o))} onDrop={() => drop(p.id)}>
          <div className={p.kind === "product" && !p.type ? "ocp-attention" : undefined} style={{ ...stickyGroupHead, display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "var(--surface-2)", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
            {p.id === ASIDE ? <Ico name="archive" size={15} style={{ color: "var(--faint)" }} /> : null}
            <span style={{ fontWeight: 600, fontSize: 14, color: p.id === ASIDE ? "var(--body)" : "var(--text)" }}>{p.name}</span>
            {p.id !== ASIDE && p.kind === "bundle" && <span style={kindPill}>🧩 Bundle</span>}
            {p.id !== ASIDE && p.kind === "bonus" && <span style={kindPill}>🎁 Bonus</span>}
            {p.id !== ASIDE && p.kind === "misc" && <span style={kindPill}>🧾 Misc</span>}
            {p.id !== ASIDE && p.kind === "wholesale" && <span style={kindPill}>🏷️ Wholesale</span>}
            {p.id !== ASIDE && p.kind === "product" && <span style={unitPill}>{p.unit}</span>}
            <span style={{ color: "var(--faint)", fontSize: 12 }}>{p.skus.length} SKU{p.skus.length === 1 ? "" : "s"}</span>
            {p.id !== ASIDE && p.kind === "product" && (
              <select value={p.type ?? ""} onChange={(event) => { if (event.target.value) onSetType(p.id, event.target.value as ProductType); }} style={{ ...moveSelect, minWidth: 160, borderColor: p.type ? "var(--border)" : "var(--c2)" }} aria-label={`What kind of product is ${p.name}?`}>
                <option value="">Choose what this is…</option>
                {PRODUCT_TYPES.map((option) => <option key={option.type} value={option.type}>{option.label}</option>)}
              </select>
            )}
            {p.id !== ASIDE && <button style={{ ...ghostBtnSm, marginLeft: "auto" }} onClick={() => rename(p)}>Rename</button>}
          </div>
          {/* ONE amber banner per AI judgment: the doubt was about this product,
              not each row — so the merchant glances at the echoed reads below
              and clears them all in one tap instead of row by row. */}
          {p.id !== ASIDE && groupFlags(p).length > 1 && (
            <div className="ocp-attention" style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", padding: "10px 14px", fontSize: 12.5, lineHeight: 1.45, color: "var(--body)", background: "color-mix(in srgb, var(--c2) 12%, transparent)", borderBottom: "1px solid var(--border)", borderLeft: "3px solid var(--c2)" }}>
              <Ico name="alert" size={15} style={{ color: "var(--c2)", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 220 }}>I wasn't 100% sure about <strong>{p.name}</strong> — each row below says what I read from its name and price. If they look right, one tap confirms them all.</div>
              <button onClick={() => onConfirmGroup(p.id)} style={{ ...ghostBtnSm, borderColor: "var(--c2)", color: "var(--c2)", fontWeight: 700, padding: "4px 12px", whiteSpace: "nowrap" }}>✓ Confirm all {groupFlags(p).length}</button>
            </div>
          )}
          {p.kind === "wholesale" && p.id !== ASIDE && (
            <div style={{ display: "flex", gap: 9, padding: "9px 14px", fontSize: 12.5, lineHeight: 1.4, color: "var(--body)", background: "color-mix(in srgb, var(--c2) 10%, transparent)", borderBottom: "1px solid var(--border)", borderLeft: "3px solid var(--c2)" }}>
              <Ico name="warehouse" size={16} style={{ color: "var(--c2)", flexShrink: 0, marginTop: 1 }} />
              <div>Wholesale is a separate B2B channel, so it's <strong>kept out of your numbers by default</strong>. If you ever want it counted, flip <strong>“Include wholesale”</strong> in the <strong>Revenue adjustments</strong> section of the filter sidebar (right side of any dashboard page).</div>
            </div>
          )}
          {p.id === ASIDE && (
            <div style={{ display: "flex", gap: 9, padding: "9px 14px", fontSize: 12.5, lineHeight: 1.45, color: "var(--body)", background: "color-mix(in srgb, var(--c2) 8%, transparent)", borderBottom: "1px solid var(--border)" }}>
              <Ico name="info" size={15} style={{ color: "var(--c2)", flexShrink: 0, marginTop: 1 }} />
              <div>Set aside anything that was never a real product, like a coupon, shipping line, or test item. If a discontinued SKU was a real product, move it back to its product first. Then use <strong>Remove</strong>. Its past sales will stay, and any missing old costs will be flagged before historical profit is trusted.</div>
            </div>
          )}
          {p.kind === "bundle" && p.id !== ASIDE && (
            <div style={{ display: "flex", gap: 9, padding: "11px 14px", fontSize: 13, lineHeight: 1.45, color: "var(--body)", background: "color-mix(in srgb, var(--c2) 14%, transparent)", borderBottom: "1px solid var(--border)", borderLeft: "3px solid var(--c2)" }}>
              <Ico name="puzzle" size={17} style={{ color: "var(--c2)", flexShrink: 0, marginTop: 1 }} />
              <div><strong>This is all you set for a bundle.</strong> We read what's inside from its name — just check the parts and quantities below. Each part's cost goes in on the next step, and the bundle's margin adds up from those parts automatically.</div>
            </div>
          )}
          {p.skus.length > 0 ? (
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12.5 }}>
              <thead>
                <tr style={{ color: "var(--faint)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                <th style={{ ...stickyTh, width: 28 }}></th><th style={{ ...stickyTh, textAlign: "left" }}>SKU</th><th style={{ ...stickyTh, textAlign: "left", width: 120 }}>{p.id === ASIDE ? "" : "# Units/Sale"}</th><th style={{ ...stickyTh, width: 190 }}></th>
              </tr></thead>
              {byVariant(p).map((grp) => (
                <tbody key={grp.variant ?? "_"}>
                  {grp.variant && (
                    <tr><td colSpan={4} style={{ padding: "6px 14px 2px 36px", fontSize: 11, fontWeight: 600, color: "var(--accent)" }}><Ico name="cornerDownRight" size={12} style={{ verticalAlign: -1, marginRight: 4, opacity: 0.6 }} />{grp.variant}<span style={{ color: "var(--faint)", fontWeight: 400 }}> · {grp.skus.length}</span></td></tr>
                  )}
                  {grp.skus.map((s) => (
                    <Fragment key={s.skuId}>
                    <tr className={p.id !== ASIDE && ((s.needsReview && (!bannered(p) || emptyBundleFlag(p, s))) || (p.kind === "product" && (s.boxesPerSale === null || s.boxesPerSale <= 0)) || (p.kind === "bundle" && !bundlePartsComplete(s))) ? "ocp-attention" : undefined} style={{ borderTop: "1px solid var(--hair)" }}>
                      <td style={{ ...td, textAlign: "center", padding: "8px 2px" }}>
                        <span draggable onDragStart={(e) => { dragId.current = s.skuId; e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { dragId.current = null; }} title="Drag to move" style={{ cursor: "grab", color: "var(--faint)" }}><GripIcon /></span>
                      </td>
                      <td style={{ ...td, paddingLeft: grp.variant ? 30 : 8, ...(s.needsReview && p.id !== ASIDE && (!bannered(p) || emptyBundleFlag(p, s)) ? { boxShadow: "inset 3px 0 0 var(--c2)" } : {}) }}>
                        <div style={{ lineHeight: 1.35 }} title={s.label}>{s.label}</div>
                        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--faint)" }}>{s.skuCode}</div>
                        <div style={{ fontSize: 10.5, color: "var(--faint)", marginTop: 1, fontVariantNumeric: "tabular-nums" }}>{whole(s.orders)} order{s.orders === 1 ? "" : "s"} · {money(s.revenue, currency)}</div>
                        {s.needsReview && p.id !== ASIDE && (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 4, flexWrap: "wrap" }}>
                            {/* Echo what we READ instead of asking cold — "looks like a
                                single bottle at $44.95" confirms at a glance; the empty
                                bundle gets its own ask (its parts, not a confirmation). */}
                            <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--c2)" }}><Ico name="alert" size={12} style={{ verticalAlign: -2, marginRight: 2 }} />{emptyBundleFlag(p, s) ? "Tell me what's inside this bundle — add its parts below" : s.inferenceNote ?? "Not 100% sure — double-check this"}</span>
                            {!emptyBundleFlag(p, s) && <button onClick={() => onConfirm(s.skuId)} style={{ ...ghostBtnSm, padding: "2px 9px", borderColor: "var(--c2)", color: "var(--c2)", fontWeight: 600 }}>✓ Looks right</button>}
                            {!emptyBundleFlag(p, s) && p.kind === "product" && <button onClick={() => document.getElementById(`ocp-units-${s.skuId}`)?.focus()} style={{ ...ghostBtnSm, padding: "2px 9px" }}>Fix the count</button>}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {p.id === ASIDE ? null
                          : p.kind === "bundle" ? (
                              <div style={{ ...editInput, background: "transparent", border: "none", paddingLeft: 0, color: bundleUnitCount(s) > 0 ? "var(--body)" : "var(--faint)" }} title="Total units in the bundle — adds up from the parts below">
                                {bundleUnitCount(s) > 0 ? <>{bundleUnitCount(s)} <span style={{ color: "var(--faint)", fontSize: 10.5 }}>from parts</span></> : <>add parts ↓</>}
                              </div>
                            )
                          : p.kind === "bonus" || p.kind === "misc" || p.kind === "wholesale" ? (
                              <NumberBox value={s.boxesPerSale ?? 1} min="0" style={editInput} title="Units per sale — 1 by default for add-ons" onChange={(v) => onEditLocal(s.skuId, { boxesPerSale: v })} onCommit={(v) => onEdit(s.skuId, { boxesPerSale: v ?? 1 })} />
                            )
                          : (
                              <NumberBox id={`ocp-units-${s.skuId}`} value={s.boxesPerSale} min="0" style={{ ...editInput, ...(s.boxesPerSale === null ? requiredInputStyle : {}) }} title="How many physical units ship per sale — this is what drives the SKU's cost" onChange={(v) => onEditLocal(s.skuId, { boxesPerSale: v })} onCommit={(v) => onEdit(s.skuId, { boxesPerSale: v })} />
                            )}
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <select value="" onChange={(e) => { if (e.target.value) onMove(s.skuId, e.target.value); }} style={{ ...moveSelect, minWidth: 0 }} aria-label="Move to product">
                            <option value="">Move…</option>
                            {moveTargets(p.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                          {p.id !== ASIDE && <button onClick={() => removeFromSetup(s)} style={{ ...ghostBtnSm, color: "var(--faint)", whiteSpace: "nowrap" }} title="Use this when you no longer sell this SKU. Past sales stay in reports. Old costs are still needed for historical profit.">Remove</button>}
                        </div>
                      </td>
                    </tr>
                    {p.kind === "bundle" && p.id !== ASIDE && (
                      <tr><td colSpan={4} style={{ padding: "2px 14px 10px 36px", background: "color-mix(in srgb, var(--c2) 5%, transparent)" }}>
                        <div style={{ fontSize: 10, color: s.components.length === 0 ? "var(--c2)" : "var(--faint)", textTransform: "uppercase", letterSpacing: "0.03em", paddingBottom: 3 }}>{s.components.length === 0 ? "⚠ Couldn't read this one — add the products inside:" : "What's inside this bundle"}</div>
                        {s.components.map((c) => (
                          <div key={c.id} style={{ display: "flex", gap: 6, alignItems: "center", padding: "2px 0", fontSize: 11.5, maxWidth: 460 }}>
                            <span style={{ color: "var(--faint)" }}>↳</span>
                            <select
                              value={c.componentSkuId ? `sku:${c.componentSkuId}` : c.componentProductGroupId ? `group:${c.componentProductGroupId}` : ""}
                              onChange={(e) => {
                                const [kind, id] = e.target.value.split(":", 2);
                                const exact = kind === "sku" ? componentSkus.find((item) => item.sku.skuId === id) : undefined;
                                const group = kind === "group" ? productGroups.find((item) => item.id === id) : exact?.product;
                                onSaveComponents(s.skuId, s.components.map((x) => x.id === c.id ? {
                                  ...x,
                                  componentProductGroupId: group?.id ?? null,
                                  componentSkuId: exact?.sku.skuId ?? null,
                                  label: exact ? `${exact.product.name} — ${exact.sku.label}` : group?.name ?? x.label,
                                  // Component quantity always means real units.
                                  // Selecting a 3-pack should therefore start at
                                  // 3, not silently undercount the bundle as 1.
                                  quantity: exact && exact.sku.boxesPerSale && exact.sku.boxesPerSale > 0
                                    ? exact.sku.boxesPerSale
                                    : x.quantity,
                                } : x));
                              }}
                              style={{ ...moveSelect, flex: 1, minWidth: 0 }}
                              aria-label="Component product or exact SKU"
                              data-component-save="serial"
                            >
                              <option value="">Pick a product…</option>
                              <optgroup label="Product">
                                {productGroups.map((x) => <option key={x.id} value={`group:${x.id}`}>{x.name}</option>)}
                              </optgroup>
                              <optgroup label="Exact SKU or variant">
                                {componentSkus.map(({ product, sku }) => <option key={sku.skuId} value={`sku:${sku.skuId}`}>{product.name} — {sku.label} — {sku.skuCode}</option>)}
                              </optgroup>
                            </select>
                            <span style={{ color: "var(--faint)" }}>×</span>
                            <NumberBox value={c.quantity} min="1" resetWhenInvalid style={{ ...editInput, width: 48 }} title="How many real units are in one bundle" onCommit={(v) => { if (v !== null && Number.isFinite(v) && v > 0 && v !== c.quantity) onSaveComponents(s.skuId, s.components.map((x) => x.id === c.id ? { ...x, quantity: v } : x)); }} />
                            <span style={{ color: "var(--faint)", whiteSpace: "nowrap" }}>real units</span>
                            <button onClick={() => onSaveComponents(s.skuId, s.components.filter((x) => x.id !== c.id))} style={{ ...ghostBtnSm, padding: "2px 7px" }} title="Remove this part" aria-label="Remove">✕</button>
                          </div>
                        ))}
                        <button onClick={() => onSaveComponents(s.skuId, [...s.components, { id: `new-${s.skuId}-${Date.now()}`, componentSkuId: null, componentProductGroupId: null, label: "", quantity: 1 }])} style={{ ...ghostBtnSm, marginTop: 4 }}><Ico name="plus" size={12} style={{ verticalAlign: -1, marginRight: 3 }} />Add product</button>
</td></tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              ))}
            </table>
          ) : <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--faint)" }}>Drop SKUs here.</div>}
        </div>
      ))}
      {model.removed.length > 0 && (
        <div style={{ ...card, padding: 0, marginTop: 12, marginBottom: 10 }}>
          <button
            onClick={() => setShowRemoved((v) => !v)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", border: "none", background: "var(--surface-2)", color: "var(--body)", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
          >
            <Ico name="archive" size={15} style={{ color: "var(--faint)" }} />
            <strong>Removed from setup</strong>
            <span style={{ color: "var(--faint)", fontSize: 12 }}>{model.removed.length} SKU{model.removed.length === 1 ? "" : "s"}</span>
            <span style={{ marginLeft: "auto", color: "var(--faint)", fontSize: 12 }}>{showRemoved ? "Hide" : "View"}</span>
          </button>
          {showRemoved && (
            <div>
              <div style={{ padding: "9px 14px", color: "var(--faint)", fontSize: 12, lineHeight: 1.45, borderTop: "1px solid var(--border)" }}>
                These SKUs are hidden from current setup because you no longer sell them. Their past sales stay in reports. Historical profit is complete only where their old product and shipping costs are on file; any gap stays flagged for team review.
              </div>
              {model.removed.map((s) => (
                <div key={s.skuId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderTop: "1px solid var(--hair)" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--faint)" }}>{s.skuCode}</div>
                  </div>
                  <button onClick={() => onRetire([s.skuId], false)} style={ghostBtnSm}>Restore</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ fontSize: 11, color: "var(--faint)", marginTop: 6 }}><Ico name="pointer" style={{ verticalAlign: -2 }} /> Drag the handle or use Move · variants shown under each product · # Units/Sale is the one number we need (it drives cost) · orders &amp; revenue are shown for context · price comes straight from your orders</div>
      {/* zIndex 6: must paint over the sheet's sticky group headers (4) and
          column guide (5), which otherwise slide OVER this bar as they pass
          through the bottom of the viewport (founder 2026-07-02: "funky").
          Redesigned per founder 2026-07-06: a CENTERED floating card with one
          standout button — narrower than the page so the chat pill in the
          bottom-right corner gets clear air instead of crowding it. */}
      <div style={{ position: "sticky", bottom: 18, zIndex: 6, display: "flex", justifyContent: "center", pointerEvents: "none", marginTop: 18 }}>
        <div style={{
          pointerEvents: "auto",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
          padding: "16px 26px 18px",
          maxWidth: 560,
          background: "color-mix(in srgb, var(--surface) 92%, var(--accent) 8%)",
          border: "1px solid color-mix(in srgb, var(--accent) 25%, var(--border))",
          borderRadius: 16,
          boxShadow: "0 12px 34px color-mix(in srgb, var(--accent) 16%, rgba(0,0,0,.18))",
          backdropFilter: "blur(6px)",
        }}>
          <div style={{ fontSize: 12.5, color: "var(--body)", textAlign: "center", lineHeight: 1.45 }}>
            {saveError
              ? <><Ico name="alert" size={14} style={{ verticalAlign: -2, color: "var(--bad)", marginRight: 4 }} />{saveError} <button onClick={onRetrySave} style={{ background: "none", border: "none", color: "var(--bad)", fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontSize: 12.5, padding: 0 }}>Try again</button></>
              : savePending
                ? <>Saving your changes…</>
                : !canContinue
                  ? <><Ico name="alert" size={14} style={{ verticalAlign: -2, color: "var(--c2)", marginRight: 4 }} />{[untypedProducts.length > 0 ? `Choose what kind of thing ${untypedProducts.length === 1 ? "this product is" : `these ${untypedProducts.length} products are`}` : "", unconfirmed > 0 ? `${unconfirmed} spot${unconfirmed === 1 ? "" : "s"} to double-check + confirm (the amber ones)` : "", missingUnitsHint(model.products)].filter(Boolean).join(" · ")} before you can continue. <button onClick={() => document.querySelector(".ocp-attention")?.scrollIntoView({ behavior: "smooth", block: "center" })} style={{ background: "none", border: "none", color: "var(--c2)", fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontSize: 12.5, padding: 0 }}>Take me to the next one →</button></>
                  : <>Everything grouped the way you sell it? Then you're one click from costs.</>}
          </div>
          <button
            className="ocp-cta"
            style={{
              padding: "13px 40px",
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "inherit",
              color: "var(--accent-contrast)",
              background: canContinue
                ? "linear-gradient(180deg, color-mix(in srgb, var(--accent) 88%, white 12%), var(--accent))"
                : "var(--accent)",
              border: "none",
              borderRadius: 12,
              cursor: canContinue ? "pointer" : "not-allowed",
              boxShadow: canContinue ? "0 6px 18px color-mix(in srgb, var(--accent) 35%, transparent)" : "none",
              opacity: canContinue ? 1 : 0.45,
              whiteSpace: "nowrap",
            }}
            onClick={() => { if (canContinue) onContinue(); }}
            disabled={!canContinue}
            title={canContinue ? "" : savePending ? "Saving your changes" : saveError ?? "Finish each highlighted item first"}
          >Looks good — set up costs →</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ costs ----------------------------- */
// What KIND OF THING a product is — drives which cost questions even apply.
// Founder 2026-06-23: capture this per group, then ask only that type's costs
// (physical gets shipping/pick-pack; the others never do).
type ProductType = "physical" | "software" | "info" | "service";
const PRODUCT_TYPES: Array<{ type: ProductType; label: string; physical: boolean }> = [
  { type: "physical", label: "Physical (you ship it)", physical: true },
  { type: "software", label: "Software (app, download, license)", physical: false },
  { type: "info", label: "Info (course, newsletter, ebook, membership)", physical: false },
  { type: "service", label: "Service (coaching, program, done-for-you)", physical: false },
];
const TYPE_ORDER: Record<ProductType, number> = { physical: 0, software: 1, info: 2, service: 3 };
// Normalize a group NAME for matching the type across steps. Categorize (sandbox)
// uses synthetic "preview-<slug>" ids while the Costs step loads real DB UUIDs, so
// the Categorize-set type is carried into Costs by name, not id.
const typeKeyOf = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
// Best guess at a group's type from its name, so the merchant mostly confirms
// instead of cold-picking — and so we can process products in type batches.
function guessType(name: string): ProductType {
  const n = name.toLowerCase();
  // NOTE: "guide"/"report" deliberately excluded — they collide with physical
  // supplement/CPG names ("Sleep Guide Kit", "Magnesium Report") and a wrong "info"
  // guess hides ship & pack. Bias ambiguous tokens toward physical.
  if (/\b(newsletter|members?'?s?|membership|subscription|course|e-?book|ebook|magazine|webinar|masterclass)\b/.test(n)) return "info";
  if (/\b(program|coaching|coach|consult\w*|service|session|done.?for.?you|mastermind|workshop|retreat|1.?on.?1)\b/.test(n)) return "service";
  if (/\b(software|app|saas|license|licen[sc]e|plugin|api|dashboard|platform)\b/.test(n)) return "software";
  return "physical";
}






// ─────────────────────────────────────────────────────────────────────────────
// CostsChat — the costs conversation driven by the REAL AI turn engine
// (POST /onboarding/chat/message). Replaces the old hard-coded script below: the
// engine batches look-alike products, handles corrections/questions/protests, and
// returns a live per-SKU margin sheet. Founder 2026-07-01.
// ─────────────────────────────────────────────────────────────────────────────
interface EngineSheetSku { skuId: string; skuCode: string; label: string; unitsPerSale: number; price: number; costPerUnit?: number | null; cogs: number; fulfillment: number; processing: number; returns: number; extras: number; cm: number; cmPct: number | null }
interface EngineSheetGroup { id: string; name: string; unit: string; costPerUnit: number | null; refundRatePct: number; costsComplete: boolean; skipped: boolean; skus: EngineSheetSku[] }
interface EngineProcessing { basePct: number | null; fixed: number | null; gatewayMonthly: number | null; processors?: Array<{ name: string; pct: number; fixed: number; sharePct: number }> }
interface EngineTurn { assistantMessage: string; done: boolean; sheet: EngineSheetGroup[]; unitLabel: string; currency?: string; committed?: number; commitFailed?: boolean; blocked?: boolean; openOverheadReview?: boolean; questionId?: string | null; promptRevision?: string }
interface SavedCostFile { id: string; displayName: string; purpose: string; status: string }
// editable = the server holds a rewind point for this user message ("edit an
// answer and everything re-runs from there", founder 2026-07-02).
interface EngineChatMsg { role: "user" | "assistant"; content: string; editable?: boolean; requestId?: string }
interface EngineChatState { transcript: EngineChatMsg[]; sheet: EngineSheetGroup[]; unitLabel: string; currency?: string; done: boolean; started: boolean; hasCatalog: boolean; requiresCatalogReview?: boolean; processing?: EngineProcessing; openOverheadReview?: boolean; commitFailed?: boolean; reviewedGroupIds?: string[]; reviewFingerprints?: Record<string, string>; questionId?: string | null; promptRevision?: string }

// Exported: the Costs page (/costs) embeds this same chat + live sheet as the
// permanent home for updating product costs after onboarding — same saved
// conversation, same engine, no second editor to drift. `home` hides the
// onboarding-flow chrome (back-to-catalog, start-over) that makes no sense there.
// Module-scoped so it survives remounts: last time the chat booted, per account
// and mode.
// Guards against a remount-storm hammering the chat endpoints (see the effect).
const chatBootAt = new Map<string, number>();

const newChatRequestId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export function CostsChat({ mode, accountId, catalog, fresh, resumeAfterOverhead, fromGuided = false, pendingUploads, onUploadsConsumed, onConsumedFresh, onConsumedOverheadResume, onDone, onBack, onBusyChange, home, prefill, doneCtaLabel }: { mode: string; accountId?: string; catalog?: unknown; fresh?: boolean; resumeAfterOverhead?: boolean; fromGuided?: boolean; pendingUploads?: File[]; onUploadsConsumed?: () => void; onConsumedFresh?: () => void; onConsumedOverheadResume?: () => void; onDone: () => void; onBack: () => void; onBusyChange?: (busy: boolean) => void; home?: boolean; prefill?: { text: string; nonce: number } | null; doneCtaLabel?: string }): React.ReactElement {
  const api = useApiClient();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const bootAccountQ = useQuery({
    queryKey: ["account"],
    enabled: !accountId,
    queryFn: () => api.get<{ account: { id: string } }>("/account"),
  });
  const bootAccountId = accountId || bootAccountQ.data?.account.id || "";
  const bootKey = bootAccountId ? `${bootAccountId}:${mode}` : "";
  const savedFilesQ = useQuery({
    queryKey: ["onboarding", "journey", "costs-files", bootAccountId],
    enabled: mode === "real" && !home && Boolean(bootAccountId),
    queryFn: () => api.get<{ files: SavedCostFile[] }>("/onboarding/journey"),
  });
  const [msgs, setMsgs] = useState<EngineChatMsg[]>([]);
  // Inline edit of an earlier answer: ordinal = the nth USER message (assistant
  // bubbles don't count, so a never-persisted apology bubble can't skew it).
  const [editing, setEditing] = useState<{ ordinal: number; text: string } | null>(null);
  const [sheet, setSheet] = useState<EngineSheetGroup[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(true);
  const [done, setDone] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [resumed, setResumed] = useState(false);
  // On the Costs page (home), the finished onboarding conversation loads as
  // history — tuck it behind a "previous conversation" toggle so the page opens
  // to a clean, always-ready chat (founder 2026-07-06: "just like any normal
  // chatbot"). historyLen = messages that predate this session; only collapsed
  // when home. Onboarding mode is untouched (you want the full thread there).
  const [historyLen, setHistoryLen] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [commitFailed, setCommitFailed] = useState(false);
  const [failedSend, setFailedSend] = useState<{ text: string; requestId: string; reason: "network" | "conflict" } | null>(null);

  useEffect(() => {
    onBusyChange?.(busy || failedSend !== null);
    return () => onBusyChange?.(false);
  }, [busy, failedSend, onBusyChange]);
  const [startupError, setStartupError] = useState<string | null>(null);
  const [bootRetry, setBootRetry] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Answer box stays ~2 lines tall (founder 2026-07-05: "it gets really big as
  // I use it") — the toggle beside it switches to a taller box when someone's
  // typing a list. Height snaps back to small after every send.
  const [inputTall, setInputTall] = useState(false);
  const inputCap = inputTall ? 180 : 64;
  // How many leading (onboarding-history) messages to tuck behind "Previous
  // conversation" on the Costs page — only for a FINISHED conversation, clamped
  // to the live length so a rewind/start-over can never hide current messages.
  const histEnd = home && done ? Math.min(historyLen, msgs.length) : 0;
  const taRef = useRef<HTMLTextAreaElement>(null);
  const resetInputHeight = (): void => { const el = taRef.current; if (el) { el.style.height = "auto"; } };
  const scroller = useRef<HTMLDivElement>(null);
  // Resizable fit-width columns for the live sheet (name fits the longest real
  // label, numeric columns split the rest evenly, every boundary draggable).
  const sheetBodyRef = useRef<HTMLDivElement>(null);
  const skuLabels = useMemo(() => sheet.flatMap((g) => g.skus.map((s) => s.label)), [sheet]);
  const engineCols = useColWidths("ccd.onboarding.cols.engine.v1", sheetBodyRef, skuLabels, 7, "minmax(150px, 2.4fr) repeat(7, minmax(64px, 1fr))");
  // The organized catalog rides along on the FIRST post of this mount (sandbox
  // bridge) — after that the server keeps its own copy with the conversation.
  const catalogSent = useRef(false);
  const uploadsDrained = useRef(false);
  const savedReportsDrained = useRef(false);
  const [failedSavedReports, setFailedSavedReports] = useState<SavedCostFile[]>([]);
  const promptExpectation = useRef<{ questionId: string | null; promptRevision: string } | null>(null);

  const rememberPromptExpectation = (response: { questionId?: string | null; promptRevision?: string }): void => {
    if (typeof response.promptRevision !== "string" || !("questionId" in response)) return;
    promptExpectation.current = {
      questionId: response.questionId ?? null,
      promptRevision: response.promptRevision,
    };
  };

  const fmt = useMemo(() => {
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 2 }); }
    catch { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }); }
  }, [currency]);

  const firstPostBody = (message: string, extra: Record<string, unknown> = {}): Record<string, unknown> => {
    const body: Record<string, unknown> = { message, mode, ...extra };
    if (!catalogSent.current && catalog) { body.catalog = catalog; catalogSent.current = true; }
    return body;
  };

  const applyTurn = (r: EngineTurn): void => {
    rememberPromptExpectation(r);
    if (r.openOverheadReview) {
      const returnTo = home ? "/costs" : productSetupPath("costs", fromGuided);
      navigate(`/costs/overhead?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }
    setSheet(r.sheet);
    setDone(r.done);
    if (r.currency) setCurrency(r.currency);
    setBlocked(r.blocked === true);
    if (r.commitFailed) setCommitFailed(true);
    else if (r.done) setCommitFailed(false);
    // A commit changed cm_inputs — the margins on every other page are now
    // stale. Refetch the data queries (shell/gate spared) so "your margins and
    // profit update everywhere" is true the moment the chat says it saved.
    if ((r.committed ?? 0) > 0) {
      relockFinancialQueries(queryClient);
      void invalidateCatalogQueries(queryClient);
      // Also refresh the setup gate + readiness (deliberately spared by
      // invalidateCatalogQueries): costing a newly-added product can clear the
      // amber "new product needs review" box, and it must go away LIVE from BOTH
      // the setup costs stage and the /costs "Fix it" path (founder 2026-07-12).
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
      void queryClient.invalidateQueries({ queryKey: ["cost-readiness"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "status"] });
    }
  };

  const reloadConversationState = async (keepSessionHistoryBoundary = false): Promise<EngineChatState> => {
    const state = await api.get<EngineChatState>("/onboarding/chat/state", { mode });
    rememberPromptExpectation(state);
    if (state.openOverheadReview) {
      const returnTo = home ? "/costs" : productSetupPath("costs", fromGuided);
      navigate(`/costs/overhead?returnTo=${encodeURIComponent(returnTo)}`);
      return state;
    }
    setMsgs(state.transcript);
    setSheet(state.sheet);
    setDone(state.done);
    setCommitFailed(mode === "real" && state.commitFailed === true);
    setBlocked(!state.hasCatalog || state.requiresCatalogReview === true);
    setResumed(state.transcript.length > 0);
    if (keepSessionHistoryBoundary) setHistoryLen((current) => Math.min(current, state.transcript.length));
    else setHistoryLen(state.transcript.length);
    if (state.currency) setCurrency(state.currency);
    return state;
  };

  // The Costs page's "needs your attention" strip drops a ready-made ask into
  // the answer box ("Let's set costs for X") — the founder just hits send.
  useEffect(() => {
    if (!prefill || !prefill.text) return;
    setInput(prefill.text);
    taRef.current?.focus();
  }, [prefill?.nonce]);

  const openConversation = async (): Promise<void> => {
    const r = await api.post<EngineTurn>("/onboarding/chat/message", firstPostBody(""));
    setMsgs([{ role: "assistant", content: r.assistantMessage }]);
    applyTurn(r);
  };

  // Open: ?session=fresh wipes first (founder test control); else resume saved
  // history with a "picking up where you left off" note, else ask for the opener.
  useEffect(() => {
    if (!bootKey) return;
    // CIRCUIT BREAKER (2026-07-06 prod incident): if some parent re-mounts this
    // chat in a tight loop (an ErrorBoundary retrying after a render throw, seen
    // on a freshly-reset test account), the mount effect would fire GET /state +
    // POST /onboarding/chat/message hundreds of times a second — blowing the
    // per-account LLM limit AND the per-IP flood limit, so the WHOLE app 429s.
    // Debounce the boot per account + mode so a remount storm can never reach
    // the network. A fast remount WAITS; it never opens an empty chat.
    const delayMs = Math.max(0, 2500 - (Date.now() - (chatBootAt.get(bootKey) ?? 0)));
    setBusy(true);
    setStartupError(null);
    // Account switches can reuse this mounted component. Clear the previous
    // account immediately so no old conversation is visible during the wait.
    setMsgs([]);
    setSheet([]);
    setDone(false);
    setBlocked(false);
    setResumed(false);
    setHistoryLen(0);
    catalogSent.current = false;
    uploadsDrained.current = false;
    savedReportsDrained.current = false;
    setFailedSavedReports([]);
    promptExpectation.current = null;
    let alive = true;
    let bootTimer: ReturnType<typeof setTimeout> | undefined;
    const bootChat = async (): Promise<void> => {
      chatBootAt.set(bootKey, Date.now());
      try {
        if (fresh) {
          await api.post("/onboarding/chat/reset", { mode });
          if (mode === "real") relockFinancialQueries(queryClient);
          onConsumedFresh?.();
          if (!alive) return;
          await openConversation();
          return;
        }
        const s = await api.get<EngineChatState>("/onboarding/chat/state", { mode });
        if (!alive) return;
        rememberPromptExpectation(s);
        setBlocked(!s.hasCatalog || s.requiresCatalogReview === true);
        if (s.openOverheadReview) {
          const returnTo = home ? "/costs" : productSetupPath("costs", fromGuided);
          navigate(`/costs/overhead?returnTo=${encodeURIComponent(returnTo)}`);
          return;
        }
        if (s.transcript.length > 0) {
          setMsgs(s.transcript); setSheet(s.sheet); setDone(s.done); setResumed(true);
          setCommitFailed(mode === "real" && s.commitFailed === true);
          setHistoryLen(s.transcript.length); // everything loaded here predates this session
          if (s.currency) setCurrency(s.currency);
          if (resumeAfterOverhead) {
            const r = await api.post<EngineTurn>("/onboarding/chat/message", firstPostBody("", { resumeAfterOverhead: true }));
            if (!alive) return;
            if (!r.openOverheadReview) {
              setMsgs([...s.transcript, { role: "assistant", content: r.assistantMessage }]);
              applyTurn(r);
              onConsumedOverheadResume?.();
            } else {
              applyTurn(r);
            }
            return;
          }
          // A saved conversation resumes against the CURRENT catalog: hand the
          // fresh organized copy over so renames/exclusions since last visit stick.
          if (catalog) {
            const r = await api.post<EngineTurn>("/onboarding/chat/message", firstPostBody(""));
            if (!alive) return;
            applyTurn(r);
            if (!r.openOverheadReview) await reloadConversationState();
          }
        } else {
          if (resumeAfterOverhead) {
            const r = await api.post<EngineTurn>("/onboarding/chat/message", firstPostBody("", { resumeAfterOverhead: true }));
            if (!alive) return;
            setMsgs([{ role: "assistant", content: r.assistantMessage }]);
            applyTurn(r);
            if (!r.openOverheadReview) onConsumedOverheadResume?.();
          } else {
            await openConversation();
          }
        }
      } catch {
        if (alive) setStartupError("I couldn't open your costs conversation. Your saved answers are safe.");
      } finally {
        if (alive) setBusy(false);
      }
    };
    if (delayMs > 0) bootTimer = setTimeout(() => { void bootChat(); }, delayMs);
    else void bootChat();
    return () => { alive = false; if (bootTimer) clearTimeout(bootTimer); };
  }, [bootKey, bootRetry]);

  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [msgs, busy]);

  const send = async (text: string, requestId = newChatRequestId()): Promise<void> => {
    const t = text.trim();
    if (!t || busy || blocked || (failedSend?.reason === "network" && requestId !== failedSend.requestId)) return;
    setFailedSend(null);
    setMsgs((m) => [...m, { role: "user", content: t }]);
    setBusy(true);
    try {
      const expected = promptExpectation.current;
      const r = await api.post<EngineTurn>("/onboarding/chat/message", firstPostBody(t, {
        requestId,
        ...onboardingPromptGuard(expected),
      }));
      // The turn persisted: the user message is now a rewind point (editable),
      // and the reply lands after it.
      setMsgs((m) => {
        const n = [...m];
        for (let i = n.length - 1; i >= 0; i--) { if (n[i]!.role === "user") { n[i] = { ...n[i]!, editable: true }; break; } }
        n.push({ role: "assistant", content: r.assistantMessage });
        return n;
      });
      applyTurn(r);
    } catch (error) {
      // The response did not arrive, so we cannot assume whether the server saw
      // it. Pull the unconfirmed bubble and keep the same request id for a safe
      // retry. A 409 first reloads the authoritative latest conversation.
      if (isOnboardingConversationConflict(error)) {
        try {
          await reloadConversationState();
          setInput(t);
          setFailedSend({ text: t, requestId, reason: "conflict" });
        } catch {
          setMsgs((m) => m.slice(0, -1));
          setFailedSend({ text: t, requestId, reason: "network" });
        }
      } else {
        setMsgs((m) => m.slice(0, -1));
        setFailedSend({ text: t, requestId, reason: "network" });
      }
    } finally {
      setBusy(false);
    }
  };

  const rewind = async (): Promise<void> => {
    if (!editing || busy) return;
    const t = editing.text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const original = msgs.filter((message) => message.role === "user")[editing.ordinal];
      const r = await api.post<EngineTurn & { transcript: EngineChatMsg[] }>("/onboarding/chat/rewind", {
        mode,
        userOrdinal: editing.ordinal,
        message: t,
        ...(original?.requestId ? { expectedRequestId: original.requestId } : { expectedContent: original?.content ?? "" }),
      });
      setMsgs(r.transcript); // authoritative: everything after the edit is gone
      setHistoryLen(0); setShowHistory(false); // the resumed-history boundary is void once they edit
      setEditing(null);
      applyTurn(r);
    } catch (e) {
      if (isOnboardingConversationConflict(e)) {
        try { await reloadConversationState(); } catch { /* retry below remains available */ }
        setEditing(null);
      }
      const detail = (e as { detail?: { error?: string } })?.detail?.error;
      toast.error(detail ?? "Couldn't edit that answer — try again.");
    } finally {
      setBusy(false);
    }
  };

  const startOver = async (): Promise<void> => {
    if (busy) return;
    if (!window.confirm("Start the costs conversation over? Your answers here will be cleared.")) return;
    setBusy(true);
    try {
      await api.post("/onboarding/chat/reset", { mode });
      if (mode === "real") relockFinancialQueries(queryClient);
      try { window.sessionStorage.removeItem(`ccd.onboarding.reviewed.${mode}`); } catch { /* non-fatal */ }
      catalogSent.current = false;
      setMsgs([]); setDone(false); setResumed(false); setCommitFailed(false); setFailedSend(null);
      setHistoryLen(0); setShowHistory(false); // fresh conversation — no previous-history boundary
      await openConversation();
    } catch {
      toast.error("Couldn't reset — try again.");
    } finally {
      setBusy(false);
    }
  };

  const fileRef = useRef<HTMLInputElement>(null);
  const uploadReport = async (f: File): Promise<void> => {
    const requestId = newChatRequestId();
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: `(uploaded cost file: ${f.name})`, requestId }]);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("mode", mode);
      form.append("requestId", requestId);
      await api.postForm<EngineTurn>("/onboarding/chat/shipping-report", form);
      if (mode === "real") relockFinancialQueries(queryClient);
      // Use the server transcript after every upload. If the response was lost
      // and this is a safe replay, the optimistic bubble disappears instead of
      // showing the same file twice until the next refresh.
      await reloadConversationState(true);
    } catch (e) {
      // The server may have saved the file even when its response never reached
      // the browser. Reload first and trust the exact persisted request id.
      let recoveryCheckFailed = false;
      try {
        const authoritative = await reloadConversationState(true);
        if (onboardingRequestWasSaved(authoritative.transcript, requestId)) return;
      } catch {
        // If both requests fail, the browser cannot safely claim it was applied.
        recoveryCheckFailed = true;
      }
      const detail = (e as { detail?: { error?: string } })?.detail?.error;
      const msg = detail ?? (e as { message?: string })?.message ?? "";
      // Drop only this upload's optimistic bubble. The authoritative reload may
      // already have replaced the rest of the conversation while we checked.
      setMsgs((m) => [...m.filter((message) => message.requestId !== requestId), { role: "assistant", content: recoveryCheckFailed
        ? "I couldn't confirm whether that file finished. Choose the same file again. It won't be added twice if it already saved."
        : msg.includes("SKU") || msg.includes("spreadsheet") || msg.includes("file")
          ? msg
          : "I couldn't read that file yet. Try an Excel or CSV export with product names and the costs you know. You can also keep going and tell me the missing numbers here." }]);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Files attached back on the Upload step: once the conversation has opened
  // (busy clears) and the catalog exists, submit them one by one — same path as
  // the in-chat Upload button. Runs exactly once (ref guard), then clears the
  // page-level queue so a remount can't re-post them.
  useEffect(() => {
    if (busy || uploadsDrained.current || !pendingUploads?.length) return;
    uploadsDrained.current = true;
    void (async () => {
      for (const f of pendingUploads) { await uploadReport(f); }
      onUploadsConsumed?.();
    })();
  }, [busy, pendingUploads]);

  const applySavedReports = async (saved: SavedCostFile[]): Promise<void> => {
    if (saved.length === 0) return;
    setBusy(true);
    const failed: SavedCostFile[] = [];
    try {
      for (const file of saved) {
        const requestId = newChatRequestId();
        const uploadLabel = `(using saved cost file: ${file.displayName})`;
        setMsgs((messages) => [...messages, { role: "user", content: uploadLabel, requestId }]);
        try {
          await retryOnboardingImportOnce(() => api.post<EngineTurn>("/onboarding/chat/saved-report", {
            fileId: file.id,
            mode,
            requestId,
          }));
          if (mode === "real") relockFinancialQueries(queryClient);
          await reloadConversationState(true);
        } catch (error) {
          // Both responses may have been lost after the server saved the file.
          // Reload before showing an error and trust the exact persisted id.
          try {
            const authoritative = await reloadConversationState(true);
            if (onboardingRequestWasSaved(authoritative.transcript, requestId)) continue;
          } catch {
            // The clear retry message below is safer than claiming success.
          }
          const detail = (error as { detail?: { error?: string } })?.detail?.error;
          failed.push(file);
          setMsgs((messages) => [
            ...messages.filter((message) => message.requestId !== requestId),
            { role: "user", content: uploadLabel, requestId },
            { role: "assistant", content: `I couldn't confirm ${file.displayName} yet. ${detail ?? "Your saved file is still here."}` },
          ]);
        }
      }
      setFailedSavedReports(failed);
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "journey"] });
    }
  };

  // Cost and shipping files collected in the earlier universal data step are
  // applied here once the Product → Variant → SKU catalog is ready. Submit all
  // saved cost files, including ones used before: the server keys each result to
  // the current catalog, so unchanged files replay safely while a regroup gets a
  // fresh match. The client never has to find and upload the same file twice.
  useEffect(() => {
    if (busy || blocked || savedReportsDrained.current || !savedFilesQ.isSuccess) return;
    const saved = (savedFilesQ.data?.files ?? []).filter((file) => file.purpose === "costs");
    savedReportsDrained.current = true;
    if (saved.length === 0) return;
    void applySavedReports(saved);
  }, [blocked, busy, savedFilesQ.isSuccess]);

  const retryCommit = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.post("/onboarding/chat/commit", {});
      relockFinancialQueries(queryClient);
      setCommitFailed(false);
      void invalidateCatalogQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
      void queryClient.invalidateQueries({ queryKey: ["cost-readiness"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "status"] });
      toast.success("Costs saved to your dashboard.");
    } catch {
      toast.error("Still couldn't save — your answers are safe; try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  const costable = sheet.filter((g) => !g.skipped);
  const costedCount = costable.filter((g) => g.costsComplete).length;
  // Example product for helper copy — THEIR catalog, never a hardcoded name
  // (a "Biotin" example leaked another client's product; founder 2026-07-02).
  const exampleName = costable[0]?.name ?? "your first product";
  const primaryBtn: React.CSSProperties = { padding: "0 16px", height: 40, borderRadius: 8, border: "1px solid var(--accent)", background: "var(--accent)", color: "var(--accent-contrast)", fontWeight: 600, cursor: "pointer" };
  const linkBtn: React.CSSProperties = { background: "none", border: "none", color: "var(--body)", cursor: "pointer", fontSize: 12.5, textDecoration: "underline", padding: 0 };
  if (!accountId && bootAccountQ.isError) return (
    <div style={card}>
      <div style={{ color: "var(--bad)", fontWeight: 600 }}>I couldn't open this account's costs. Your saved answers are safe.</div>
      <button style={{ ...primaryBtn, marginTop: 12 }} onClick={() => { void bootAccountQ.refetch(); }}>Try again</button>
    </div>
  );
  if (startupError) return (
    <div style={card}>
      <div style={{ color: "var(--bad)", fontWeight: 600 }}>{startupError}</div>
      <button style={{ ...primaryBtn, marginTop: 12 }} onClick={() => setBootRetry((n) => n + 1)}>Try again</button>
    </div>
  );
  if (mode === "real" && !home && savedFilesQ.isError) return (
    <div style={card}>
      <div style={{ color: "var(--bad)", fontWeight: 600 }}>I couldn't check the cost files you already uploaded. I won't skip them or make you start by hand.</div>
      <button style={{ ...primaryBtn, marginTop: 12 }} onClick={() => { void savedFilesQ.refetch(); }}>Try again</button>
    </div>
  );
  return (
    <div>
      <ChatDock heightKey="ccd.onboarding.chatH" defaultH={560}>
        {(chatH) => <>
        {CHAT_STYLE}
        {resumed && !done && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "var(--body)", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 11px", marginBottom: 8 }}>
            <span>Picking up where you left off — {costedCount} of {costable.length} products costed.</span>
            <button disabled={busy} onClick={() => void startOver()} style={{ ...linkBtn, opacity: busy ? 0.55 : 1 }}>Start over</button>
          </div>
        )}
        <div ref={scroller} data-chat-thread style={{ display: "flex", flexDirection: "column", height: chatH, overflowY: "auto", padding: "4px 2px" }}>
          {histEnd > 0 && (
            <button type="button" onClick={() => setShowHistory((v) => !v)}
              style={{ alignSelf: "center", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "var(--body)", padding: "4px 10px", marginBottom: showHistory ? 8 : 4 }}>
              {showHistory ? "▾ Hide previous conversation" : "▸ Previous conversation"}
            </button>
          )}
          {msgs.map((m, i) => {
            // On the Costs page, keep the FINISHED onboarding history tucked away
            // until asked (histEnd is 0 unless done — computed in the body).
            if (!showHistory && i < histEnd) return null;
            if (m.role !== "user") return <div key={i} className="ocp-msg" style={{ ...bubble, ...asstB }}>{m.content}</div>;
            const ordinal = msgs.slice(0, i).filter((x) => x.role === "user").length;
            if (editing && editing.ordinal === ordinal) {
              return (
                <div key={i} style={{ alignSelf: "flex-end", width: "min(440px, 92%)", marginBottom: 9 }}>
                  <textarea value={editing.text} autoFocus rows={2}
                    onChange={(e) => setEditing({ ordinal, text: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void rewind(); } if (e.key === "Escape") setEditing(null); }}
                    style={{ width: "100%", boxSizing: "border-box", resize: "none", padding: "8px 10px", lineHeight: 1.4, ...inputStyle }} />
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", marginTop: 4 }}>
                    <span style={{ fontSize: 10.5, color: "var(--faint)", marginRight: "auto" }}>The chat re-runs from here with your new answer.</span>
                    <button onClick={() => setEditing(null)} style={ghostBtnSm}>Cancel</button>
                    <button onClick={() => void rewind()} disabled={!editing.text.trim()} style={{ ...ghostBtnSm, background: "var(--accent)", color: "var(--accent-contrast)", border: "1px solid var(--accent)", fontWeight: 600 }}>Save &amp; re-run</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={i} className="ocp-msg" style={{ display: "flex", alignItems: "center", gap: 6, alignSelf: "flex-end", maxWidth: "88%", marginBottom: 9 }}>
                {m.editable && !busy && !editing && (
                  <button onClick={() => setEditing({ ordinal, text: m.content })} title="Edit this answer — the chat re-runs from here"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--faint)", fontSize: 13, padding: 2, lineHeight: 1 }}>✎</button>
                )}
                <div style={{ ...bubble, ...userB, margin: 0, maxWidth: "100%", alignSelf: "auto" }}>{m.content}</div>
              </div>
            );
          })}
          {busy && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", alignSelf: "flex-start", marginBottom: 9 }}>
              <span style={asstAvatar}><SparkleIcon /></span>
              <div style={{ ...bubble, ...asstB, marginBottom: 0, color: "var(--faint)" }}>thinking <TypingDots /></div>
            </div>
          )}
        </div>
        {failedSavedReports.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--bad) 30%, var(--border))", borderRadius: 8, padding: "7px 11px", marginTop: 8 }}>
            <span>{failedSavedReports.length === 1 ? "One saved cost file still needs to be checked." : `${failedSavedReports.length} saved cost files still need to be checked.`}</span>
            <button
              disabled={busy}
              onClick={() => { void applySavedReports(failedSavedReports); }}
              style={{ ...linkBtn, color: "var(--bad)", opacity: busy ? 0.55 : 1 }}
            >
              Try {failedSavedReports.length === 1 ? "file" : "files"} again
            </button>
          </div>
        )}
        {failedSend !== null && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--bad) 30%, var(--border))", borderRadius: 8, padding: "7px 11px", marginTop: 8 }}>
            <span>{failedSend.reason === "conflict" ? "This conversation changed in another tab. I loaded the latest question and put your old answer back in the box. Check that it still answers the question before sending it." : "I couldn't confirm that message saved. Your answer is ready to retry safely."}</span>
            {failedSend.reason === "conflict"
              ? <button onClick={() => { setFailedSend(null); taRef.current?.focus(); }} style={{ ...linkBtn, color: "var(--bad)" }}>Review answer</button>
              : <button onClick={() => { const failed = failedSend; setFailedSend(null); void send(failed.text, failed.requestId); }} style={{ ...linkBtn, color: "var(--bad)" }}>Send again</button>}
          </div>
        )}
        {blocked && (
          <div style={{ marginTop: 12, padding: "12px 14px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 13.5 }}>
            <div style={{ marginBottom: 8 }}>Your catalog needs organizing before we can talk costs.</div>
            <button onClick={onBack} style={primaryBtn}>← Back to Categorize</button>
          </div>
        )}
        {done && !commitFailed && (
          <div style={{ marginTop: 12, padding: "12px 14px", background: "color-mix(in srgb, var(--good) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--good) 35%, var(--border))", borderRadius: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>All set — your costs are in.</div>
            <div style={{ fontSize: 12.5, color: "var(--body)", marginBottom: 10 }}>Spot a wrong number? Just tell me below ("actually the {exampleName} is $7") and I'll fix it.</div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button
                onClick={onDone}
                disabled={busy || failedSend !== null}
                style={{ ...primaryBtn, opacity: busy || failedSend !== null ? 0.55 : 1, cursor: busy || failedSend !== null ? "wait" : "pointer" }}
              >
                {busy ? "Saving your change…" : (doneCtaLabel ?? "See your margins →")}
              </button>
              {!home && <button disabled={busy || failedSend !== null} onClick={() => void startOver()} style={{ ...linkBtn, opacity: busy || failedSend !== null ? 0.55 : 1 }}>Start over</button>}
            </div>
          </div>
        )}
        {commitFailed && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--bad) 30%, var(--border))", borderRadius: 8, padding: "7px 11px", marginTop: 8 }}>
            <span>Your answers are safe, but they didn't save to the dashboard yet.</span>
            <button disabled={busy} onClick={() => void retryCommit()} style={{ ...linkBtn, color: "var(--bad)", opacity: busy ? 0.55 : 1 }}>Retry save</button>
          </div>
        )}
        {!blocked && (
          <>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input ref={fileRef} type="file" multiple accept=".csv,.tsv,.txt,.xls,.xlsx,.pdf,.png,.jpg,.jpeg,.webp,.gif,text/csv,application/pdf,image/*,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: "none" }}
              onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.target.value = ""; if (fs.length) void (async () => { for (const f of fs) await uploadReport(f); })(); }} />
            <button onClick={() => fileRef.current?.click()} disabled={busy || failedSend?.reason === "network"} title="Upload one or more cost sheets, shipping reports, or supplier invoices. Your general KPI and tracking sheets were collected earlier." style={{ height: 40, padding: "0 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)", cursor: "pointer", fontSize: 12.5, whiteSpace: "nowrap", opacity: busy || failedSend?.reason === "network" ? 0.5 : 1 }}>
              <PaperclipIcon />Upload cost or shipping file
            </button>
            <textarea ref={taRef} value={input} onChange={(e) => setInput(e.target.value)} rows={1}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter makes a line break (for list answers).
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (input.trim() && !busy) { const t = input.trim(); setInput(""); resetInputHeight(); void send(t); }
                }
              }}
              onInput={(e) => { const el = e.currentTarget; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, inputCap)}px`; }}
              placeholder={failedSend?.reason === "network" ? "Use Send again above before adding another answer" : done ? `Change anything, for example: “make the ${exampleName} $7”` : "Type your answer. Shift+Enter starts a new line…"} disabled={busy || failedSend?.reason === "network"}
              // data-gramm & friends: keep Grammarly-style browser extensions
              // from injecting their own floating icon inside the answer box
              // (founder 2026-07-02: "weird symbol thing where I type answers" —
              // nothing in our code renders there).
              data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false"
              style={{ flex: 1, minHeight: 40, maxHeight: inputCap, boxSizing: "border-box", resize: "none", overflowY: "auto", paddingTop: 10, paddingBottom: 10, lineHeight: 1.4, ...inputStyle }} />
            <button type="button"
              onClick={() => {
                setInputTall((t) => {
                  const next = !t;
                  // Re-fit the box to its content under the new cap right away.
                  requestAnimationFrame(() => { const el = taRef.current; if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, next ? 180 : 64)}px`; } });
                  return next;
                });
              }}
              disabled={busy || failedSend?.reason === "network"}
              title={inputTall ? "Make the answer box smaller" : "Make the answer box bigger"}
              aria-label={inputTall ? "Make the answer box smaller" : "Make the answer box bigger"}
              style={{ alignSelf: "stretch", width: 26, border: "1px solid var(--border)", background: "var(--surface-2)", borderRadius: 8, cursor: "pointer", color: "var(--faint)", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
              <Ico name="chevronRight" size={13} style={{ transform: inputTall ? "rotate(90deg)" : "rotate(-90deg)" }} />
            </button>
            <SendButton onClick={() => { const t = input.trim(); if (t) { setInput(""); resetInputHeight(); void send(t); } }} disabled={busy || failedSend?.reason === "network" || !input.trim()} />
          </div>
          {!done && <div style={{ marginTop: 7, color: "var(--faint)", fontSize: 11.5, lineHeight: 1.45 }}>Not sure? Say “I don’t know.” I’ll suggest a clearly labeled starting estimate when there is a safe one. You approve it first, and you can change it later in Costs or Settings.</div>}
          </>
        )}
        {!home && <button disabled={busy} onClick={onBack} style={{ marginTop: 12, background: "none", border: "none", color: "var(--body)", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.55 : 1, fontSize: 12.5 }}>← Back to catalog</button>}
        </>}
      </ChatDock>

      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Your costs &amp; margins</div>
          {sheet.length > 0 && <div style={{ fontSize: 11.5, color: costedCount === costable.length && costable.length > 0 ? "var(--good)" : "var(--faint)" }} className="dd-num">{costedCount} of {costable.length} products costed</div>}
        </div>
        {sheet.length === 0 && <div style={{ fontSize: 12.5, color: "var(--faint)", padding: "8px 0" }}>Your numbers fill in here as we talk.</div>}
        <div ref={sheetBodyRef} style={{ overflowX: "auto" }}>
        {sheet.map((g) => {
          const isCollapsed = collapsed[g.id] === true;
          const showAll = expanded[g.id] === true;
          const visible = showAll || g.skus.length <= 5 ? g.skus : g.skus.slice(0, 5);
          const anyRefund = g.refundRatePct > 0;
          return (
            <div key={g.id} style={{ marginBottom: 14, borderTop: "1px solid var(--hair)", paddingTop: 10, opacity: g.skipped ? 0.5 : 1 }}>
              <div onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !isCollapsed }))} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, fontWeight: 700, alignItems: "center", padding: "2px 0", cursor: "pointer" }} title={isCollapsed ? "Expand" : "Collapse"}>
                <span><Ico name="chevronRight" style={{ marginRight: 5, color: "var(--faint)", transform: isCollapsed ? "none" : "rotate(90deg)", transition: "transform .12s", verticalAlign: -1 }} />{g.name}</span>
                <span style={{ fontSize: 10.5, color: g.skipped ? "var(--faint)" : g.costsComplete ? "var(--good)" : "var(--faint)", whiteSpace: "nowrap" }}>{g.skipped ? "skipped" : g.costsComplete ? "priced" : "needs costs"} · {g.skus.length} code{g.skus.length === 1 ? "" : "s"}</span>
              </div>
              {!g.skipped && !isCollapsed && <>
                <div style={{ display: "grid", gridTemplateColumns: engineCols.template, gap: 8, padding: "6px 0 4px", color: "var(--faint)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.03em", ...engineCols.dividers }}>
                  <ColHead label="Code" i={0} startDrag={engineCols.startDrag} reset={engineCols.reset} />
                  <ColHead label="Price" i={1} startDrag={engineCols.startDrag} reset={engineCols.reset} />
                  <ColHead label="Cost" title="What the whole sale costs you to make or buy — all the units in it" i={2} startDrag={engineCols.startDrag} reset={engineCols.reset} />
                  <ColHead label="Ship & pack" title="Postage, packing, handling — per order of this SKU" i={3} startDrag={engineCols.startDrag} reset={engineCols.reset} />
                  <ColHead label="Merch fees" title="What your card processor takes on each sale" i={4} startDrag={engineCols.startDrag} reset={engineCols.reset} />
                  <ColHead label="Other" title="Product-specific extras (royalty, licensing…)" i={5} startDrag={engineCols.startDrag} reset={engineCols.reset} />
                  <ColHead label="Refund" title="Each product's real refund rate, from its own orders — the $ it shaves off the margin" i={6} startDrag={engineCols.startDrag} reset={engineCols.reset} />
                  <ColHead label="Keep" title="What you keep per sale — the $ over the %" alignRight i={7} startDrag={engineCols.startDrag} reset={engineCols.reset} last />
                </div>
                {visible.map((s) => {
                  const filled = g.costsComplete;
                  const losing = filled && s.cmPct !== null && s.cmPct < 0;
                  const cell = (v: number, always = false): React.ReactElement => (
                    <div style={cellStack}>
                      <span style={{ color: filled || always ? "var(--body)" : "var(--faint)" }}>{filled || always ? fmt.format(v) : "—"}</span>
                      <span style={subPctStyle}>{(filled || always) && s.price > 0 ? `${Math.round((v / s.price) * 100)}%` : ""}</span>
                    </div>
                  );
                  return (
                    <div key={s.skuId} style={{ padding: "7px 6px", margin: "0 -6px", borderTop: "1px solid var(--hair)", ...(losing ? { background: "color-mix(in srgb, var(--bad) 7%, transparent)", borderRadius: 6 } : {}) }}>
                      <div style={{ display: "grid", gridTemplateColumns: engineCols.template, gap: 8, alignItems: "center", fontSize: 12, ...engineCols.dividers }}>
                        <span style={{ color: "var(--body)", minWidth: 0 }}>
                          <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{losing ? "⚠ " : ""}{s.label}</span>
                          <span style={{ color: "var(--faint)", display: "block", fontFamily: "var(--font-mono)", fontSize: 10.5, marginTop: 1 }}>{s.skuCode}</span>
                        </span>
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>{s.price > 0 ? fmt.format(s.price) : "—"}</span>
                        {cell(s.cogs)}
                        {cell(s.fulfillment)}
                        {cell(s.processing)}
                        {cell(s.extras)}
                        {anyRefund ? (
                          <div style={cellStack} title={`${(g.refundRatePct * 100).toFixed(1)}% of ${g.name} orders come back as refunds`}>
                            <span style={{ color: filled && s.returns > 0 ? "var(--body)" : "var(--faint)" }}>{filled && s.returns > 0 ? fmt.format(s.returns) : "—"}</span>
                            <span style={subPctStyle}>{(g.refundRatePct * 100).toFixed(g.refundRatePct * 100 % 1 ? 1 : 0)}%</span>
                          </div>
                        ) : <div style={{ ...cellStack, color: "var(--faint)" }}>—</div>}
                        <div style={{ ...cellStack, alignItems: "flex-end" }}>
                          <span style={{ fontWeight: 700, color: !filled ? "var(--faint)" : s.cm < 0 ? "var(--bad)" : "var(--body)" }}>{filled ? fmt.format(s.cm) : "—"}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: !filled || s.cmPct === null ? "var(--faint)" : s.cmPct >= 0.4 ? "var(--good)" : s.cmPct >= 0.2 ? "var(--c2)" : "var(--bad)" }}>{filled && s.cmPct !== null ? `${Math.round(s.cmPct * 100)}%` : ""}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {g.skus.length > 5 && (
                  <button onClick={() => setExpanded((e) => ({ ...e, [g.id]: !showAll }))} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 11.5, padding: "4px 0" }}>{showAll ? "Show fewer" : `Show all ${g.skus.length} codes`}</button>
                )}
              </>}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}



/* ------------------------------ review ---------------------------- */
function Review({ model, mode, accountId, fromGuided, resetConfirmed, onBack, onCatalogReview, onBusyChange }: { model: Model; mode: string; accountId: string; fromGuided: boolean; resetConfirmed: boolean; onBack: () => void; onCatalogReview: () => void; onBusyChange?: (busy: boolean) => void }): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [, setReviewParams] = useSearchParams();
  const queryClient = useQueryClient();
  // Never-sold SKUs are deliberately hidden from the whole costs flow — the
  // footnote below tells the founder where they went and what brings them back.
  const hiddenCount = model.products.reduce((n, p) => n + p.skus.filter((s) => (s.units ?? 0) <= 0 && (s.revenue ?? 0) <= 0).length, 0);
  const [sheet, setSheet] = useState<EngineSheetGroup[] | null>(null);
  const [processing, setProcessing] = useState<EngineProcessing | null>(null);
  const [currency, setCurrency] = useState("USD");
  const [finishing, setFinishing] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);
  const reviewCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    onBusyChange?.(finishing || reviewSaving);
    return () => onBusyChange?.(false);
  }, [finishing, onBusyChange, reviewSaving]);
  // Resizable fit-width columns, same behavior as the live sheet.
  const reviewBodyRef = useRef<HTMLDivElement>(null);
  const goToProduct = (next: number): void => {
    setIdx(next);
    requestAnimationFrame(() => {
      reviewBodyRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      reviewCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const reviewLabels = useMemo(() => (sheet ?? []).flatMap((x) => x.skus.map((s) => s.label)), [sheet]);
  // Numeric floor 64px: the editable $ boxes live here — narrower tracks made
  // the inputs pile onto each other ("completely jacked up", founder
  // 2026-07-03). Tighter than the live sheet (founder 2026-07-05: "less
  // padding between columns; units can def be shrunk"): 5px gaps, Units +
  // Refund at ~half width, and the name column sticks while the rest scroll.
  // The Other column only exists when some product actually HAS extras —
  // an all-dash column was stealing width from the (truncated) product names
  // (founder 2026-07-06: "the box on the review page was never fixed").
  const hasExtras = useMemo(() => (sheet ?? []).some((x) => x.skus.some((s) => s.extras > 0)), [sheet]);
  const numCols = hasExtras ? 9 : 8;
  const reviewGrid = useColWidths(`ccd.onboarding.cols.review.v7-${numCols}`, reviewBodyRef, reviewLabels, numCols, `minmax(170px, 1.25fr) repeat(${numCols}, minmax(76px, 1fr))`, { minNumeric: 76, nameCapFrac: 0.24, nameMaxPx: 260, gap: 5, narrowCols: [2, numCols - 1] });
  // Explicit per-product sign-off, FINGERPRINTED: a confirmation vouches for
  // specific numbers — if the chat (or an edit) changes them afterwards, the
  // checkmark self-invalidates so nobody signs off on numbers they never saw.
  const [confirmedFp, setConfirmedFp] = useState<Record<string, string>>(() => {
    if (mode !== "sandbox" || resetConfirmed) return {};
    try { return JSON.parse(window.sessionStorage.getItem(sandboxCostReviewKey(accountId)) ?? "{}") as Record<string, string>; } catch { return {}; }
  });
  const [confirmedIds, setConfirmedIds] = useState<string[]>([]);
  const [visibleFingerprints, setVisibleFingerprints] = useState<Record<string, string>>({});
  const [reviewInputReset, setReviewInputReset] = useState(0);
  useEffect(() => {
    if (mode !== "sandbox") return;
    try { window.sessionStorage.setItem(sandboxCostReviewKey(accountId), JSON.stringify(confirmedFp)); } catch { /* non-fatal */ }
  }, [accountId, confirmedFp, mode]);
  const fpOf = (x: EngineSheetGroup): string => JSON.stringify([x.costPerUnit, x.skus.map((s) => [s.skuId, s.costPerUnit ?? (s.unitsPerSale > 0 ? s.cogs / s.unitsPerSale : x.costPerUnit), Math.round(s.fulfillment * 100), Math.round(s.extras * 100)])]);
  const isConfirmed = (x: EngineSheetGroup): boolean => mode === "sandbox" ? confirmedFp[x.id] === fpOf(x) : confirmedIds.includes(x.id);
  const reviewQueue = useRef<Promise<void>>(Promise.resolve());
  const reviewPending = useRef(0);
  const queueReviewWrite = <T,>(work: () => Promise<T>): Promise<T> => {
    reviewPending.current += 1;
    setReviewSaving(true);
    const run = reviewQueue.current.then(work, work);
    reviewQueue.current = run.then(() => undefined, () => undefined);
    return run.finally(() => {
      reviewPending.current -= 1;
      if (reviewPending.current === 0) setReviewSaving(false);
    });
  };

  // The review reads the SAME data the conversation built — and edits write
  // straight back into it, so chat, sheet, and dashboard can never disagree.
  // A FAILED fetch is not an empty sheet: showing "No costed products yet"
  // after a network blip sent founders backwards to redo work that was already
  // saved (onboarding audit M6) — so failure gets its own state + retry.
  const [sheetError, setSheetError] = useState(false);
  const loadSheet = (): void => {
    setSheetError(false);
    void api.get<EngineChatState>("/onboarding/chat/state", { mode }).then((s) => {
      if (s.requiresCatalogReview) {
        toast.error("Review your products before checking costs.");
        onCatalogReview();
        return;
      }
      setSheet(s.sheet);
      setProcessing(s.processing ?? null);
      setConfirmedIds(s.reviewedGroupIds ?? []);
      setVisibleFingerprints(s.reviewFingerprints ?? {});
      if (s.currency) setCurrency(s.currency);
    }).catch(() => setSheetError(true));
  };
  const resetStarted = useRef(false);
  const resetReviewConfirmations = (): void => {
    if (resetStarted.current) return;
    resetStarted.current = true;
    setSheetError(false);
    void api.post("/onboarding/chat/review/reset", {}).then(() => {
      relockFinancialQueries(queryClient);
      setConfirmedIds([]);
      setReviewParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete("resetReview");
        return next;
      }, { replace: true });
      loadSheet();
    }).catch(() => {
      // Let the visible Try again button repeat the reset itself. Retrying only
      // the sheet load could otherwise bring old confirmations back after the
      // reset request failed.
      resetStarted.current = false;
      setSheetError(true);
      toast.error("I couldn't reset this review. Nothing else changed.");
    });
  };
  useEffect(() => {
    if (mode === "sandbox" || !resetConfirmed) {
      loadSheet();
      return;
    }
    resetReviewConfirmations();
  }, []);

  const fmt = useMemo(() => {
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 2 }); }
    catch { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }); }
  }, [currency]);

  // Blank-price SKUs (no paid sale on record yet) are TUCKED AWAY, not shown as
  // "no price yet" rows (founder 2026-07-06: "hide until a sale is made"). The
  // moment a real priced sale lands, the SKU reappears here to be costed. A
  // product with zero priced SKUs drops out entirely (nothing to review).
  const priced = (grp: EngineSheetGroup): EngineSheetSku[] => grp.skus.filter((s) => s.price > 0);
  const blankPriceHidden = (sheet ?? []).filter((x) => !x.skipped).reduce((n, x) => n + x.skus.filter((s) => s.price <= 0).length, 0);
  const groups = (sheet ?? []).filter((g) => !g.skipped && priced(g).length > 0);
  const g = groups[Math.min(idx, Math.max(0, groups.length - 1))];
  const allConfirmed = groups.length > 0 && groups.every((x) => isConfirmed(x));

  // When columns genuinely don't fit, SAY so: a right-edge fade + "more"
  // marker replaces the silent cut-off (founder 2026-07-05: "it literally
  // cuts off several of the columns... gotta figure out how to make this
  // easy to navigate"). Disappears once scrolled to the end.
  const [moreRight, setMoreRight] = useState(false);
  useEffect(() => {
    const el = reviewBodyRef.current;
    if (!el) return;
    const check = (): void => setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 8);
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, [sheet, idx, reviewGrid.template]);

  const edit = (groupId: string, patch: { costPerUnit?: number; fulfillment?: number; skuCostPerUnit?: Record<string, number>; skuFulfillment?: Record<string, number> }): Promise<void> => queueReviewWrite(async () => {
    try {
      const r = await api.post<{ sheet: EngineSheetGroup[]; commitFailed?: boolean; openOverheadReview?: boolean; reviewedGroupIds?: string[]; reviewFingerprints?: Record<string, string> }>("/onboarding/chat/edit", { mode, groupId, ...patch });
      if (mode !== "sandbox") relockFinancialQueries(queryClient);
      if (r.openOverheadReview) {
        navigate(`/costs/overhead?returnTo=${encodeURIComponent(productSetupPath("review", fromGuided))}`);
        return;
      }
      setSheet(r.sheet);
      if (mode !== "sandbox") setConfirmedIds(r.reviewedGroupIds ?? []);
      setVisibleFingerprints(r.reviewFingerprints ?? {});
      setReviewInputReset((version) => version + 1);
      // Fingerprints self-invalidate on change — nothing else to do here.
      if (r.commitFailed) toast.error("Saved here, but it hasn't reached the dashboard yet — it'll retry when you hit Finish.");
    } catch {
      try {
        const latest = await api.get<EngineChatState>("/onboarding/chat/state", { mode });
        setSheet(latest.sheet);
        setConfirmedIds(latest.reviewedGroupIds ?? []);
        setVisibleFingerprints(latest.reviewFingerprints ?? {});
      } catch { /* the retry message below remains accurate */ }
      setReviewInputReset((version) => version + 1);
      toast.error("Couldn't save that change — try again.");
    }
  });

  const confirmCurrent = (): void => {
    if (!g) return;
    if (mode === "sandbox") {
      setConfirmedFp((c) => ({ ...c, [g.id]: fpOf(g) }));
      const next = groups.findIndex((x, i) => i > idx && !isConfirmed(x) && x.id !== g.id);
      if (next >= 0) goToProduct(next);
      else if (idx < groups.length - 1) goToProduct(idx + 1);
      return;
    }
    void queueReviewWrite(async () => {
      try {
        const result = await api.post<{ reviewedGroupIds: string[]; reviewFingerprints?: Record<string, string> }>("/onboarding/chat/review", { mode, groupId: g.id, confirmed: true, fingerprint: visibleFingerprints[g.id] });
        setConfirmedIds(result.reviewedGroupIds);
        if (result.reviewFingerprints) setVisibleFingerprints(result.reviewFingerprints);
        const next = groups.findIndex((x, i) => i > idx && !result.reviewedGroupIds.includes(x.id));
        if (next >= 0) goToProduct(next);
        else if (idx < groups.length - 1) goToProduct(idx + 1);
      } catch {
        try {
          const latest = await api.get<EngineChatState>("/onboarding/chat/state", { mode });
          setSheet(latest.sheet);
          setConfirmedIds(latest.reviewedGroupIds ?? []);
          setVisibleFingerprints(latest.reviewFingerprints ?? {});
        } catch { /* keep the product unconfirmed */ }
        setReviewInputReset((version) => version + 1);
        toast.error("Those numbers changed or could not be saved. I reloaded the current version for you to review.");
      }
    });
  };

  const undoConfirmation = (groupId: string): void => {
    if (mode === "sandbox") {
      setConfirmedFp((current) => { const next = { ...current }; delete next[groupId]; return next; });
      return;
    }
    void queueReviewWrite(async () => {
      try {
        const result = await api.post<{ reviewedGroupIds: string[]; reviewFingerprints?: Record<string, string> }>("/onboarding/chat/review", { mode, groupId, confirmed: false });
        relockFinancialQueries(queryClient);
        setConfirmedIds(result.reviewedGroupIds);
        if (result.reviewFingerprints) setVisibleFingerprints(result.reviewFingerprints);
      } catch {
        toast.error("Couldn't remove that confirmation. Try again.");
      }
    });
  };

  const finish = async (): Promise<void> => {
    if (finishing) return;
    if (mode === "sandbox") { toast.success("Sandbox preview — in real setup, Finish unlocks your dashboard."); return; }
    setFinishing(true);
    setMissing([]);
    try {
      await api.post("/onboarding/finalize", {});
      await queryClient.invalidateQueries();
      toast.success(fromGuided ? "Profit model saved. Next, we'll verify everything." : "Setup complete. Opening your dashboard.");
      navigate(fromGuided ? "/welcome?step=7" : "/dashboard");
    } catch (e) {
      const steps = (e as { detail?: { steps?: Array<{ name?: string; action?: string }> } })?.detail?.steps;
      const missingSteps = Array.isArray(steps) ? steps : [];
      const names = missingSteps.map((s) => s.name ?? "a required step").filter((x): x is string => Boolean(x));
      setMissing(names);
      if (names.length) {
        if (fromGuided) {
          const firstAction = missingSteps[0]?.action;
          toast.error(firstAction ?? `Finish this step first: ${names[0]}.`);
          if (names.includes("Final product review")) {
            loadSheet();
          } else if (names.includes("Costs and margin") || names.includes("Save financial model")) {
            onBack();
          } else {
            navigate("/welcome");
          }
        } else {
          toast.error(`Your costs are saved. ${names.length === 1 ? "One quick step left" : `${names.length} quick steps left`}: ${names.join(", ")}.`);
          navigate("/welcome");
        }
      } else {
        toast.error("Couldn't finish setup — try again.");
      }
    } finally {
      setFinishing(false);
    }
  };

  if (sheetError) return (
    <div style={card}>
      <span style={{ fontSize: 13.5 }}>We couldn't load your numbers just now — they're saved, nothing is lost.</span>
      <div style={{ marginTop: 12 }}><button style={primaryBtn} onClick={mode !== "sandbox" && resetConfirmed ? resetReviewConfirmations : loadSheet}>Try again</button></div>
    </div>
  );
  if (sheet === null) return <div style={card}><span style={{ color: "var(--faint)", fontSize: 13 }}>Loading your numbers…</span></div>;
  if (groups.length === 0) return <div style={card}><span style={{ fontSize: 13.5 }}>No costed products yet — head back to the costs step first.</span><div style={{ marginTop: 12 }}><button style={ghostBtn} onClick={onBack}>← Back to costs</button></div></div>;

  return (
    <div ref={reviewCardRef} style={{ ...card, scrollMarginTop: 16 }}>
      <div style={{ fontSize: 14, marginBottom: 4, fontWeight: 700 }}>Final check: go through each product and confirm its numbers.</div>
      <div style={{ fontSize: 12.5, color: "var(--body)", marginBottom: 12 }}>Every cost below is editable. Click a number to change it. {groups.filter(isConfirmed).length} of {groups.length} products confirmed.</div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {groups.map((x, i) => (
          <button key={x.id} onClick={() => goToProduct(i)}
            style={{ fontSize: 11.5, fontWeight: 600, padding: "4px 11px", borderRadius: 999, cursor: "pointer",
              border: `1px solid ${i === idx ? "var(--accent)" : "var(--border)"}`,
              background: isConfirmed(x) ? "color-mix(in srgb, var(--good) 14%, transparent)" : i === idx ? "color-mix(in srgb, var(--accent) 10%, transparent)" : "var(--surface-2)",
              color: isConfirmed(x) ? "var(--good)" : "var(--text)" }}>
            {isConfirmed(x) ? "✓ " : ""}{x.name}
          </button>
        ))}
      </div>

      {g && (
        <div style={{ border: `1px solid ${isConfirmed(g) ? "color-mix(in srgb, var(--good) 40%, var(--border))" : "var(--border)"}`, borderRadius: 12, padding: "14px 16px", background: "var(--surface-2)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{g.name} <span style={{ fontWeight: 400, fontSize: 12, color: "var(--faint)" }}>({idx + 1} of {groups.length})</span></div>
          </div>

          <div style={{ position: "relative" }}>
          {/* Fixed-height scroll box (founder 2026-07-06: with ~80 SKUs the
              horizontal scrollbar sat at the very bottom, off-screen). Capping
              the height pins the bottom scrollbar inside a screen-sized box —
              always reachable — while rows scroll vertically under a sticky
              header. */}
          <div ref={reviewBodyRef} style={{ overflow: "auto", maxHeight: 460 }}>
          <div style={{ display: "grid", gridTemplateColumns: reviewGrid.template, gap: 5, padding: "4px 0", color: "var(--faint)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.03em", position: "sticky", top: 0, zIndex: 3, background: "var(--surface-2)", ...reviewGrid.dividers }}>
            <span style={stickyFirstCol}><ColHead label="Product" i={0} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} /></span>
            <ColHead label="Price" alignRight i={1} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />
            <ColHead label="Units" alignRight i={2} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />
            <ColHead label="Cost/Unit ✎" title="What one unit in this exact SKU costs you to make" alignRight i={3} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />
            <ColHead label="Prod cost" title="The whole sale's make cost: cost/unit × units" alignRight i={4} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />
            <ColHead label="Ship ✎" title="What it costs YOU to ship this pack (postage + packing) — editable per row" alignRight i={5} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />
            <ColHead label="Fees" title="What your card processor takes on each sale" alignRight i={6} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />
            {hasExtras && <ColHead label="Other" title="Product-specific extras: royalties, licensing, platform fees" alignRight i={7} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />}
            <ColHead label="Refund" alignRight i={hasExtras ? 8 : 7} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} />
            <ColHead label="CM" title="Contribution margin — what you keep per sale" alignRight i={hasExtras ? 9 : 8} startDrag={reviewGrid.startDrag} reset={reviewGrid.reset} last />
          </div>
          {priced(g).map((s) => (
            <div key={s.skuId} style={{ display: "grid", gridTemplateColumns: reviewGrid.template, gap: 5, alignItems: "center", fontSize: 12, padding: "6px 0", borderTop: "1px solid var(--hair)", ...reviewGrid.dividers }}>
              <span style={{ minWidth: 0, ...stickyFirstCol }}>
                {/* Wrap to two lines instead of chopping the name — hover shows the whole thing. */}
                <span title={s.label} style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.3 }}>{s.label}</span>
                <span style={{ color: "var(--faint)", fontFamily: "var(--font-mono)", fontSize: 10.5 }}>{s.skuCode}</span>
              </span>
              <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{s.price > 0 ? fmt.format(s.price) : "—"}</span>
              <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{s.unitsPerSale}</span>
              <span style={{ display: "flex", justifyContent: "flex-end" }}>
                <NumberBox value={s.costPerUnit ?? (s.unitsPerSale > 0 ? s.cogs / s.unitsPerSale : g.costPerUnit)} min="0" step="0.01" placeholder="add $" prefix="$" decimals={2} disabled={reviewSaving} resetNonce={reviewInputReset} resetWhenBlank resetWhenInvalid
                  style={{ width: 96, maxWidth: "100%", minWidth: 0, boxSizing: "border-box", height: 30, textAlign: "right", background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 7, fontSize: 12, padding: "0 7px" }}
                  onCommit={(v) => { const current = s.costPerUnit ?? (s.unitsPerSale > 0 ? s.cogs / s.unitsPerSale : g.costPerUnit); if (v !== null && v >= 0 && v !== current) void edit(g.id, { skuCostPerUnit: { [s.skuId]: v } }); }} />
              </span>
              <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt.format(s.cogs)}</span>
              <span style={{ display: "flex", justifyContent: "flex-end" }}>
                <NumberBox value={Math.round(s.fulfillment * 100) / 100} min="0" step="0.01" placeholder="-" prefix="$" decimals={2} disabled={reviewSaving} resetNonce={reviewInputReset} resetWhenBlank resetWhenInvalid
                  style={{ width: 96, maxWidth: "100%", minWidth: 0, boxSizing: "border-box", height: 30, textAlign: "right", background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 7, fontSize: 12, padding: "0 7px" }}
                  onCommit={(v) => { const current = Math.round(s.fulfillment * 100) / 100; if (v !== null && v >= 0 && v !== current) void edit(g.id, { skuFulfillment: { [s.skuId]: v } }); }} />
              </span>
              <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--body)" }}>{fmt.format(s.processing)}</span>
              {hasExtras && <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: s.extras > 0 ? "var(--body)" : "var(--faint)" }}>{s.extras > 0 ? fmt.format(s.extras) : "—"}</span>}
              <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--body)" }}>{s.returns > 0 ? fmt.format(s.returns) : "—"}</span>
              {/* No sale price on record → there's no real margin to show. A
                  scary red negative here was pure noise (founder 2026-07-06). */}
              {s.price > 0
                ? <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: s.cm < 0 ? "var(--bad)" : "var(--text)" }}>{fmt.format(s.cm)}<span style={{ display: "block", fontWeight: 600, fontSize: 10, color: s.cmPct === null ? "var(--faint)" : s.cmPct >= 0.4 ? "var(--good)" : s.cmPct >= 0.2 ? "var(--c2)" : "var(--bad)" }}>{s.cmPct === null ? "" : `${Math.round(s.cmPct * 100)}%`}</span></span>
                : <span title="This SKU has no sale price on record, so there's no margin to calculate yet." style={{ textAlign: "right", fontWeight: 600, color: "var(--faint)" }}>—<span style={{ display: "block", fontWeight: 500, fontSize: 10 }}>no price yet</span></span>}
            </div>
          ))}
          </div>
          {moreRight && (
            <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 64, pointerEvents: "none", zIndex: 2, background: "linear-gradient(to right, transparent, var(--surface-2) 85%)", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: "0.04em" }}>more →</span>
            </div>
          )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <button style={{ ...ghostBtnSm, visibility: idx > 0 ? "visible" : "hidden" }} onClick={() => goToProduct(idx - 1)}>← Previous</button>
            {isConfirmed(g)
              ? <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--good)", display: "flex", alignItems: "center", gap: 8 }}>
                  ✓ Confirmed
                  <button style={ghostBtnSm} disabled={reviewSaving} onClick={() => undoConfirmation(g.id)}>Undo</button>
                  {idx < groups.length - 1 && <button style={ghostBtnSm} onClick={() => goToProduct(idx + 1)}>Next product →</button>}
                </span>
              : <button style={{ ...primaryBtn, opacity: reviewSaving ? 0.6 : 1 }} disabled={reviewSaving} onClick={confirmCurrent}>{reviewSaving ? "Saving…" : `✓ These numbers are right${idx < groups.length - 1 ? ". Next" : ""}`}</button>}
          </div>
        </div>
      )}

      {processing && (
        <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface-2)", fontSize: 12.5 }}>
          <span style={{ fontWeight: 700 }}>Payment processing: </span>
          {processing.processors && processing.processors.length > 0
            ? processing.processors.map((pr) => `${pr.name} ${pr.pct}%${pr.fixed ? ` + ${fmt.format(pr.fixed)}` : ""} (${pr.sharePct}%)`).join(" · ")
            : processing.basePct !== null ? `${processing.basePct}%${processing.fixed ? ` + ${fmt.format(processing.fixed)}` : ""}` : "not set"}
          {processing.gatewayMonthly ? ` · gateways ${fmt.format(processing.gatewayMonthly)}/mo` : ""}
          <span style={{ color: "var(--faint)" }}> — to change these, hop back to the chat.</span>
        </div>
      )}

      {(hiddenCount > 0 || blankPriceHidden > 0) && (
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--faint)" }}>
          {(() => { const n = hiddenCount + blankPriceHidden; return `${n} SKU${n === 1 ? "" : "s"} without a paid sale yet ${n === 1 ? "is" : "are"} tucked away to keep this clean`; })()} — the moment one makes a real sale, your dashboard's setup checklist flags it so you can price it then.
        </div>
      )}
      {missing.length > 0 && <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--bad) 8%, var(--surface))", border: "1px solid color-mix(in srgb, var(--bad) 30%, transparent)", fontSize: 12.5, color: "var(--bad)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span>Your catalog &amp; costs are set. A few account steps still need finishing before the dashboard unlocks: {missing.join(", ")}.</span>
        <button style={{ ...ghostBtnSm, whiteSpace: "nowrap" }} onClick={() => navigate("/welcome")}>Finish in Setup →</button>
      </div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
        <button disabled={finishing || reviewSaving} style={{ ...ghostBtn, opacity: finishing || reviewSaving ? 0.55 : 1 }} onClick={onBack}>← Back to costs</button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!allConfirmed && <span style={{ fontSize: 12, color: "var(--faint)" }}>Confirm every product to finish</span>}
          <button style={{ ...primaryBtn, opacity: finishing || reviewSaving || !allConfirmed ? 0.5 : 1 }} disabled={finishing || reviewSaving || !allConfirmed} onClick={() => void finish()}>{finishing ? "Finishing…" : reviewSaving ? "Saving…" : mode === "sandbox" ? "Finish (preview)" : "Finish setup"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ styles ---------------------------- */
const card: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-card)", padding: 14 };
/** Review table: the name/code column stays put while the numbers scroll under
 *  it — with 10 columns, losing the row's identity mid-scroll was the pain. */
// alignSelf stretch + single-column grid: the opaque background must cover the
// FULL row height (rows are center-aligned grids), or numbers scrolling
// underneath peek out above/below short labels. Grid (not flex) keeps the
// name + code lines stacked while alignContent re-centers them.
const stickyFirstCol: React.CSSProperties = { position: "sticky", left: 0, zIndex: 1, background: "var(--surface-2)", paddingRight: 6, alignSelf: "stretch", display: "grid", alignContent: "center" };
const asstAvatar: React.CSSProperties = { width: 24, height: 24, borderRadius: "50%", background: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
const primaryBtn: React.CSSProperties = { background: "var(--accent)", color: "var(--accent-contrast)", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 14px" };
const ghostBtn: React.CSSProperties = { background: "transparent", color: "var(--body)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 13px" };
const ghostBtnSm: React.CSSProperties = { background: "transparent", color: "var(--body)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, cursor: "pointer", padding: "4px 9px" };
const unitPill: React.CSSProperties = { fontSize: 10.5, color: "var(--accent)", background: "color-mix(in srgb, var(--accent) 13%, transparent)", borderRadius: 999, padding: "1px 9px" };
const kindPill: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, color: "var(--c2)", background: "color-mix(in srgb, var(--c2) 14%, transparent)", borderRadius: 999, padding: "1px 9px" };
const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 400 };
// Column headers freeze just below the global window bar (--sticky-bar-h, set by
// AppLayout) AND below this lane's sticky group-name box (~44px) — so as you
// scroll a long product you always see the category + the column meanings.
const stickyTh: React.CSSProperties = { ...th, position: "sticky", top: "calc(var(--sticky-bar-h, 64px) + 44px)", zIndex: 3, background: "var(--surface)", borderBottom: "1px solid var(--border)" };
const stickyGroupHead: React.CSSProperties = { position: "sticky", top: "var(--sticky-bar-h, 64px)", zIndex: 4 };
const sheetColumnGuide: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "28px minmax(240px, 1fr) 120px 130px",
  gap: 0,
  alignItems: "center",
  position: "sticky",
  top: 0,
  zIndex: 5,
  marginBottom: 8,
  padding: "7px 8px",
  background: "color-mix(in srgb, var(--surface) 96%, white)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  boxShadow: "0 6px 14px rgba(15,23,42,0.08)",
  color: "var(--faint)",
  fontSize: 10,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};
const td: React.CSSProperties = { padding: "6px 8px", verticalAlign: "middle" };
const editInput: React.CSSProperties = { width: "100%", boxSizing: "border-box", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 5, padding: "4px 6px", fontSize: 12, color: "var(--text)", fontFamily: "inherit", fontVariantNumeric: "tabular-nums" };
// Sheet/Review column widths now come from useColWidths (fit-to-width name +
// equal numeric columns + drag-to-resize). The fr strings live inline as the
// pre-measure fallback only.

const cellStack: React.CSSProperties = { display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.25, fontVariantNumeric: "tabular-nums" };
const subPctStyle: React.CSSProperties = { fontSize: 10, color: "var(--faint)" };
const requiredInputStyle: React.CSSProperties = { borderColor: "var(--bad)", color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 8%, var(--surface-2))" };
const moveSelect: React.CSSProperties = { width: "100%", boxSizing: "border-box", background: "transparent", border: "1px solid var(--border)", borderRadius: 5, padding: "4px 4px", fontSize: 11.5, color: "var(--body)", fontFamily: "inherit", cursor: "pointer" };
const bubble: React.CSSProperties = { fontSize: 13.5, lineHeight: 1.55, padding: "9px 12px", borderRadius: 12, marginBottom: 9, maxWidth: "88%", whiteSpace: "pre-wrap" };
const asstB: React.CSSProperties = { background: "var(--surface-2)", alignSelf: "flex-start", border: "1px solid var(--border)", borderBottomLeftRadius: 3 };
const userB: React.CSSProperties = { background: "var(--accent)", color: "var(--accent-contrast)", alignSelf: "flex-end", borderBottomRightRadius: 3 };
const inputStyle: React.CSSProperties = { background: "var(--surface-2)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8, padding: "0 12px", fontSize: 13.5, fontFamily: "inherit" };
const smartInput: React.CSSProperties = { flex: 1, minWidth: 150, height: 40, boxSizing: "border-box", background: "var(--surface-2)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 10, padding: "0 14px", fontSize: 14, fontFamily: "inherit", lineHeight: "normal", verticalAlign: "middle" };
